/**
 * App Shell Component
 * Main application container that orchestrates all other components
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

// Import components
import './game-wheel/game-wheel.js';
import './chat/chat-interface.js';
import './debug/debug-panel.js';
import './auth/login-form.ts';
import './modals/contract-disclaimer-modal.js';
import './modals/tailored-activity-details-modal.js';
import './modals/user-profile-modal.js';
import './modals/activity-history-modal.js';
import type { InventoryItem } from './modals/inventory-modal.js';
import './progress/real-time-progress-bar.js';
import './error-banner.js';
import './error-popup.js';
import './error-persistent.js';
import { ContractDisclaimerModal } from './modals/contract-disclaimer-modal.js';
import './post-spin/post-spin-view-manager.js';
import type { PostSpinActivity } from './post-spin/post-spin-view-manager.js';

// Import services
import { StateManager } from '../services/state-manager.js';
import { MessageHandler } from '../services/message-handler.js';
import { WebSocketManager } from '../services/websocket-manager.js';
import { ConfigService } from '../services/config-service.js';
import { authService, type AuthStatus } from '../services/authentication-service.js';
import { DebugMessageFilter } from '../utils/debug-message-filter.js';
import { PostSpinService } from '../services/post-spin-service.js';
import { getDomainColor, getDomainColorWithOpacity, getContrastingTextColor } from '../services/domainColorService.js';
import { WheelStateMachine, WheelState, type WheelData as StateMachineWheelData } from './wheel-state-machine.js';
import type { WheelData as BusinessWheelData } from '../types/business-objects.js';

// Create a flexible WheelData type that can handle both formats during transition
type WheelData = BusinessWheelData & {
  segments?: any[];
};

import { ErrorHandler } from '../services/error-handler.js';
import { ErrorNotificationManager } from '../services/error-notification-manager.js';
import { ErrorClassificationService } from '../services/error-classification.js';
import { ErrorType, ErrorLevel, AppError } from '../types/error-types.js';

// Import inventory modal
import './modals/inventory-modal.js';
import type { InventoryData } from './modals/inventory-modal.js';

// Import environment switcher modal
import './modals/environment-switcher-modal.js';
import type { EnvironmentData } from './modals/environment-switcher-modal.js';

// Import types
import type { ChatMessage } from './chat/message-bubble.js';

@customElement('app-shell')
export class AppShell extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      min-height: 100vh;
      background: var(--gradient-primary);
      position: relative;
      overflow-x: hidden;
    }

    .app-container {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      max-width: 100vw;
      margin: 0 auto;
      position: relative;
    }

    .header {
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      padding: var(--spacing-4) var(--spacing-6);
      position: sticky;
      top: 0;
      z-index: var(--z-sticky);
    }

    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: 1200px;
      margin: 0 auto;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
      color: white;
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-bold);
    }

    .logo-icon {
      width: 32px;
      height: 32px;
      border-radius: var(--radius-full);
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .status-bar {
      display: flex;
      align-items: center;
      gap: var(--spacing-4);
      color: white;
      font-size: var(--font-size-sm);
    }

    .connection-status {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
    }

    .status-indicator {
      width: 8px;
      height: 8px;
      border-radius: var(--radius-full);
      background: var(--color-error);
      transition: background var(--transition-fast);
    }

    .status-indicator.connected {
      background: var(--color-success);
      box-shadow: 0 0 8px rgba(76, 175, 80, 0.4);
    }

    .status-indicator.connecting {
      background: #ff9800;
      box-shadow: 0 0 8px rgba(255, 152, 0, 0.4);
      animation: pulse 1.5s ease-in-out infinite;
    }

    .status-indicator.disconnected {
      background: var(--color-error);
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
    }

    .user-name {
      font-weight: 500;
    }

    .staff-badge {
      background: rgba(255, 193, 7, 0.2);
      color: #ffc107;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      border: 1px solid rgba(255, 193, 7, 0.3);
    }

    .user-icon-btn, .logout-btn {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 14px;
    }

    .user-icon-btn:hover, .logout-btn:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .logout-btn:hover {
      background: rgba(255, 107, 107, 0.3);
      border-color: rgba(255, 107, 107, 0.5);
    }

    .debug-btn {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: white;
      padding: 4px 8px;
      font-size: 10px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .debug-btn:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    /* Hamburger Menu Styles */
    .hamburger-menu-container {
      position: relative;
    }

    .hamburger-button {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 44px;
      height: 44px;
    }

    .hamburger-button:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .hamburger-button.active {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
    }

    .hamburger-menu {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 8px;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      min-width: 280px;
      z-index: 1000;
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
      transition: all 0.2s ease;
      pointer-events: none;
    }

    .hamburger-menu.open {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: all;
    }

    .hamburger-menu-header {
      padding: var(--spacing-4);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(255, 255, 255, 0.05);
      border-radius: 12px 12px 0 0;
    }

    .hamburger-menu-user {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
      color: #1a1a1a; /* Dark text for white background */
    }

    .hamburger-menu-user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      color: white;
      font-weight: 600;
    }

    .hamburger-menu-user-info {
      flex: 1;
    }

    .hamburger-menu-user-name {
      font-weight: 600;
      font-size: var(--font-size-base);
      margin-bottom: 2px;
    }

    .hamburger-menu-user-status {
      font-size: var(--font-size-sm);
      color: #666666; /* Dark gray for white background */
      display: flex;
      align-items: center;
      gap: var(--spacing-1);
    }

    .hamburger-menu-body {
      padding: var(--spacing-2);
    }

    .hamburger-menu-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      color: #1a1a1a; /* Dark text for white background */
      text-decoration: none;
      margin-bottom: var(--spacing-1);
    }

    .hamburger-menu-item:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateX(4px);
    }

    .hamburger-menu-item-icon {
      font-size: 18px;
      width: 24px;
      text-align: center;
    }

    .hamburger-menu-item-content {
      flex: 1;
    }

    .hamburger-menu-item-title {
      font-weight: 500;
      margin-bottom: 2px;
    }

    .hamburger-menu-item-subtitle {
      font-size: var(--font-size-sm);
      color: #666666; /* Dark gray for white background */
    }

    .hamburger-menu-divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      margin: var(--spacing-2) var(--spacing-4);
    }

    .hamburger-menu-item.danger:hover {
      background: rgba(255, 107, 107, 0.1);
      color: #ff6b6b;
    }

    .hamburger-menu-item.danger .hamburger-menu-item-icon {
      color: #ff6b6b;
    }







    .main-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: var(--spacing-6);
      gap: var(--spacing-6);
      max-width: 1200px;
      margin: 0 auto;
      width: 100%;
      position: relative;
      z-index: 1;
    }

    .main-content.menu-open {
      /* Reason: Removed pointer-events: none to prevent blocking the Generate button
       * when the hamburger menu overlay is open. The backdrop inside the menu handles
       * closing on outside clicks. Interactive elements in .main-content remain clickable,
       * which is correct behaviour — the Generate button should always be accessible. */
    }

    .wheel-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-6);
      min-height: 60vh;
    }

    .wheel-controls {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-4);
      width: 100%;
      max-width: 500px;
    }

    .button-bar {
      display: flex;
      gap: var(--spacing-4);
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: var(--spacing-4);
      background: rgba(255, 255, 255, 0.05);
      border-radius: 12px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .potentiometer-control {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-2);
      flex: 1;
    }

    .potentiometer-label {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      font-weight: 500;
      text-align: center;
    }

    .potentiometer-slider {
      width: 100%;
      max-width: 140px;
      height: 8px;
      border-radius: 4px;
      background: linear-gradient(to right, #4CAF50, #FFC107, #FF5722);
      outline: none;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
    }

    .potentiometer-slider::-webkit-slider-thumb {
      appearance: none;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #fff;
      border: 3px solid #007bff;
      cursor: pointer;
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
      transition: all 0.2s ease;
      position: relative;
      z-index: 2;
    }

    .potentiometer-slider::-webkit-slider-thumb:hover {
      transform: scale(1.15);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
      border-color: #0056b3;
    }

    .potentiometer-slider::-moz-range-thumb {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #fff;
      border: 3px solid #007bff;
      cursor: pointer;
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    }

    .time-slider-container {
      position: relative;
      width: 100%;
      max-width: 140px;
    }

    .time-step-indicators {
      position: absolute;
      top: -8px;
      left: 0;
      right: 0;
      height: 24px;
      pointer-events: none;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .time-step-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      border: 2px solid rgba(255, 255, 255, 0.8);
      transition: all 0.2s ease;
    }

    .time-step-dot.active {
      background: #007bff;
      border-color: #007bff;
      transform: scale(1.2);
      box-shadow: 0 0 8px rgba(0, 123, 255, 0.5);
    }

    .time-step-labels {
      position: absolute;
      top: 20px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      color: var(--color-text-muted);
      pointer-events: none;
    }

    .time-step-label {
      text-align: center;
      font-weight: 500;
      transition: all 0.2s ease;
    }

    .time-step-label.active {
      color: #007bff;
      font-weight: 600;
      transform: scale(1.1);
    }

    .potentiometer-value {
      font-size: var(--font-size-xs);
      color: var(--color-text-muted);
      font-weight: 600;
    }

    /* 🚀 Enhanced 6-Slider Energy Grid System */
    .energy-grid {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);
      width: 100%;
      margin-top: var(--spacing-3);
    }

    .energy-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: var(--spacing-3);
      width: 100%;
    }

    .energy-slider-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--spacing-3);
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      transition: all 0.2s ease;
      backdrop-filter: blur(5px);
    }

    .energy-slider-card:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.12);
      transform: translateY(-1px);
    }

    .energy-icon {
      font-size: 1.5rem;
      margin-bottom: var(--spacing-1);
      opacity: 0.9;
    }

    .energy-label {
      font-size: var(--font-size-sm);
      font-weight: 500;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-2);
      text-align: center;
    }

    .slider-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-2);
      width: 100%;
    }

    .energy-slider {
      width: 100%;
      max-width: 100px;
      height: 4px;
      border-radius: 2px;
      background: linear-gradient(to right,
        rgba(255, 107, 107, 0.3),
        rgba(254, 202, 87, 0.3),
        rgba(72, 219, 251, 0.3),
        rgba(255, 159, 243, 0.3)
      );
      outline: none;
      cursor: pointer;
      transition: all 0.2s ease;
      -webkit-appearance: none;
      appearance: none;
    }

    .energy-slider::-webkit-slider-thumb {
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #fff;
      border: 2px solid rgba(102, 126, 234, 0.8);
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      transition: all 0.2s ease;
    }

    .energy-slider::-webkit-slider-thumb:hover {
      transform: scale(1.1);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
      border-color: rgba(102, 126, 234, 1);
    }

    .energy-slider::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #fff;
      border: 2px solid rgba(102, 126, 234, 0.8);
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }

    .energy-value {
      font-size: var(--font-size-xs);
      font-weight: 600;
      color: var(--color-text-muted);
      opacity: 0.8;
    }

    .action-button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }

    .action-button {
      padding: var(--spacing-3) var(--spacing-6);
      border: none;
      border-radius: 8px;
      font-size: var(--font-size-base);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 100px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .generate-button {
      background: linear-gradient(135deg, #4CAF50, #45a049);
      color: white;
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
    }

    .generate-button:hover:not(:disabled) {
      background: linear-gradient(135deg, #45a049, #3d8b40);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
    }

    .generate-button:disabled {
      background: #666;
      cursor: not-allowed;
      opacity: 0.6;
      transform: none;
      box-shadow: none;
    }

    .spin-button {
      background: linear-gradient(135deg, #FF6B6B, #e55a5a);
      color: white;
      box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
    }

    .spin-button:hover:not(:disabled) {
      background: linear-gradient(135deg, #e55a5a, #d94545);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4);
    }

    .spin-button:disabled {
      background: #666;
      cursor: not-allowed;
      opacity: 0.6;
      transform: none;
      box-shadow: none;
    }

    .activity-list {
      width: 100%;
      max-width: 500px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 12px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      overflow: hidden;
    }

    .activity-list-header {
      padding: var(--spacing-4);
      background: rgba(255, 255, 255, 0.1);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      font-weight: 600;
      color: var(--color-text-primary);
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .add-activity-btn {
      background: var(--color-success);
      color: white;
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 18px;
      font-weight: bold;
    }

    .add-activity-btn:hover {
      background: var(--color-success-dark);
      transform: scale(1.1);
    }

    .remove-activity-btn {
      background: none;
      border: none;
      color: var(--color-error);
      cursor: pointer;
      padding: var(--spacing-1);
      border-radius: 4px;
      transition: all 0.2s ease;
      font-size: 16px;
      opacity: 0.7;
    }

    .remove-activity-btn:hover {
      background: rgba(255, 107, 107, 0.1);
      opacity: 1;
      transform: scale(1.1);
    }

    .activity-item {
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .activity-item:last-child {
      border-bottom: none;
    }

    .activity-item-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-2) var(--spacing-3);
      cursor: pointer;
      transition: background-color 0.2s ease;
      min-height: 44px; /* Ensure consistent compact height */
    }

    .activity-item-header:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .activity-item-title {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
      flex: 1;
      min-width: 0; /* Allow text truncation */
    }

    .activity-color-dot {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      flex-shrink: 0;
      border: 2px solid rgba(255, 255, 255, 0.3);
      transition: all 0.3s ease;
    }

    .activity-color-dot:hover {
      transform: scale(1.2);
    }

    .domain-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      transition: all 0.3s ease;
    }

    .domain-badge:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    .activity-name {
      font-weight: 500;
      color: var(--color-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      font-size: var(--font-size-sm);
    }

    .activity-expand-icon {
      transition: transform 0.2s ease;
      color: var(--color-text-secondary);
    }

    .activity-item.expanded .activity-expand-icon {
      transform: rotate(180deg);
    }

    .activity-expand-icon {
      font-size: var(--font-size-sm);
      color: var(--color-text-muted);
      transition: color 0.2s ease;
    }

    .activity-item-header:hover .activity-expand-icon {
      color: var(--color-text-primary);
    }

    .domain-badge-small {
      display: inline-block;
      padding: 1px 4px;
      border-radius: 6px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .activity-item-details {
      padding: 0 var(--spacing-4) var(--spacing-3) var(--spacing-4);
      border-top: 1px solid rgba(255, 255, 255, 0.05);
    }

    .activity-description {
      color: var(--color-text-secondary);
      font-size: var(--font-size-sm);
      line-height: 1.5;
      margin-bottom: var(--spacing-3);
    }

    .activity-meta {
      display: flex;
      gap: var(--spacing-4);
      margin-bottom: var(--spacing-3);
      font-size: var(--font-size-xs);
    }

    .activity-meta-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-1);
      color: var(--color-text-muted);
    }

    .activity-actions {
      display: flex;
      justify-content: flex-end;
    }

    .activity-change-btn {
      display: flex;
      align-items: center;
      gap: var(--spacing-1);
      padding: var(--spacing-2) var(--spacing-3);
      background: var(--color-primary);
      color: white;
      border: none;
      border-radius: 6px;
      font-size: var(--font-size-xs);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .activity-change-btn:hover {
      background: var(--color-primary-dark);
      transform: translateY(-1px);
    }

    /* Modal styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      backdrop-filter: blur(4px);
    }

    .modal-overlay::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.4);
      z-index: -1;
    }

    .profile-modal {
      max-width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
    }

    /* Static Profile Sections */
    .profile-static-section {
      margin-bottom: var(--spacing-6);
      padding: var(--spacing-4);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: var(--radius-lg);
      background: rgba(255, 255, 255, 0.05);
    }

    .profile-static-section h4 {
      color: var(--color-primary);
      margin: 0 0 var(--spacing-4) 0;
      font-size: 1.1rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
    }

    .profile-static-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: var(--spacing-3);
    }

    /* Compact Profile Section */
    .profile-compact-section {
      margin-bottom: var(--spacing-4);
      padding: var(--spacing-3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: var(--radius-lg);
      background: rgba(255, 255, 255, 0.05);
    }

    .profile-compact-header h4 {
      color: var(--color-primary);
      margin: 0 0 var(--spacing-3) 0;
      font-size: 1rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
    }

    .profile-compact-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: var(--spacing-2);
    }

    .profile-field-compact {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-1);
    }

    .profile-field-compact.profile-field-full-width {
      grid-column: 1 / -1;
    }

    .profile-field-compact label {
      font-size: 0.8rem;
      font-weight: 500;
      color: var(--color-text-secondary);
      margin: 0;
    }

    .profile-value-compact {
      background: rgba(255, 255, 255, 0.03);
      padding: var(--spacing-1) var(--spacing-2);
      border-radius: var(--radius-sm);
      border: 1px solid rgba(255, 255, 255, 0.05);
      color: var(--color-text);
      font-size: 0.9rem;
      min-height: 1.5rem;
      display: flex;
      align-items: center;
    }

    /* Goal display styles */
    .goal-description {
      margin-bottom: var(--spacing-2);
      font-style: italic;
      color: var(--color-text-secondary);
    }

    .goal-meta {
      display: flex;
      gap: var(--spacing-3);
      font-size: 0.8rem;
      color: var(--color-text-muted);
    }

    .goal-importance, .goal-strength {
      padding: var(--spacing-1) var(--spacing-2);
      background: rgba(255, 255, 255, 0.05);
      border-radius: var(--radius-sm);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* Environment details styles */
    .environment-details {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-1);
    }

    .detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-1) var(--spacing-2);
      background: rgba(255, 255, 255, 0.02);
      border-radius: var(--radius-sm);
      font-size: 0.85rem;
    }

    .detail-key {
      font-weight: 500;
      color: var(--color-text-secondary);
      flex: 1;
    }

    .detail-value {
      color: var(--color-text);
      text-align: right;
      max-width: 60%;
      word-break: break-word;
    }

    /* Enhanced Profile Modal with Accordion */
    .profile-section {
      margin-bottom: var(--spacing-4);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: var(--radius-lg);
      overflow: hidden;
      background: rgba(255, 255, 255, 0.02);
    }

    .profile-section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-4);
      cursor: pointer;
      background: rgba(255, 255, 255, 0.05);
      transition: background-color 0.2s ease;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .profile-section-header:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .profile-section-header h4 {
      color: var(--color-primary);
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
    }

    .profile-section-icon {
      font-size: 1.2em;
    }

    .profile-section-toggle {
      transition: transform 0.2s ease;
      color: var(--color-text-secondary);
      font-size: 1.2em;
    }

    .profile-section.expanded .profile-section-toggle {
      transform: rotate(180deg);
    }

    .profile-section-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease;
    }

    .profile-section.expanded .profile-section-content {
      max-height: 500px;
    }

    .profile-section-body {
      padding: var(--spacing-4);
    }

    .profile-field {
      margin-bottom: var(--spacing-3);
    }

    .profile-field label {
      display: block;
      font-weight: 500;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-1);
      font-size: 0.9rem;
    }

    .profile-value {
      background: rgba(255, 255, 255, 0.05);
      padding: var(--spacing-2) var(--spacing-3);
      border-radius: var(--radius-md);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: var(--color-text);
    }

    .profile-value.editable {
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .profile-value.editable:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: var(--color-primary);
    }

    .profile-edit-input {
      width: 100%;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid var(--color-primary);
      color: var(--color-text);
      padding: var(--spacing-2) var(--spacing-3);
      border-radius: var(--radius-md);
      font-size: 0.9rem;
    }

    .profile-edit-input:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.3);
    }

    .profile-edit-actions {
      display: flex;
      gap: var(--spacing-2);
      margin-top: var(--spacing-2);
    }

    .profile-edit-btn {
      padding: var(--spacing-1) var(--spacing-3);
      border: none;
      border-radius: var(--radius-sm);
      font-size: 0.8rem;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .profile-edit-btn.save {
      background: var(--color-success);
      color: white;
    }

    .profile-edit-btn.cancel {
      background: var(--color-error);
      color: white;
    }

    .loading-state {
      text-align: center;
      padding: var(--spacing-8);
    }

    .loading-state .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid var(--color-primary);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto var(--spacing-4);
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .modal {
      background: var(--color-background);
      border-radius: 12px;
      width: 90%;
      max-width: 600px;
      max-height: 85vh;
      overflow: hidden;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
    }

    /* Bootstrap-compatible modal classes */
    .modal-dialog {
      background: var(--color-background);
      border-radius: 12px;
      width: 90%;
      max-width: 600px;
      max-height: 85vh;
      overflow: hidden;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
    }

    .modal-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: transparent;
      border: none;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-4);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(255, 255, 255, 0.05);
    }

    .modal-title {
      font-size: var(--font-size-lg);
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .modal-close {
      background: none;
      border: none;
      font-size: var(--font-size-xl);
      color: var(--color-text-secondary);
      cursor: pointer;
      padding: var(--spacing-1);
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .modal-close:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--color-text-primary);
    }

    .modal-body {
      padding: var(--spacing-4);
      flex: 1;
      overflow-y: auto;
      min-height: 0; /* Important for flex scrolling */
    }

    /* Enhanced scrolling for activity modal */
    .activity-modal .modal-body {
      max-height: calc(85vh - 120px); /* Account for header and footer */
      overflow-y: auto;
    }

    .search-input {
      width: 100%;
      padding: var(--spacing-3);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.05);
      color: var(--color-text-primary);
      font-size: var(--font-size-base);
      margin-bottom: var(--spacing-4);
    }

    .search-input:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.2);
    }

    .search-input::placeholder {
      color: var(--color-text-muted);
    }

    /* Enhanced Search Modal Styles */
    .enhanced-search-modal {
      max-width: 700px;
      width: 90vw;
    }

    .search-container {
      margin-bottom: var(--spacing-4);
    }

    .search-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .search-icon {
      position: absolute;
      left: var(--spacing-3);
      color: var(--color-text-muted);
      font-size: 1.1em;
      z-index: 1;
    }

    .enhanced-search-input {
      width: 100%;
      padding: var(--spacing-3) var(--spacing-5) var(--spacing-3) 2.5rem;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.05);
      color: var(--color-text-primary);
      font-size: var(--font-size-base);
      transition: all 0.3s ease;
    }

    .enhanced-search-input:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.15);
      background: rgba(255, 255, 255, 0.08);
    }

    .enhanced-search-input::placeholder {
      color: var(--color-text-muted);
    }

    .clear-search {
      position: absolute;
      right: var(--spacing-2);
      background: none;
      border: none;
      color: var(--color-text-muted);
      font-size: 1.2em;
      cursor: pointer;
      padding: var(--spacing-1);
      border-radius: 50%;
      transition: all 0.2s ease;
    }

    .clear-search:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--color-text-primary);
    }

    .search-context {
      display: flex;
      gap: var(--spacing-2);
      margin-top: var(--spacing-2);
      flex-wrap: wrap;
    }

    .context-item {
      background: rgba(var(--color-primary-rgb), 0.1);
      color: var(--color-primary);
      padding: var(--spacing-1) var(--spacing-2);
      border-radius: 6px;
      font-size: 0.85em;
      border: 1px solid rgba(var(--color-primary-rgb), 0.2);
    }

    /* Enhanced Catalog Styles */
    .enhanced-catalog {
      max-height: 60vh;
      overflow-y: auto;
    }

    .empty-initial-state,
    .empty-search-state {
      text-align: center;
      padding: var(--spacing-6) var(--spacing-4);
      color: var(--color-text-muted);
    }

    .empty-icon {
      font-size: 3em;
      margin-bottom: var(--spacing-3);
      opacity: 0.6;
    }

    .empty-initial-state h4,
    .empty-search-state h4 {
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-2);
      font-size: 1.2em;
    }

    .search-suggestions {
      margin-top: var(--spacing-4);
    }

    .suggestion-tags {
      display: flex;
      gap: var(--spacing-2);
      flex-wrap: wrap;
      justify-content: center;
      margin-top: var(--spacing-2);
    }

    .suggestion-tag {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: var(--spacing-2) var(--spacing-3);
      border-radius: 20px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 0.9em;
    }

    .suggestion-tag:hover {
      background: rgba(var(--color-primary-rgb), 0.1);
      border-color: rgba(var(--color-primary-rgb), 0.3);
      color: var(--color-primary);
      transform: translateY(-1px);
    }

    .results-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-2) 0;
      margin-bottom: var(--spacing-3);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .results-count {
      color: var(--color-text-muted);
      font-size: 0.9em;
    }

    .tailored-indicator {
      color: var(--color-primary);
      font-size: 0.85em;
      background: rgba(var(--color-primary-rgb), 0.1);
      padding: var(--spacing-1) var(--spacing-2);
      border-radius: 6px;
    }

    .activity-catalog {
      display: grid;
      gap: var(--spacing-3);
      max-height: calc(60vh - 120px); /* Ensure scrolling within modal */
      overflow-y: auto;
      padding-right: var(--spacing-2); /* Space for scrollbar */
    }

    .catalog-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
      padding: var(--spacing-3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      background: rgba(255, 255, 255, 0.02);
    }

    .catalog-item:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: var(--color-primary);
      transform: translateY(-1px);
    }

    .catalog-item.tailored {
      border-color: rgba(255, 193, 7, 0.3);
      background: rgba(255, 193, 7, 0.1);
    }

    .catalog-item.tailored:hover {
      border-color: rgba(255, 193, 7, 0.5);
      background: rgba(255, 193, 7, 0.15);
    }

    .catalog-item-info {
      flex: 1;
    }

    .catalog-item-name {
      font-weight: 500;
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-1);
    }

    .catalog-item-description {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      line-height: 1.4;
    }

    .catalog-item-meta {
      display: flex;
      gap: var(--spacing-2);
      margin-top: var(--spacing-2);
      font-size: var(--font-size-xs);
      color: var(--color-text-muted);
    }

    .catalog-item-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
      margin-bottom: var(--spacing-2);
    }

    .activity-type-icon {
      font-size: 1.2em;
    }

    .activity-type-badge {
      font-size: 0.75em;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 600;
      text-transform: uppercase;
      margin-left: auto;
    }

    .activity-type-badge.tailored {
      background: rgba(255, 193, 7, 0.2);
      color: #ffc107;
      border: 1px solid rgba(255, 193, 7, 0.3);
    }

    .activity-type-badge.tailored::before {
      content: '✨ ';
      font-size: 0.9em;
    }

    .activity-type-badge.generic {
      background: rgba(108, 117, 125, 0.2);
      color: #6c757d;
      border: 1px solid rgba(108, 117, 125, 0.3);
    }

    .activity-type-badge.generic::before {
      content: '📋 ';
      font-size: 0.9em;
    }

    /* Enhanced visual differentiation for activity type icons */
    .activity-type-icon.tailored {
      color: #ffc107;
      text-shadow: 0 0 8px rgba(255, 193, 7, 0.5);
      font-size: 1.3em;
    }

    .activity-type-icon.generic {
      color: #6c757d;
      opacity: 0.8;
    }

    /* Enhanced catalog item styling for better differentiation */
    .catalog-item.tailored {
      border-left: 3px solid #ffc107;
      background: linear-gradient(135deg, rgba(255, 193, 7, 0.05), rgba(255, 193, 7, 0.02));
    }

    .catalog-item.generic {
      border-left: 3px solid rgba(108, 117, 125, 0.3);
      background: rgba(108, 117, 125, 0.02);
    }

    .catalog-item.tailored:hover {
      background: linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 193, 7, 0.05));
      border-left-color: #ffcd39;
    }

    /* Enhanced Catalog Item Styles for New Search Interface */
    .enhanced-item {
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.03);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
    }

    .enhanced-item:hover {
      background: rgba(255, 255, 255, 0.06);
      transform: translateY(-3px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
      border-color: rgba(255, 255, 255, 0.15);
    }

    .enhanced-item .activity-color-indicator {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      margin-right: var(--spacing-2);
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
      flex-shrink: 0;
    }

    .enhanced-item .catalog-item-header {
      display: flex;
      align-items: center;
      margin-bottom: var(--spacing-2);
      gap: var(--spacing-2);
    }

    .enhanced-item .catalog-item-name {
      flex: 1;
      font-weight: 600;
      color: var(--color-text-primary);
      font-size: 1.05em;
    }

    .enhanced-item .activity-badges {
      display: flex;
      gap: var(--spacing-1);
      align-items: center;
      flex-shrink: 0;
    }

    .enhanced-item .activity-type-badge {
      background: rgba(255, 255, 255, 0.1);
      color: var(--color-text-primary);
      padding: 2px var(--spacing-2);
      border-radius: 8px;
      font-size: 0.75em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .enhanced-item .activity-type-badge.tailored {
      background: rgba(var(--color-primary-rgb), 0.2);
      color: var(--color-primary);
    }

    .enhanced-item .relevance-badge {
      background: linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.2), rgba(var(--color-primary-rgb), 0.1));
      color: var(--color-primary);
      padding: 2px var(--spacing-2);
      border-radius: 8px;
      font-size: 0.7em;
      font-weight: 600;
      border: 1px solid rgba(var(--color-primary-rgb), 0.3);
    }

    .enhanced-item .catalog-item-description {
      color: var(--color-text-secondary);
      line-height: 1.5;
      margin-bottom: var(--spacing-3);
      font-size: 0.95em;
    }

    .enhanced-item .catalog-item-meta {
      display: flex;
      gap: var(--spacing-3);
      flex-wrap: wrap;
      align-items: center;
    }

    .enhanced-item .meta-item {
      font-size: 0.85em;
      color: var(--color-text-muted);
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .enhanced-item .domain-meta {
      font-weight: 500;
    }

    .enhanced-item .challenge-meta {
      color: var(--color-text-muted);
    }

    .enhanced-item .tailored-meta {
      color: var(--color-primary);
      font-weight: 500;
    }

    /* Winning Modal Styles */
    .winning-modal-overlay {
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(5px);
    }

    .winning-modal-content {
      max-width: 500px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: var(--radius-xl);
      color: white;
      text-align: center;
      animation: winningModalAppear 0.5s ease-out;
    }

    @keyframes winningModalAppear {
      0% {
        transform: scale(0.8) translateY(-20px);
        opacity: 0;
      }
      100% {
        transform: scale(1) translateY(0);
        opacity: 1;
      }
    }

    .winning-header {
      padding: var(--spacing-6) var(--spacing-4) var(--spacing-4);
      position: relative;
    }

    .winning-icon {
      font-size: 3rem;
      margin-bottom: var(--spacing-2);
      animation: bounce 1s infinite;
    }

    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-10px);
      }
      60% {
        transform: translateY(-5px);
      }
    }

    .winning-header h2 {
      margin: 0;
      font-size: 1.8rem;
      font-weight: 700;
    }

    .winning-activity {
      padding: var(--spacing-4);
    }

    .activity-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
      margin-bottom: var(--spacing-4);
    }

    .activity-icon {
      font-size: 2.5rem;
      text-align: center;
      min-width: 50px;
    }

    .activity-title {
      flex: 1;
    }

    .activity-name {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: var(--spacing-2);
      color: #fff;
    }

    .activity-description {
      font-size: 1rem;
      opacity: 0.9;
      margin-bottom: var(--spacing-4);
      line-height: 1.5;
      background: rgba(255, 255, 255, 0.1);
      padding: var(--spacing-3);
      border-radius: var(--radius-md);
      border-left: 4px solid rgba(255, 255, 255, 0.5);
    }

    .activity-instructions {
      background: rgba(255, 255, 255, 0.15);
      border-radius: var(--radius-md);
      padding: var(--spacing-4);
      margin-bottom: var(--spacing-4);
      text-align: left;
    }

    .activity-instructions h4 {
      margin: 0 0 var(--spacing-2) 0;
      color: rgba(255, 255, 255, 0.95);
      font-size: 1rem;
      font-weight: 600;
    }

    .activity-instructions p {
      margin: 0;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.5;
      font-size: 0.95rem;
    }

    .tailored-info {
      background: rgba(255, 193, 7, 0.2);
      border: 1px solid rgba(255, 193, 7, 0.4);
      border-radius: var(--radius-md);
      padding: var(--spacing-3);
      margin-bottom: var(--spacing-4);
    }

    .tailored-info h4 {
      color: #ffc107;
      margin: 0 0 var(--spacing-2) 0;
      font-size: 1rem;
    }

    .tailored-info p {
      margin: 0;
      color: #fff;
      line-height: 1.5;
      opacity: 0.9;
    }

    .activity-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--spacing-2);
      background: rgba(255, 255, 255, 0.1);
      padding: var(--spacing-3);
      border-radius: var(--radius-lg);
      margin-bottom: var(--spacing-4);
    }

    .detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-2);
      background: rgba(255, 255, 255, 0.05);
      border-radius: var(--radius-sm);
    }

    .detail-label {
      font-weight: 500;
      opacity: 0.8;
      font-size: 0.9rem;
    }

    .detail-value {
      font-weight: 600;
      font-size: 0.9rem;
    }

    .activity-requirements {
      margin-bottom: var(--spacing-4);
    }

    .requirement-section {
      margin-bottom: var(--spacing-3);
      background: rgba(255, 255, 255, 0.05);
      padding: var(--spacing-3);
      border-radius: var(--radius-md);
      border-left: 3px solid rgba(255, 255, 255, 0.3);
    }

    .requirement-section h4 {
      margin: 0 0 var(--spacing-2) 0;
      color: rgba(255, 255, 255, 0.9);
      font-size: 1rem;
    }

    .requirement-section p {
      margin: 0;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.5;
    }

    .winning-actions {
      text-align: center;
      margin-top: var(--spacing-4);
    }

    .winning-actions .primary-button {
      background: rgba(255, 255, 255, 0.9);
      color: #667eea;
      border: none;
      padding: var(--spacing-3) var(--spacing-6);
      border-radius: var(--radius-lg);
      font-size: 1.1rem;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }

    .winning-actions .primary-button:hover {
      background: #fff;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    }

    .winning-actions .secondary-button {
      background: transparent;
      color: rgba(255, 255, 255, 0.8);
      border: 2px solid rgba(255, 255, 255, 0.3);
      padding: var(--spacing-2) var(--spacing-4);
      border-radius: var(--radius-lg);
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-top: var(--spacing-3);
      display: block;
      width: 100%;
    }

    .winning-actions .secondary-button:hover {
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 1);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-1px);
    }

    .winning-actions .decline-button {
      color: rgba(255, 107, 107, 0.9);
      border-color: rgba(255, 107, 107, 0.4);
    }

    .winning-actions .decline-button:hover {
      background: rgba(255, 107, 107, 0.1);
      color: #ff6b6b;
      border-color: rgba(255, 107, 107, 0.6);
    }

    .winning-modal-content .modal-footer {
      padding: var(--spacing-4);
      display: flex;
      gap: var(--spacing-3);
      justify-content: center;
    }

    .winning-modal-content .btn {
      padding: var(--spacing-3) var(--spacing-6);
      border-radius: var(--radius-lg);
      font-weight: 600;
      transition: all 0.2s ease;
    }

    .winning-modal-content .btn-primary {
      background: #fff;
      color: #667eea;
      border: none;
    }

    .winning-modal-content .btn-primary:hover {
      background: #f8f9fa;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .winning-modal-content .btn-secondary {
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.3);
    }

    .winning-modal-content .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }

    /* Contract Confirmation Styles in Winning Modal */
    .contract-confirmation {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      padding: var(--spacing-4);
      margin-bottom: var(--spacing-4);
      backdrop-filter: blur(10px);
    }

    .contract-confirmation-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
      margin-bottom: var(--spacing-3);
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
    }

    .contract-icon {
      font-size: 1.2rem;
    }

    .contract-message {
      font-size: 0.95rem;
    }

    .signature-display {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-2);
    }

    .signature-image {
      max-width: 200px;
      max-height: 60px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.9);
      padding: var(--spacing-1);
    }

    .signature-timestamp {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.7);
      font-style: italic;
    }

    .no-results {
      text-align: center;
      padding: var(--spacing-6);
      color: var(--color-text-muted);
    }

    /* Create Activity Modal Styles */
    .create-activity-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-2);
      width: 100%;
      padding: var(--spacing-3);
      background: var(--color-primary);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: var(--font-size-sm);
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: var(--spacing-4);
    }

    .create-activity-btn:hover {
      background: var(--color-primary-dark);
      transform: translateY(-1px);
    }

    /* Custom Activity Creation Styles */
    .custom-activity-section {
      margin-top: var(--spacing-4);
      padding: var(--spacing-4);
      border: 2px dashed rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.02);
    }

    .custom-activity-prompt {
      text-align: center;
      margin-bottom: var(--spacing-4);
    }

    .custom-activity-prompt h4 {
      color: var(--color-primary);
      margin: 0 0 var(--spacing-2) 0;
      font-size: 1.2rem;
    }

    .custom-activity-prompt p {
      color: var(--color-text-secondary);
      margin: 0 0 var(--spacing-3) 0;
    }

    .create-custom-btn {
      background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .create-custom-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(var(--color-primary-rgb), 0.3);
    }

    .custom-activity-form {
      margin-top: var(--spacing-4);
      padding: var(--spacing-4);
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .duration-range {
      display: flex;
      gap: var(--spacing-4);
      align-items: center;
    }

    .range-input-group {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-1);
    }

    .range-input-group label {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      font-weight: 500;
    }

    .range-input-group span {
      font-size: var(--font-size-sm);
      color: var(--color-text-primary);
      font-weight: 600;
      text-align: center;
    }

    .form-group {
      margin-bottom: var(--spacing-4);
    }

    .form-label {
      display: block;
      margin-bottom: var(--spacing-2);
      font-weight: 500;
      color: var(--color-text-primary);
      font-size: var(--font-size-sm);
    }

    .form-input,
    .form-textarea,
    .form-select {
      width: 100%;
      padding: var(--spacing-3);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.05);
      color: var(--color-text-primary);
      font-size: var(--font-size-sm);
      transition: all 0.2s ease;
    }

    .form-input:focus,
    .form-textarea:focus,
    .form-select:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.2);
    }

    .form-textarea {
      resize: vertical;
      min-height: 80px;
    }

    .form-range {
      width: 100%;
      margin: var(--spacing-2) 0;
    }

    .range-value {
      text-align: center;
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      margin-top: var(--spacing-1);
    }

    .modal-actions {
      display: flex;
      gap: var(--spacing-3);
      justify-content: flex-end;
      margin-top: var(--spacing-4);
      padding-top: var(--spacing-4);
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn-secondary {
      padding: var(--spacing-2) var(--spacing-4);
      background: transparent;
      color: var(--color-text-secondary);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.05);
      color: var(--color-text-primary);
    }

    .btn-primary {
      padding: var(--spacing-2) var(--spacing-4);
      background: var(--color-primary);
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-primary:hover {
      background: var(--color-primary-dark);
    }

    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .wheel-container {
      position: relative;
      width: 100%;
      max-width: 500px;
      aspect-ratio: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .wheel-center-button {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 100;
      background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
      border: none;
      border-radius: 50%;
      width: 150px;
      height: 150px;
      color: white;
      font-weight: bold;
      font-size: var(--font-size-lg);
      cursor: pointer;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .wheel-center-button:hover {
      transform: translate(-50%, -50%) scale(1.1);
      box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
    }

    .wheel-center-button:active {
      transform: translate(-50%, -50%) scale(0.95);
    }

    .wheel-center-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: translate(-50%, -50%) scale(1);
    }

    .wheel-center-button.generate {
      background: linear-gradient(135deg, #4CAF50, #45a049);
    }

    .wheel-center-button.generate:hover:not(:disabled) {
      background: linear-gradient(135deg, #45a049, #3d8b40);
    }

    .wheel-center-button.spin {
      background: linear-gradient(135deg, #FF6B6B, #e55a5a);
    }

    .wheel-center-button.spin:hover:not(:disabled) {
      background: linear-gradient(135deg, #e55a5a, #d94545);
    }

    /* Unpopulated wheel state: grey and non-interactive */
    .wheel-unpopulated {
      opacity: 0.3;
      filter: grayscale(100%) brightness(0.7);
      pointer-events: none;
    }

    /* Error overlay: displayed over the unpopulated wheel when generation fails */
    .wheel-error-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 100;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      text-align: center;
      padding: 16px;
    }

    .wheel-error-icon {
      font-size: 2rem;
    }

    .wheel-error-message {
      color: var(--color-error, #ef4444);
      font-size: var(--font-size-sm, 0.875rem);
      font-weight: 600;
      max-width: 180px;
      line-height: 1.3;
    }

    .wheel-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.7);
      text-align: center;
      padding: var(--spacing-8);
    }

    .wheel-placeholder-icon {
      width: 64px;
      height: 64px;
      margin-bottom: var(--spacing-4);
      opacity: 0.5;
    }

    .chat-section {
      background: rgba(255, 255, 255, 0.95);
      border-radius: var(--radius-2xl);
      padding: var(--spacing-6);
      box-shadow: var(--shadow-xl);
      backdrop-filter: blur(10px);
    }

    .error-banner {
      background: var(--color-error);
      color: white;
      padding: var(--spacing-4);
      border-radius: var(--radius-lg);
      margin-bottom: var(--spacing-4);
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
    }

    .error-banner button {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: white;
      padding: var(--spacing-2) var(--spacing-3);
      font-size: var(--font-size-sm);
    }

    .progress-bar-container {
      margin-bottom: var(--spacing-4, 16px);
      padding: 0 var(--spacing-2, 8px);
      animation: slideDown 0.3s ease-out;
    }

    /* Progress Modal Overlay */
    .progress-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.3s ease-out;
    }

    .progress-modal {
      background: white;
      border-radius: 16px;
      padding: 24px;
      max-width: 500px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      animation: slideUp 0.3s ease-out;
    }

    .progress-modal.user-friendly {
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .progress-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid #e5e7eb;
    }

    .progress-modal-title {
      font-size: 20px;
      font-weight: 600;
      color: #111827;
      margin: 0;
    }

    .progress-modal-close {
      background: none;
      border: none;
      font-size: 24px;
      color: #6b7280;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .progress-modal-close:hover {
      background: #f3f4f6;
      color: #374151;
    }

    .progress-modal-cancel {
      background: none;
      border: 1px solid #d1d5db;
      color: #6b7280;
      padding: 8px 24px;
      border-radius: 8px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .progress-modal-cancel:hover {
      background: #f3f4f6;
      color: #374151;
      border-color: #9ca3af;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .connection-error-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #dc3545; /* Red background for connection errors */
      color: white;
      padding: var(--spacing-3);
      text-align: center;
      z-index: 2000;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      font-weight: 500;
    }

    /* Enjoy Overlay Styles */
    .enjoy-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(10px);
      z-index: 3000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: var(--color-text-primary);
      text-align: center;
      animation: enjoyOverlayFadeIn 0.5s ease-out;
    }

    @keyframes enjoyOverlayFadeIn {
      0% {
        opacity: 0;
        transform: scale(0.95);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    .enjoy-overlay-content {
      max-width: 600px;
      padding: var(--spacing-8);
      background: rgba(255, 255, 255, 0.9);
      border-radius: var(--radius-2xl);
      border: 1px solid rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(20px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }

    .enjoy-title {
      font-size: 3rem;
      font-weight: 700;
      margin-bottom: var(--spacing-6);
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-shadow: 0 0 30px rgba(102, 126, 234, 0.5);
    }

    .enjoy-activity-info {
      margin-bottom: var(--spacing-6);
    }

    .enjoy-activity-name {
      font-size: 2rem;
      font-weight: 600;
      margin-bottom: var(--spacing-3);
      color: var(--color-text-primary);
    }

    .enjoy-activity-description {
      font-size: 1.2rem;
      line-height: 1.6;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-4);
    }

    .enjoy-activity-details {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: var(--spacing-3);
      margin-bottom: var(--spacing-6);
    }

    .enjoy-detail-item {
      background: rgba(102, 126, 234, 0.1);
      padding: var(--spacing-3);
      border-radius: var(--radius-lg);
      border: 1px solid rgba(102, 126, 234, 0.2);
    }

    .enjoy-detail-label {
      font-size: 0.9rem;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-1);
    }

    .enjoy-detail-value {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .enjoy-hourglass {
      font-size: 3rem;
      margin: var(--spacing-6) 0;
      animation: rotateHourglass 2s linear infinite;
    }

    @keyframes rotateHourglass {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .enjoy-timer {
      font-size: 1.2rem;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-4);
    }

    .enjoy-close-btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-6);
      border-radius: var(--radius-lg);
      font-size: 1.1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    }

    .enjoy-close-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    /* DEBUG Timer Controls */
    .debug-timer-controls {
      margin: var(--spacing-4) 0;
      padding: var(--spacing-3);
      border: 2px dashed #ffaa00;
      border-radius: var(--radius-lg);
      background: rgba(255, 170, 0, 0.1);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-2);
    }

    .debug-indicator {
      font-size: var(--font-size-sm);
      font-weight: 600;
      color: #ff8800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .debug-skip-btn {
      background: #ff8800;
      color: white;
      border: none;
      padding: var(--spacing-2) var(--spacing-4);
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .debug-skip-btn:hover {
      background: #ff6600;
      transform: translateY(-1px);
    }

    /* Post-Activity Feedback Modal Styles */
    .post-activity-feedback-modal {
      max-width: 600px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
    }

    /* Activity Cancellation Modal Styles */
    .cancellation-modal-overlay {
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(8px);
    }

    .cancellation-modal {
      max-width: 550px;
      width: 90%;
      background: #fff;
      border-radius: var(--radius-xl);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    }

    .cancellation-modal .modal-header {
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
      color: white;
      padding: var(--spacing-6);
      border-radius: var(--radius-xl) var(--radius-xl) 0 0;
      text-align: center;
      position: relative;
    }

    .cancellation-icon {
      font-size: 3rem;
      margin-bottom: var(--spacing-3);
      display: block;
    }

    .cancellation-modal .modal-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .contract-reminder {
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: var(--radius-lg);
      padding: var(--spacing-4);
      margin-bottom: var(--spacing-4);
    }

    .contract-reminder-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
      margin-bottom: var(--spacing-2);
      font-weight: 600;
      color: #856404;
    }

    .contract-text {
      color: #856404;
      margin: 0;
      line-height: 1.5;
    }

    .activity-reminder {
      background: #f8f9fa;
      border-radius: var(--radius-lg);
      padding: var(--spacing-4);
      margin-bottom: var(--spacing-4);
    }

    .activity-reminder h4 {
      margin: 0 0 var(--spacing-3) 0;
      color: var(--color-text-primary);
    }

    .activity-reminder .activity-name {
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: var(--spacing-2);
      color: var(--color-text-primary);
    }

    .activity-reminder .activity-description {
      color: var(--color-text-muted);
      line-height: 1.5;
    }

    .feedback-section h4 {
      margin: 0 0 var(--spacing-3) 0;
      color: var(--color-text-primary);
    }

    .cancellation-textarea {
      width: 100%;
      min-height: 100px;
      border: 2px solid #e9ecef;
      border-radius: var(--radius-lg);
      padding: var(--spacing-3);
      font-family: inherit;
      resize: vertical;
      transition: border-color 0.2s ease;
    }

    .cancellation-textarea:focus {
      outline: none;
      border-color: #ff6b6b;
      box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
    }

    .cancellation-actions {
      display: flex;
      gap: var(--spacing-3);
      justify-content: space-between;
      margin-top: var(--spacing-6);
    }

    .cancel-cancellation {
      background: var(--color-success);
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      flex: 1;
    }

    .cancel-cancellation:hover {
      background: var(--color-success-dark);
      transform: translateY(-1px);
    }

    .confirm-cancellation {
      background: #ff6b6b;
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      flex: 2;
    }

    .confirm-cancellation:hover {
      background: #ee5a52;
      transform: translateY(-1px);
    }

    .btn-tertiary {
      background: transparent;
      color: var(--color-text-muted);
      border: 2px solid #e9ecef;
      font-size: 0.9rem;
    }

    .btn-tertiary:hover {
      background: #f8f9fa;
      color: var(--color-text-primary);
      border-color: #dee2e6;
    }

    /* Update modal overlay to have white background */
    .modal-overlay {
      background: rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(10px);
    }

    /* Activity Tailoring Spinner Styles */
    .tailoring-spinner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1001;
      border-radius: var(--radius-lg);
    }

    .tailoring-spinner-content {
      text-align: center;
      padding: var(--spacing-8);
      max-width: 400px;
    }

    .spinner-animation {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      margin-bottom: var(--spacing-4);
    }

    .spinner-circle {
      width: 12px;
      height: 12px;
      background: var(--color-primary);
      border-radius: 50%;
      animation: tailoring-bounce 1.4s ease-in-out infinite both;
    }

    .spinner-circle:nth-child(1) { animation-delay: -0.32s; }
    .spinner-circle:nth-child(2) { animation-delay: -0.16s; }
    .spinner-circle:nth-child(3) { animation-delay: 0s; }

    @keyframes tailoring-bounce {
      0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.5;
      }
      40% {
        transform: scale(1.2);
        opacity: 1;
      }
    }

    .tailoring-message h4 {
      color: var(--color-primary);
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: var(--spacing-2);
    }

    .tailoring-message p {
      color: var(--color-text-secondary);
      font-size: 0.95rem;
      margin-bottom: var(--spacing-4);
      line-height: 1.5;
    }

    .tailoring-progress {
      margin-top: var(--spacing-4);
    }

    .progress-bar {
      width: 100%;
      height: 4px;
      background: rgba(102, 126, 234, 0.2);
      border-radius: 2px;
      overflow: hidden;
      margin-bottom: var(--spacing-2);
    }

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
      border-radius: 2px;
      animation: tailoring-progress 10s ease-in-out infinite;
    }

    @keyframes tailoring-progress {
      0% { width: 0%; }
      50% { width: 70%; }
      100% { width: 100%; }
    }

    .tailoring-progress small {
      color: var(--color-text-muted);
      font-size: 0.8rem;
    }

    .activity-summary {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
      border-radius: var(--radius-lg);
      padding: var(--spacing-4);
      margin-bottom: var(--spacing-6);
      border: 1px solid rgba(102, 126, 234, 0.2);
    }

    .activity-info .activity-name {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: var(--spacing-2);
      color: var(--color-text-primary);
    }

    .activity-info .activity-description {
      font-size: 1rem;
      line-height: 1.5;
      color: var(--color-text-secondary);
    }

    .feedback-message {
      font-size: 1.1rem;
      line-height: 1.6;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-6);
      text-align: center;
      font-style: italic;
    }

    .feedback-form {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-6);
    }

    .rating-group {
      background: rgba(255, 255, 255, 0.05);
      border-radius: var(--radius-lg);
      padding: var(--spacing-4);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .rating-group .form-label {
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: var(--spacing-3);
      color: var(--color-text-primary);
    }

    .rating-options {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-2);
      justify-content: space-between;
    }

    .rating-option {
      flex: 1;
      min-width: 100px;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--spacing-3);
      border-radius: var(--radius-md);
      border: 2px solid rgba(255, 255, 255, 0.1);
      background: rgba(255, 255, 255, 0.05);
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;
    }

    .rating-option:hover {
      border-color: rgba(102, 126, 234, 0.5);
      background: rgba(102, 126, 234, 0.1);
      transform: translateY(-2px);
    }

    .rating-option input[type="radio"] {
      display: none;
    }

    .rating-option input[type="radio"]:checked + .rating-text {
      color: #667eea;
      font-weight: 600;
    }

    .rating-option:has(input[type="radio"]:checked) {
      border-color: #667eea;
      background: rgba(102, 126, 234, 0.2);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    }

    .rating-text {
      font-size: 0.9rem;
      color: var(--color-text-secondary);
      transition: all 0.3s ease;
      margin-top: var(--spacing-1);
    }

    @media (max-width: 768px) {
      .rating-options {
        flex-direction: column;
      }

      .rating-option {
        min-width: auto;
        flex-direction: row;
        justify-content: flex-start;
        gap: var(--spacing-2);
      }

      .rating-text {
        margin-top: 0;
      }
    }

    .loading-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: var(--spacing-3) var(--spacing-4);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-3);
      z-index: var(--z-sticky);
      font-size: var(--font-size-sm);
      font-weight: 500;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-100%);
      animation: slideDown 0.3s ease-out forwards;
    }

    .loading-banner .dots-animation {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .loading-banner .dots-animation span {
      width: 6px;
      height: 6px;
      background: white;
      border-radius: 50%;
      animation: dots-bounce 1.4s infinite ease-in-out;
    }

    .loading-banner .dots-animation span:nth-child(1) {
      animation-delay: -0.32s;
    }

    .loading-banner .dots-animation span:nth-child(2) {
      animation-delay: -0.16s;
    }

    .loading-banner .dots-animation span:nth-child(3) {
      animation-delay: 0s;
    }

    @keyframes slideDown {
      to {
        transform: translateY(0);
      }
    }

    @keyframes dots-bounce {
      0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.7;
      }
      40% {
        transform: scale(1);
        opacity: 1;
      }
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .main-content {
        padding: var(--spacing-4);
        gap: var(--spacing-4);
      }

      .header {
        padding: var(--spacing-3) var(--spacing-4);
      }

      .logo {
        font-size: var(--font-size-lg);
      }

      .chat-section {
        padding: var(--spacing-4);
      }
    }
  `;

  @state() private messages: ChatMessage[] = [];
  @state() private wheelData: WheelData | null = null;
  @state() private wsConnected = false;
  @state() private isLoading = false;
  @state() private isTailoringActivity = false;
  @state() private tailoringActivityName = '';
  @state() private error: string | null = null;
  @state() private isAuthenticated = false;
  @state() private showDebugPanel = false;
  @state() private isHamburgerMenuOpen = false;
  @state() private showInventoryModal = false;
  @state() private inventoryData: InventoryData | null = null;
  @state() private showEnvironmentSwitcherModal = false;
  @state() private environmentData: EnvironmentData | null = null;
  @state() private showActivityHistoryModal = false;
  @state() private activityHistoryData: any[] = [];
  @state() private isActivityHistoryLoading = false;

  // Error handling system
  @state() private currentError: AppError | null = null;
  @state() private showErrorPopup = false;
  @state() private showErrorPersistent = false;
  private errorHandler!: ErrorHandler;
  private errorNotificationManager!: ErrorNotificationManager;

  // State Machine for reliable wheel management
  private wheelStateMachine = new WheelStateMachine();

  // Subscribe to state machine changes
  private wheelStateUnsubscribe?: () => void;
  @state() private currentMode: 'debug' | 'production' | 'production-test' = 'debug';
  @state() private debugUserId = '';
  @state() private debugLLMConfigId = '';
  @state() private timeAvailable = 20; // Default to 20 minutes
  @state() private showProgressBar = false;
  @state() private currentProgressTrackerId = '';
  // Reason: Stored so cancel_workflow message can reference the correct backend workflow.
  private currentActiveWorkflowId: string | null = null;
  @state() private progressTitle = 'Processing...';
  @state() private progressModalMode: 'debug' | 'user-friendly' = 'user-friendly';
  @state() private generationStalled = false; // True when progress has been stuck at 90%+ for 30+ seconds
  // Legacy single energy slider (kept for backward compatibility)
  @state() private energyLevel = 50; // 0-100 scale

  // 🚀 Enhanced 5-slider energy system (Updated 2025-07-24)
  // Note: timeAvailable already exists above
  @state() private physicalEnergy = 50; // 0-100 scale
  @state() private mentalEnergy = 50; // 0-100 scale
  @state() private socialEnergy = 50; // 0-100 scale
  @state() private emotionalEnergy = 50; // 0-100 scale (gentle to aggressive)
  @state() private energyDirection = 50; // 0-100 scale (inward to outward)
  // Note: creativityLevel removed from UI (handled as fixed value in agents)
  @state() private currentUser: { id: string; name: string; isStaff?: boolean } | null = null;
  @state() private connectionState: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  @state() private expandedActivities = new Set<string>();
  @state() private showActivityDetailsModal = false;
  @state() private selectedActivity: any = null; // Track which activities are expanded
  @state() private showActivityModal = false;
  @state() private selectedActivityId: string | null = null;
  @state() private searchQuery = '';
  @state() private activityCatalog: Array<{
    id: string;
    name: string;
    description: string;
    domain: string;
    base_challenge_rating: number;
    type: 'generic' | 'tailored';
    icon: string;
  }> = [];

  // Activity catalog caching
  private activityCatalogCache: {
    data: any[] | null;
    timestamp: number;
    userId: string | null;
  } = {
    data: null,
    timestamp: 0,
    userId: null
  };

  private readonly ACTIVITY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // New activity creation modal state
  @state() private showCreateActivityModal = false;
  @state() private newActivityForm = {
    name: '',
    description: '',
    domain: 'general',
    base_challenge_rating: 50
  };

  // Custom activity creation state (for HistoryEvent-based activities)
  @state() private showCustomActivityForm = false;
  @state() private customActivityForm = {
    title: '',
    description: '',
    duration_from: 15,
    duration_to: 30,
    challengingness: 50,
    comfort_level: 50
  };
  @state() private showProfileModal = false;
  @state() private currentUserProfile: any = null;
  @state() private showWinningModal = false;
  @state() private winningActivity: any = null;
  @state() private expandedProfileSections = new Set<string>(['basic']); // Default to basic section expanded
  @state() private editingProfileFields = new Set<string>(); // Track which fields are being edited
  @state() private showFeedbackModal = false;
  @state() private feedbackModalConfig = {
    title: '',
    message: '',
    feedback_type: '',
    content_type: '',
    object_id: '',
    primaryButtonLabel: 'Send Feedback',
    secondaryButtonLabel: 'Cancel'
  };
  @state() private showAddActivityModal = false;
  @state() private showContractModal = false;

  // Post-spin state management
  @state() private postSpinView: 'enjoy' | 'feedback' | 'cancellation' | null = null;
  @state() private postSpinActivity: PostSpinActivity | null = null;
  @state() private postSpinStartTime: number | null = null;

  private stateManager = StateManager.getInstance();
  private messageHandler = MessageHandler.getInstance();
  private websocketManager = WebSocketManager.getInstance();
  private configService = ConfigService.getInstance();
  private postSpinService = PostSpinService.getInstance();
  // Use the singleton authentication service
  private debugFilter = DebugMessageFilter.getInstance();

  connectedCallback() {
    super.connectedCallback();
    this.loadDebugSelections();
    this.checkForActiveEnjoyOverlay();

    // Initialize error handling system
    this.initializeErrorHandling();

    // Listen for authentication changes from the auth service
    authService.addAuthStatusListener(this.handleAuthStatusChange);

    // Subscribe to wheel state machine changes
    this.wheelStateUnsubscribe = this.wheelStateMachine.subscribe((context) => {
      // CRITICAL FIX: Update this.wheelData when state machine has populated data
      // The template needs this.wheelData to be set for the populated wheel to render
      if (context.state === 'POPULATED' && context.data) {
        // Convert StateMachine format back to business format for template
        this.wheelData = this.convertFromStateMachineFormat(context.data);
      } else if (context.state === 'EMPTY') {
        // Clear wheel data when state machine is empty
        this.wheelData = null;
      }

      // Trigger re-render when state changes
      this.requestUpdate();
    });

    this.initializeApp();
    this.setupEventListeners();

    // Track app launch event
    this.trackAppLaunch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.websocketManager.disconnect();
    this.removeEventListeners();

    // Cleanup state machine subscription
    if (this.wheelStateUnsubscribe) {
      this.wheelStateUnsubscribe();
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('config-changed', this.handleConfigChange);
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('load-mocked-wheel', this.handleLoadMockedWheel);
    window.addEventListener('progress-update', this.handleProgressUpdate);
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    window.removeEventListener('config-changed', this.handleConfigChange);
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('load-mocked-wheel', this.handleLoadMockedWheel);
    window.removeEventListener('progress-update', this.handleProgressUpdate);
  }

  /**
   * Handle authentication status changes from the auth service
   */
  private handleAuthStatusChange = (authStatus: AuthStatus) => {
    this.isAuthenticated = authStatus.isAuthenticated;

    if (authStatus.isAuthenticated && authStatus.user) {
      // Update current user from auth service
      this.currentUser = {
        id: authStatus.user.id,
        name: authStatus.user.first_name || authStatus.user.username || 'User',
        isStaff: authStatus.user.is_staff || false
      };
      console.log('🔐 User authenticated:', this.currentUser);
    } else {
      // Clear user data on logout
      this.currentUser = null;
      console.log('🔐 User logged out');
    }

    // Force re-render to update UI
    this.requestUpdate();
  };

  /**
   * Handle configuration changes
   */
  private handleConfigChange = (event: any) => {
    // Reinitialize with new config if needed
    console.log('Configuration changed:', event.detail.config);
  };

  /**
   * Handle keyboard shortcuts
   */
  private handleKeydown = (event: KeyboardEvent) => {
    // Toggle debug panel with Ctrl+Shift+D
    if (event.ctrlKey && event.shiftKey && event.key === 'D' && this.configService.isDebugMode()) {
      event.preventDefault();
      this.showDebugPanel = !this.showDebugPanel;
      return;
    }

    // Escape key: close modals in priority order
    if (event.key === 'Escape') {
      if (this.showProgressBar) {
        this.handleCancelGeneration();
      } else if (this.showWinningModal) {
        this.closeWinningModal();
      } else if (this.showContractModal) {
        this.showContractModal = false;
      } else if (this.showCreateActivityModal) {
        this.closeCreateActivityModal();
      } else if (this.showAddActivityModal) {
        this.closeAddActivityModal();
      } else if (this.showActivityModal) {
        this.closeActivityModal();
      } else if (this.showInventoryModal) {
        this.closeInventoryModal();
      } else if (this.showActivityHistoryModal) {
        this.closeActivityHistoryModal();
      } else if (this.showEnvironmentSwitcherModal) {
        this.closeEnvironmentSwitcherModal();
      } else if (this.isHamburgerMenuOpen) {
        this.closeHamburgerMenu();
      } else {
        return; // Nothing to close, don't prevent default
      }
      event.preventDefault();
    }
  };

  /**
   * Handle load mocked wheel request from debug panel
   */
  private handleLoadMockedWheel = async (event: any) => {
    console.log('🎡 Loading mocked wheel items from debug panel...');

    // Create mock wheel data with EXACT BACKEND STRUCTURE for testing architectural fixes
    // This matches the exact structure from backend/apps/main/consumers.py lines 1033-1045
    const mockWheelData: WheelData = {
      name: 'Mock Wheel',
      wheelId: 'mock-wheel-1',
      createdAt: new Date().toISOString(),
      items: [
        // EXACT BACKEND STRUCTURE: Matches backend/apps/main/consumers.py lines 1033-1045
        // Key architectural elements: id (wheel item), activity_tailored_id (activity), domain (for colors)
        {
          id: 'item_mock_1_86',  // CRITICAL: Full wheel item ID
          name: '🏃‍♂️ Pomodoro Focus Session (Tailored for 125min)',
          text: '🏃‍♂️ Pomodoro Focus Session (Tailored for 125min)',
          percentage: 16,  // Simple percentage - system will normalize to exact distribution
          domain: 'productive_practical',  // CRITICAL: Domain for color generation
          activity_tailored_id: '86',  // CRITICAL: Activity ID (different from segment ID)
          description: 'Start your day with an energizing 20-minute jog around the neighborhood',
          // Colors will be dynamically assigned by enhanceWheelDataWithColors
        } as any,
        {
          id: 'item_mock_2_155',
          name: '📚 Advanced Urban Parkour (Tailored for 125min)',
          text: '📚 Advanced Urban Parkour (Tailored for 125min)',
          percentage: 16,  // Simple percentage - system will normalize to exact distribution
          domain: 'general',
          activity_tailored_id: '155',
          description: 'Focus on your most challenging subject for 45 minutes with breaks',
          // Colors will be dynamically assigned by enhanceWheelDataWithColors
        } as any,
        {
          id: 'item_mock_3_124',
          name: '🎨 App-Guided Movement Session (Tailored for 125min)',
          text: '🎨 App-Guided Movement Session (Tailored for 125min)',
          percentage: 16,  // Simple percentage - system will normalize to exact distribution
          domain: 'productive_practical',
          activity_tailored_id: '124',
          description: 'Express yourself through art - sketch, paint, or digital drawing',
          // Colors will be dynamically assigned by enhanceWheelDataWithColors
        } as any,
        {
          id: 'item_mock_4_84',
          name: '🧘‍♀️ Deliberate Practice Session (Tailored for 125min)',
          text: '🧘‍♀️ Deliberate Practice Session (Tailored for 125min)',
          percentage: 16,  // Simple percentage - system will normalize to exact distribution
          domain: 'productive_practical',
          activity_tailored_id: '84',
          description: 'Find inner peace with a 15-minute mindfulness session',
          // Colors will be dynamically assigned by enhanceWheelDataWithColors
        } as any,
        {
          id: 'item_mock_5_64',
          name: '🍳 Emotional Anchoring Technique (Tailored for 125min)',
          text: '🍳 Emotional Anchoring Technique (Tailored for 125min)',
          percentage: 16,  // Simple percentage - system will normalize to exact distribution
          domain: 'emotional',
          activity_tailored_id: '64',
          description: 'Try a new healthy recipe and enjoy the process of cooking',
          // Colors will be dynamically assigned by enhanceWheelDataWithColors
        } as any,
        {
          id: 'item_mock_6_121',
          name: '🎵 Intentional Break Transition (Tailored for 125min)',
          text: '🎵 Intentional Break Transition (Tailored for 125min)',
          percentage: 16,  // Simple percentage - system will normalize to exact distribution
          domain: 'productive_practical',
          activity_tailored_id: '121',
          description: 'Practice your instrument or learn a new song for 30 minutes',
          // Colors will be dynamically assigned by enhanceWheelDataWithColors
        } as any
      ]
    };

    // Enhance mock data with proper colors using the color service
    const enhancedMockData = await this.enhanceWheelDataWithColors(mockWheelData);

    // Set the enhanced wheel data using state machine
    this.wheelStateMachine.setWheelData(this.convertToStateMachineFormat(enhancedMockData));


    // Add a message to show the wheel was loaded
    const message = {
      id: `mock-loaded-${Date.now()}`,
      type: 'ai' as const,
      content: '🎡 Perfect! I\'ve loaded a diverse set of activities for you to try. Give the wheel a spin to see what adventure awaits!',
      timestamp: new Date()
    };
    this.messages = [...this.messages, message];

    console.log('✅ Mock wheel data loaded successfully');
  };

  /**
   * Initialize error handling system
   */
  private initializeErrorHandling(): void {
    try {
      // Initialize error handler with configuration
      this.errorHandler = ErrorHandler.getInstance({
        debugMode: this.currentMode === 'debug',
        productionMode: this.currentMode === 'production',
        enableReporting: true,
        enableRetry: true
      });

      // Initialize notification manager
      this.errorNotificationManager = ErrorNotificationManager.getInstance();

      // Setup error event listeners
      this.setupErrorEventListeners();

      console.log('✅ Error handling system initialized');
    } catch (error) {
      console.error('❌ Failed to initialize error handling system:', error);
    }
  }

  /**
   * Setup error event listeners
   */
  private setupErrorEventListeners(): void {
    // Listen for app errors

    window.addEventListener('app-error', (event: Event) => {
      const customEvent = event as CustomEvent;
      const error = customEvent.detail.error;

      this.handleAppError(error);
    });

    // Listen for error notifications
    window.addEventListener('show-error-notification', (event: Event) => {
      const customEvent = event as CustomEvent;
      const notification = customEvent.detail;

      this.showErrorNotification(notification);
    });

    // Listen for error dismissals
    window.addEventListener('dismiss-error-notification', (event: Event) => {
      const customEvent = event as CustomEvent;
      this.dismissErrorNotification(customEvent.detail.notificationId);
    });

    // Listen for WebSocket reconnection requests
    window.addEventListener('websocket-reconnect', () => {
      this.handleWebSocketReconnect();
    });
  }

  /**
   * Handle application errors
   */
  private handleAppError(error: AppError): void {
    this.currentError = error;

    // Update UI based on error level
    switch (error.level) {
      case ErrorLevel.TEMPORARY:
        // Banner will be shown automatically by notification manager
        break;
      case ErrorLevel.NON_CRITICAL:
        if (this.currentMode === 'debug') {
          this.showErrorPopup = true;
        }
        break;
      case ErrorLevel.CRITICAL:
        this.showErrorPersistent = true;
        break;
    }

    this.requestUpdate();
  }

  /**
   * Show error notification
   */
  private showErrorNotification(notification: any): void {
    // The notification manager handles the actual display
    // We just need to update our state if needed
    this.requestUpdate();
  }

  /**
   * Dismiss error notification
   */
  private dismissErrorNotification(notificationId: string): void {
    // Reset error states
    this.showErrorPopup = false;
    this.showErrorPersistent = false;
    this.currentError = null;
    this.requestUpdate();
  }

  /**
   * Handle WebSocket reconnection
   */
  private handleWebSocketReconnect(): void {
    try {
      this.websocketManager.disconnect();
      setTimeout(() => {
        const config = this.configService.getConfig();
        this.websocketManager.initialize(config.websocket);

      }, 1000);
    } catch (error) {
      console.error('Failed to reconnect WebSocket:', error);
    }
  }

  /**
   * Initializes the application
   */
  private async initializeApp(): Promise<void> {
    try {
      this.isLoading = true;
      this.connectionState = 'connecting';
      this.currentMode = this.configService.getMode();

      // Initialize authentication service first
      await authService.initialize();

      // Get authentication status from the service
      const authStatus = authService.getAuthStatus();
      this.isAuthenticated = authStatus.isAuthenticated;

      if (authStatus.isAuthenticated && authStatus.user) {
        this.currentUser = {
          id: authStatus.user.id,
          name: authStatus.user.first_name || authStatus.user.username || 'User',
          isStaff: authStatus.user.is_staff || false
        };
      }

      // Get security configuration
      const config = this.configService.getConfig();
      const requireAuth = config.security.requireAuthentication;

      // Check if authentication is required but user is not authenticated
      if (requireAuth && !this.isAuthenticated) {
        // In debug mode, try to auto-authenticate with known credentials
        if (this.configService.isDebugMode()) {
          console.log('🔐 Debug mode: Attempting auto-authentication...');
          try {
            const success = await authService.login('guillaume_dev_fr', 'aA000000');
            if (success) {
              console.log('✅ Debug mode: Auto-authentication successful');
              const newAuthStatus = authService.getAuthStatus();
              this.isAuthenticated = newAuthStatus.isAuthenticated;
              if (newAuthStatus.user) {
                this.currentUser = {
                  id: newAuthStatus.user.id,
                  name: newAuthStatus.user.first_name || newAuthStatus.user.username || 'User',
                  isStaff: newAuthStatus.user.is_staff || false
                };
              }
            } else {
              console.error('❌ Debug mode: Auto-authentication failed');
              this.isLoading = false;
              return; // Show login form
            }
          } catch (error) {
            console.error('❌ Debug mode: Auto-authentication error:', error);
            this.isLoading = false;
            return; // Show login form
          }
        } else {
          this.isLoading = false;
          return; // Show login form
        }
      }

      // Try to initialize WebSocket connection with robust error handling and race condition protection
      let backendAvailable = false;
      let wsError: Error | null = null;

      try {
        console.log('🔌 Attempting WebSocket connection to:', config.websocket.url);

        // Use a longer timeout for initial connection to avoid race conditions
        const wsInitPromise = this.websocketManager.initialize(config.websocket);
        const extendedTimeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Extended WebSocket connection timeout')), 45000)
        );

        await Promise.race([wsInitPromise, extendedTimeoutPromise]);

        this.wsConnected = true;
        this.connectionState = 'connected';
        backendAvailable = true;
        this.setupMessageHandlers();
        console.log('✅ WebSocket connection established successfully');

        // Set up late connection handler in case WebSocket connects after timeout
        this.setupLateConnectionHandler();

        // Verify backend API availability with timeout (non-blocking)
        try {
          const apiTestPromise = this.testBackendAPI();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('API test timeout')), 5000)
          );

          await Promise.race([apiTestPromise, timeoutPromise]);
          console.log('✅ Backend API endpoints verified');
        } catch (apiError) {
          const errorMessage = apiError instanceof Error ? apiError.message : 'Unknown error';
          console.warn('⚠️ WebSocket connected but API endpoints unavailable:', errorMessage);
          console.warn('⚠️ Continuing with WebSocket-only mode - chat functionality will work');
          // WebSocket works but API doesn't - still consider backend available for chat
        }

      } catch (error) {
        wsError = error as Error;
        this.wsConnected = false;
        this.connectionState = 'disconnected';
        backendAvailable = false;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('❌ WebSocket connection failed:', errorMessage);

        // Set up late connection handler in case WebSocket connects later
        this.setupLateConnectionHandler();
      }

      // Initialize appropriate mode based on backend availability
      if (backendAvailable) {
        // Backend is available - initialize normally
        if (this.configService.isProductionMode()) {
          // Production mode with backend - minimal demo data
          this.initializeProductionMode();
        } else {
          // Debug mode with backend - clean slate for development
          await this.initializeDebugMode();
        }
      } else {
        // Backend unavailable - fallback to demo mode but allow recovery
        console.warn('⚠️ Backend server not available, initializing demo mode');
        console.warn('WebSocket error details:', wsError?.message || 'Unknown error');
        this.initializeDemoMode();
      }

    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.error = 'Failed to initialize application';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Set up handler for late WebSocket connections (race condition recovery)
   */
  private setupLateConnectionHandler(): void {
    // Listen for WebSocket connection events that might happen after initial timeout
    const handleLateConnection = async (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && !this.wsConnected) {
        console.log('🔄 Late WebSocket connection detected - recovering from demo mode');
        this.wsConnected = true;
        this.connectionState = 'connected';

        // If we're in demo mode due to connection failure, switch to proper mode
        if (this.configService.isDebugMode()) {
          await this.initializeDebugMode();
        } else {
          this.initializeProductionMode();
        }

        // Force update of chat interface connection status
        this.updateChatConnectionStatus('connected');

        // Remove this handler since we've recovered
        window.removeEventListener('websocket-connected', handleLateConnection);
      }
    };

    window.addEventListener('websocket-connected', handleLateConnection);

    // Clean up after 30 seconds if no late connection
    setTimeout(() => {
      window.removeEventListener('websocket-connected', handleLateConnection);
    }, 30000);
  }

  /**
   * Test backend API availability
   */
  private async testBackendAPI(): Promise<void> {
    const baseUrl = this.getBackendBaseUrl();

    // Test the health endpoint first (most reliable)
    try {
      const healthResponse = await fetch(`${baseUrl}/api/health/`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000)
      });

      if (healthResponse.ok) {
        console.log('✅ Health endpoint check passed');
        return;
      }
    } catch (healthError) {
      const errorMessage = healthError instanceof Error ? healthError.message : 'Unknown error';
      console.warn('⚠️ Health endpoint check failed, trying fallback:', errorMessage);
    }

    // Fallback to debug users endpoint
    const response = await fetch(`${baseUrl}/api/debug/users/`, {
      method: 'HEAD',
      signal: AbortSignal.timeout(3000)
    });

    if (!response.ok) {
      throw new Error(`API test failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Get backend base URL from WebSocket URL
   */
  private getBackendBaseUrl(): string {
    const config = this.configService.getConfig();
    let baseUrl = config.websocket.url;

    // Convert WebSocket URL to HTTP URL
    if (baseUrl.startsWith('ws://')) {
      baseUrl = baseUrl.replace('ws://', 'http://');
    } else if (baseUrl.startsWith('wss://')) {
      baseUrl = baseUrl.replace('wss://', 'https://');
    }

    // Remove WebSocket path
    baseUrl = baseUrl.replace('/ws/game/', '');

    return baseUrl;
  }

  /**
   * Initialize production mode with backend connected
   */
  private initializeProductionMode(): void {
    console.log('🚀 Initializing production mode with backend connection');

    this.messages = [
      {
        id: 'prod-welcome',
        type: 'system',
        content: '🎯 Welcome to Goali! I\'m your personal life coach. Tell me what\'s on your mind and I\'ll help you find the perfect activity.',
        timestamp: new Date()
      }
    ];

    this.wheelStateMachine.clearWheel();
    this.error = null;

    // Update chat interface connection status
    this.updateChatConnectionStatus('connected');

    console.log('✅ Production mode initialized');
  }

  /**
   * Initialize debug mode with backend connected
   */
  private async initializeDebugMode(): Promise<void> {
    console.log('🐛 Initializing debug mode with backend connection');
    const config = this.configService.getConfig();

    this.messages = [
      {
        id: 'debug-welcome',
        type: 'system',
        content: '🐛 Debug mode active. Backend connected. Use the debug panel (Ctrl+Shift+D) to configure settings, or start chatting to generate a wheel.',
        timestamp: new Date()
      }
    ];

    // Load user profile and environment data
    try {
      await this.loadCurrentUserProfile();
      await this.loadEnvironmentData();
      console.log('✅ User profile and environment data loaded');
    } catch (error) {
      console.error('❌ Failed to load user data:', error);
    }

    // CRITICAL FIX: Never load mock data in debug mode - always wait for real backend data
    console.log('🐛 Debug mode initialized - waiting for real backend wheel data (no mock data)');
    this.wheelStateMachine.clearWheel();

    this.error = null;

    // Update chat interface connection status
    this.updateChatConnectionStatus('connected');

    console.log('✅ Debug mode initialized');
  }

  /**
   * Update chat interface connection status
   */
  private updateChatConnectionStatus(status: 'connected' | 'disconnected' | 'connecting'): void {
    const chatInterface = this.shadowRoot?.querySelector('chat-interface') as any;
    if (chatInterface) {
      chatInterface.connectionStatus = status;
      console.log(`🔄 Chat interface connection status updated to: ${status}`);
    } else {
      console.warn('⚠️ Chat interface not found for status update');
    }
  }

  /**
   * Initializes demo mode with fake data (fallback when backend unavailable)
   */
  private initializeDemoMode(): void {
    console.log('🎭 Initializing demo mode (backend unavailable)');

    // Note: Don't override wsConnected here - it might be set correctly by WebSocket manager
    // Only set to false if we're certain the backend is unavailable
    const config = this.configService.getConfig();

    // CRITICAL FIX: Never load demo data - always wait for real backend data
    console.log('🎭 Demo mode initialized - waiting for real backend wheel data (no demo data)');

    this.wheelStateMachine.clearWheel();
    this.messages = [
      {
        id: 'demo-welcome',
        type: 'system',
        content: '🎯 Welcome to Goali! Start chatting to generate your personalized activity wheel.',
        timestamp: new Date()
      }
    ];

    this.error = null;
    console.log(`✅ Demo mode initialized (${this.configService.getMode()} mode, wheel: ${this.wheelData ? 'enabled' : 'disabled'})`);
  }

  /**
   * Sets up WebSocket message handlers
   */
  private setupMessageHandlers(): void {
    // Initialize debug filter
    this.debugFilter.setDebugMode(this.configService.isDebugMode());

    this.websocketManager.onMessage('wheel_data', (data) => {
      this.handleWheelGenerated(data);
    });

    this.websocketManager.onMessage('chat_message', (data) => {
      this.handleAIResponse(data);
    });

    this.websocketManager.onMessage('system_message', (data) => {
      this.handleSystemMessage(data);
    });

    this.websocketManager.onMessage('error', (data) => {
      this.handleError(data);
    });

    // Filter debug messages - only show in debug mode and don't add to chat
    this.websocketManager.onMessage('debug_info', (data) => {
      if (this.debugFilter.shouldShowDebugMessage(data)) {
        // FIXED: Properly extract debug message content based on actual backend structure
        let debugMessage = 'Unknown debug message';

        if (data.content && typeof data.content === 'object') {
          // Priority order: message > details.message > source+level > stringify
          if (data.content.message) {
            debugMessage = data.content.message;
          } else if (data.content.details?.message) {
            debugMessage = data.content.details.message;
          } else if (data.content.source && data.content.level) {
            // FIXED: Better formatting for source+level debug messages
            debugMessage = `[${data.content.source}] ${data.content.message || data.content.level}`;
          } else {
            // FIXED: Better fallback for complex debug objects
            debugMessage = JSON.stringify(data.content, null, 2);
          }
        } else if (data.content && typeof data.content === 'string') {
          debugMessage = data.content;
        } else if (data.message) {
          debugMessage = data.message;
        } else {
          // FIXED: Better fallback for unknown debug message structure
          debugMessage = JSON.stringify(data, null, 2);
        }

        // FIXED: Better handling of undefined values
        if (!debugMessage || debugMessage === 'undefined' || debugMessage.includes('undefined')) {
          const source = data.content?.source || 'unknown';
          const level = data.content?.level || 'info';
          const details = data.content?.details ? JSON.stringify(data.content.details) : 'no details';
          debugMessage = `[${source}:${level}] ${details}`;
        }

        console.log('🔧 Debug:', debugMessage);
      }
      // Don't add debug messages to chat to prevent spam
    });

    // Handle workflow status without adding to chat
    this.websocketManager.onMessage('workflow_status', (data) => {
      if (this.configService.isDebugMode()) {
        console.log('🔄 Workflow status:', data);
      }

      // Reason: Capture the workflow_id from the first status update so cancel_workflow
      // can reference the correct backend workflow if the user dismisses the modal.
      if (data.workflow_id && !this.currentActiveWorkflowId) {
        this.currentActiveWorkflowId = data.workflow_id;
      }

      // CRITICAL FIX: Dismiss progress modal when workflow completes
      if (data.status === 'completed' && this.showProgressBar) {
        console.log('🎯 Workflow completed, dismissing progress modal...');
        // Wait for wheel data to be populated before dismissing progress bar
        this.waitForWheelPopulationAndDismiss();
        this.currentActiveWorkflowId = null;
      }

      // Don't add workflow status to chat
    });

    // Handle processing status
    this.websocketManager.onMessage('processing_status', (data) => {
      this.isLoading = data.status === 'processing';
      if (this.configService.isDebugMode()) {
        console.log('⚙️ Processing status:', data.status);
      }
      // Don't add processing status to chat
    });

    // Handle progress updates directly via WebSocket (backup handler)
    this.websocketManager.onMessage('progress_update', (data) => {
      if (this.configService.isDebugMode()) {
        console.log('📊 WebSocket progress update received:', data);
      }

      // Show progress bar if not already visible
      if (!this.showProgressBar && data.data) {
        this.showProgressBar = true;
        this.currentProgressTrackerId = data.data.tracker_id;
        this.progressTitle = data.data.workflow_type === 'wheel_generation' ? 'Generating Wheel...' : 'Processing...';

        // Set progress modal mode based on user type
        this.progressModalMode = this.currentUser?.isStaff ? 'debug' : 'user-friendly';
      }

      // Forward to progress bar component
      const progressBar = this.shadowRoot?.querySelector('real-time-progress-bar');
      if (progressBar) {
        progressBar.dispatchEvent(new CustomEvent('progress-update', { detail: data }));
      }

      // Reason: Activate stall watchdog on every progress update so we detect stalls
      // at any percentage, not just near completion. The watchdog will warn after 45s
      // without movement (30s at 90%+) and surface a Retry button after a further 30s.
      const progressPercent = data.data?.progress_percent ?? 0;
      if (this.isLoading && progressPercent > 0) {
        this.startProgressStallWatchdog(progressPercent);
      }

      // Hide progress bar when completed and trigger wheel refresh
      if (data.data?.stage === 'completed' && data.data?.progress_percent >= 100) {
        this.clearProgressStallWatchdog();
        // Wait for wheel data to be populated before dismissing progress bar
        this.waitForWheelPopulationAndDismiss();
      }
    });

    // Handle performance metrics
    this.websocketManager.onMessage('performance_metrics', (data) => {
      this.handlePerformanceMetrics(data);
    });

    // Handle workflow progress
    this.websocketManager.onMessage('workflow_progress', (data) => {
      this.handleWorkflowProgress(data);
    });

    this.websocketManager.onConnectionChange((connected) => {
      if (!connected) {
        // Track when disconnection happened for minimum banner display
        this._disconnectedAt = Date.now();
        this.wsConnected = false;
        this.connectionState = 'disconnected';
      } else {
        // Ensure banner stays visible for at least 3 seconds
        const minDisplayMs = 3000;
        const elapsed = Date.now() - (this._disconnectedAt || 0);
        const remaining = Math.max(0, minDisplayMs - elapsed);

        if (remaining > 0 && this._disconnectedAt) {
          // Delay hiding the banner
          setTimeout(() => {
            this.wsConnected = true;
            this.connectionState = 'connected';
          }, remaining);
        } else {
          this.wsConnected = true;
          this.connectionState = 'connected';
        }
        this._disconnectedAt = 0;
      }
      console.log(`🔄 Connection state updated: ${this.connectionState}`);
    });
  }

  /**
   * Convert business WheelData to StateMachine WheelData format
   */
  private convertToStateMachineFormat(businessData: WheelData): StateMachineWheelData {
    // CRITICAL FIX: Use segments instead of items - wheel data structure uses segments
    const sourceArray = businessData.segments || businessData.items || [];
    console.log('🔄 convertToStateMachineFormat: Input data has', sourceArray.length, 'segments/items');

    return {
      segments: sourceArray.map((item: any) => ({
        id: item.id,
        name: item.name || item.text,
        text: item.text || item.name,
        description: item.description,
        instructions: item.instructions || '', // CRITICAL FIX: Preserve instructions field
        percentage: item.percentage || 0,
        color: item.color || '#52C41A',
        activity_tailored_id: item.activity_tailored_id || item.id,
        domain: item.domain,
        base_challenge_rating: item.base_challenge_rating,
        wheel_item_id: item.wheel_item_id || item.id,
        // Preserve dual-color properties for inner/outer wheel rendering
        centerColor: item.centerColor,
        extremityColor: item.extremityColor
      })),
      wheelId: businessData.wheelId,
      createdAt: businessData.createdAt
    };
  }

  /**
   * Convert StateMachine WheelData back to BusinessWheelData format
   * CRITICAL: Must return proper BusinessWheelData with 'items' array
   */
  private convertFromStateMachineFormat(stateMachineData: StateMachineWheelData): WheelData {
    console.log('🔄 convertFromStateMachineFormat: Input data has', stateMachineData.segments.length, 'segments');

    // Convert to proper BusinessWheelData format with 'items' array
    const businessWheelData: WheelData = {
      // Core identification
      wheelId: String(stateMachineData.wheelId),
      name: `Generated Wheel - ${new Date().toLocaleTimeString()}`,

      // Business data - convert segments to items (BusinessWheelData format)
      items: stateMachineData.segments.map((segment: any) => ({
        id: segment.id,
        activity_tailored_id: String(segment.activity_tailored_id),
        name: segment.name || segment.text,
        description: segment.description || '',
        instructions: segment.instructions || '', // CRITICAL FIX: Preserve instructions field
        percentage: segment.percentage || 0,
        color: segment.color || '#52C41A',
        domain: segment.domain || 'general',
        base_challenge_rating: segment.base_challenge_rating || 50,
        duration_minutes: segment.duration_minutes,
        // Optional compatibility fields
        text: segment.text || segment.name,
        wheel_item_id: segment.wheel_item_id || segment.id,
        type: 'tailored' as const,
        // Preserve dual-color properties for wheel rendering
        centerColor: segment.centerColor,
        extremityColor: segment.extremityColor
      })),

      // Metadata
      createdAt: stateMachineData.createdAt,

      // Also add segments for wheel component compatibility during transition
      segments: stateMachineData.segments.map((segment: any) => ({
        id: segment.id,
        name: segment.name || segment.text,
        text: segment.text || segment.name,
        description: segment.description,
        instructions: segment.instructions || '', // CRITICAL FIX: Preserve instructions field
        percentage: segment.percentage || 0,
        color: segment.color || '#52C41A',
        activity_tailored_id: segment.activity_tailored_id,
        domain: segment.domain,
        base_challenge_rating: segment.base_challenge_rating,
        wheel_item_id: segment.wheel_item_id || segment.id,
        centerColor: segment.centerColor,
        extremityColor: segment.extremityColor
      }))
    };

    console.log('🔄 Converted to BusinessWheelData format with', businessWheelData.items.length, 'items');
    return businessWheelData;
  }

  /**
   * Transform business object WheelData to wheel component format
   */
  private transformToWheelComponentFormat(businessData: WheelData): any {
    // CRITICAL FIX: Use segments instead of items - wheel data structure uses segments
    const sourceArray = businessData.segments || businessData.items || [];
    console.log('🔄 transformToWheelComponentFormat: Input data has', sourceArray.length, 'segments/items');

    return {
      segments: sourceArray.map((item: any) => ({
        id: item.id,
        text: item.text || item.name,
        name: item.name || item.text,
        description: item.description,
        instructions: item.instructions, // CRITICAL FIX: Preserve instructions field
        percentage: item.percentage,
        color: item.color,
        domain: item.domain,
        base_challenge_rating: item.base_challenge_rating,
        activity_tailored_id: item.activity_tailored_id,
        wheel_item_id: item.wheel_item_id || item.id,
        activityId: item.activity_tailored_id,
        type: item.type || 'tailored',
        // Preserve dual-color properties for inner/outer wheel rendering
        centerColor: item.centerColor,
        extremityColor: item.extremityColor
      })),
      wheelId: businessData.wheelId,
      createdAt: businessData.createdAt
    };
  }

  /**
   * Handles wheel generation from backend
   */
  private async handleWheelGenerated(data: any): Promise<void> {
    try {
      if (data.wheel && data.wheel.items && Array.isArray(data.wheel.items)) {
        // CRITICAL FIX: Check if backend provided colors BEFORE applying frontend colors
        const backendHasValidColors = data.wheel.items.every((item: any) => {
          return item.color && /^#[0-9A-Fa-f]{6}$/.test(item.color);
        }) && data.wheel.items.length > 0 && (
          data.wheel.items.length === 1 ||
          // For multiple items, ensure they're not all the same color AND not all the fallback grey color
          (new Set(data.wheel.items.map((item: any) => item.color)).size > 1 &&
           !data.wheel.items.every((item: any) => item.color === '#95A5A6'))
        );

        // ARCHITECTURAL FIX: Convert backend wheel format to segments format for state machine
        const wheelDataForStateMachine: any = {

          wheelId: data.wheel.id || data.wheel.wheel_id || `wheel-${Date.now()}`,
          segments: await Promise.all(data.wheel.items.map(async (item: any, index: number) => {
            console.log(`🎨 Backend item ${index + 1} color data:`, {
              name: item.name || item.title,
              color: item.color,
              domain: item.domain,
              fullItem: item
            });

            // ARCHITECTURAL FIX: Apply domain colors using frontend service ONLY if backend doesn't have valid colors
            let finalColor = item.color; // Use backend color if provided
            if (!backendHasValidColors || !finalColor) {
              try {
                // Import domain color service for proper color assignment
                const { getDomainColor } = await import('../services/domainColorService.js');
                finalColor = getDomainColor(item.domain || 'general');
              } catch (error) {
                console.error('Failed to load domain color service:', error);
                finalColor = '#95A5A6'; // Fallback color
              }
            }

            return {
              id: item.id, // This is the wheel item ID (e.g., "item_123")
              // FIXED: Prioritize item.title over item.name for proper activity names
              text: item.title || item.name || 'Activity',
              name: item.name || item.title || 'Activity', // For compatibility
              percentage: item.percentage,
              color: finalColor, // ARCHITECTURAL FIX: Use domain color if backend doesn't provide one
              // FIXED: Add description from backend data
              description: item.description || '',
              instructions: item.instructions || '', // CRITICAL FIX: Preserve instructions field
              // Store the activity tailored ID separately for reference
              activity_tailored_id: item.activity_tailored_id,
              // Store additional metadata
              domain: item.domain,
              base_challenge_rating: item.base_challenge_rating,
              // Ensure we have the wheel item ID for removal operations
              wheel_item_id: item.id
            };
          })),
          createdAt: new Date().toISOString()
        };

        // Enhance wheel data with dual-color system for inner/outer wheel distinction
        const rawWheelData = wheelDataForStateMachine;

        let finalWheelData;
        // CRITICAL FIX: Always enhance with dual-color system for inner/outer wheel distinction
        // Backend only provides single color, but we need centerColor and extremityColor for dual wheel
        finalWheelData = await this.enhanceWheelDataWithColors(rawWheelData);

        // Use State Machine to set wheel data
        this.wheelStateMachine.setWheelData(this.convertToStateMachineFormat(finalWheelData));

        // Add system message about wheel generation
        const message: ChatMessage = {
          id: `msg-${Date.now()}`,
          type: 'system',
          content: 'Your personalized activity wheel has been generated! Give it a spin to choose your next adventure.',
          timestamp: new Date()
        };
        this.messages = [...this.messages, message];

        // Force re-render to ensure wheel component updates
        this.requestUpdate();

        // CRITICAL FIX: Dismiss progress modal when wheel data is successfully processed
        if (this.showProgressBar) {
          this.progressivelyDismissProgressBar();
        }
      } else {
        console.error('❌ Invalid wheel data structure:', data);
        this.handleError({ content: 'Invalid wheel data received from server' });
      }
    } catch (error) {
      console.error('❌ Error processing wheel data:', error);
      this.handleError({ content: 'Failed to process wheel data' });
    }
  }

  /**
   * Handles system messages from backend
   */
  private handleSystemMessage(data: any): void {
    const message: ChatMessage = {
      id: `msg-${Date.now()}`,
      type: 'system',
      content: data.content || 'System message',
      timestamp: new Date()
    };
    this.messages = [...this.messages, message];

    // Force re-render to ensure chat interface updates
    this.requestUpdate();
  }

  /**
   * Handles AI response messages
   */
  private handleAIResponse(data: any): void {
    // Only handle AI messages (not user echoes)
    if (data.is_user) {
      return; // Skip user message echoes
    }

    const message: ChatMessage = {
      id: `msg-${Date.now()}`,
      type: 'ai',
      content: data.content || 'No content',
      timestamp: new Date(),
      metadata: {
        processingTime: data.processingTime
      }
    };

    // CRITICAL FIX: Create new array to trigger Lit reactivity
    this.messages = [...this.messages, message];

    // Force re-render to ensure chat interface updates
    this.requestUpdate();
  }

  /**
   * Handles error messages
   */
  private handleError(data: any): void {
    this.error = data.content || data.message || 'An error occurred';

    const message: ChatMessage = {
      id: `msg-${Date.now()}`,
      type: 'error',
      content: data.content || data.message || 'An error occurred',
      timestamp: new Date(),
      metadata: {
        error: {
          code: data.code || 'UNKNOWN',
          details: data.details || ''
        }
      }
    };
    this.messages = [...this.messages, message];
  }

  /**
   * Close progress modal
   */
  private closeProgressModal = () => {
    this.showProgressBar = false;
    this.currentProgressTrackerId = '';
    // Clear any fallback timers
    if (this.progressFallbackTimer) {
      clearTimeout(this.progressFallbackTimer);
      this.progressFallbackTimer = null;
    }
    this.clearGenerationTimeout();
  };

  /**
   * Cancel ongoing generation and close modal.
   * Sends a cancel_workflow WebSocket message so the backend can mark the workflow
   * as cancelled and suppress delivering stale results to this client.
   */
  private handleCancelGeneration = () => {
    console.log('🚫 User cancelled generation');
    this.clearProgressStallWatchdog();
    this.clearGenerationTimeout();

    // Reason: Notify the backend of cancellation so it can suppress stale wheel_data
    // delivery once the in-flight LangGraph workflow eventually resolves.
    if (this.websocketManager && this.currentActiveWorkflowId) {
      this.websocketManager.sendMessage('cancel_workflow', {
        workflow_id: this.currentActiveWorkflowId
      });
    }
    this.currentActiveWorkflowId = null;

    this.closeProgressModal();
    this.isLoading = false;
  };

  private progressFallbackTimer: NodeJS.Timeout | null = null;
  private generationTimeoutTimer: NodeJS.Timeout | null = null;
  private progressStallTimer: NodeJS.Timeout | null = null; // Detects when progress stalls at 90%+
  private lastProgressValue = 0; // Tracks last received progress to detect stalls
  private _disconnectedAt: number = 0;
  private fallbackProgress = 0;

  /**
   * Start fallback progress simulation if backend doesn't send real-time updates
   */
  private startProgressFallback = (trackerId: string) => {
    this.fallbackProgress = 0;
    let currentStageIndex = 0;

    // Define realistic wheel generation stages with detailed messages
    const wheelGenerationStages = [
      {
        stage_id: 'initialization',
        stage_name: 'Initializing Workflow',
        message: 'Setting up your personalized wheel generation...',
        targetProgress: 15
      },
      {
        stage_id: 'context_analysis',
        stage_name: 'Analyzing Your Profile',
        message: 'Understanding your preferences, goals, and current context...',
        targetProgress: 30
      },
      {
        stage_id: 'resource_assessment',
        stage_name: 'Evaluating Resources',
        message: 'Assessing your available time, energy, and environment...',
        targetProgress: 45
      },
      {
        stage_id: 'activity_generation',
        stage_name: 'Generating Activities',
        message: 'Creating personalized activities tailored to your needs...',
        targetProgress: 70
      },
      {
        stage_id: 'activity_tailoring',
        stage_name: 'Personalizing Activities',
        message: 'Customizing activities based on your unique profile...',
        targetProgress: 85
      },
      {
        stage_id: 'wheel_assembly',
        stage_name: 'Assembling Your Wheel',
        message: 'Finalizing your personalized activity wheel...',
        targetProgress: 95
      }
    ];

    const updateFallbackProgress = () => {
      if (!this.showProgressBar || this.currentProgressTrackerId !== trackerId) {
        return; // Stop if progress bar was closed or tracker changed
      }

      const currentStage = wheelGenerationStages[currentStageIndex];
      if (!currentStage) return;

      // Gradually progress towards the current stage target
      const increment = Math.random() * 8 + 2; // 2-10% increments
      this.fallbackProgress = Math.min(this.fallbackProgress + increment, currentStage.targetProgress);

      const progressBar = this.shadowRoot?.querySelector('real-time-progress-bar');
      if (progressBar) {
        progressBar.dispatchEvent(new CustomEvent('progress-update', {
          detail: {
            data: {
              tracker_id: trackerId,
              progress_percent: this.fallbackProgress,
              stage: 'processing',
              stage_id: currentStage.stage_id,
              stage_name: currentStage.stage_name,
              message: currentStage.message,
              workflow_type: 'wheel_generation',
              timestamp: new Date().toISOString(),
              priority: 'normal'
            }
          }
        }));
      }

      // Move to next stage when current target is reached
      if (this.fallbackProgress >= currentStage.targetProgress && currentStageIndex < wheelGenerationStages.length - 1) {
        currentStageIndex++;
      }

      // Continue updating if not at final stage
      if (currentStageIndex < wheelGenerationStages.length - 1 || this.fallbackProgress < 95) {
        this.progressFallbackTimer = setTimeout(updateFallbackProgress, 1500 + Math.random() * 2500); // 1.5-4 second intervals
      }
    };

    // Start the fallback after 3 seconds if no real updates received
    this.progressFallbackTimer = setTimeout(updateFallbackProgress, 3000);
  };

  /**
   * Start a 120-second timeout for wheel generation.
   * If generation hasn't completed by then, show error and re-enable Generate button.
   */
  private startGenerationTimeout = () => {
    this.clearGenerationTimeout();
    this.generationTimeoutTimer = setTimeout(() => {
      if (this.showProgressBar && this.isLoading) {
        console.warn('⏱️ Generation timeout reached (120s)');
        this.clearProgressStallWatchdog();
        this.wheelStateMachine.setTimedOut();
        this.closeProgressModal();
        this.isLoading = false;
        this.error = 'Generation timed out. Please try again.';
      }
    }, 120_000);
  };

  private clearGenerationTimeout = () => {
    if (this.generationTimeoutTimer) {
      clearTimeout(this.generationTimeoutTimer);
      this.generationTimeoutTimer = null;
    }
  };

  /**
   * Start a stall watchdog that fires after no progress movement is detected.
   * - At any progress level: warns after 45 seconds without movement.
   * - After warning: triggers ERROR state after another 30 seconds (75s total) to surface the Retry button.
   * Reason: Prevents users from being stuck watching a frozen progress bar at any percentage.
   */
  private startProgressStallWatchdog = (currentProgress: number) => {
    // Reason: Only start a new watchdog if progress has actually moved since the last check.
    // This prevents resetting the timer every time we receive a duplicate progress value.
    if (currentProgress === this.lastProgressValue && this.progressStallTimer !== null) {
      return;
    }
    this.clearProgressStallWatchdog();
    this.lastProgressValue = currentProgress;
    // Reason: Use a shorter timeout at high progress (30s) since near-completion stalls
    // are more likely to be genuine failures, and a longer timeout at lower progress (45s)
    // to allow for normal processing variability early in generation.
    const stallThresholdMs = currentProgress >= 90 ? 30_000 : 45_000;
    this.progressStallTimer = setTimeout(() => {
      // Reason: Only trigger stall warning if still loading and progress hasn't moved
      if (this.showProgressBar && this.isLoading && !this.generationStalled) {
        console.warn('⚠️ Generation appears stalled at', this.lastProgressValue, '%');
        this.generationStalled = true;
        this.progressTitle = 'Taking longer than expected...';

        // Reason: After another 30 seconds of stalling with no resolution, transition
        // to ERROR state so the Retry button becomes visible to the user.
        setTimeout(() => {
          if (this.showProgressBar && this.isLoading && this.generationStalled) {
            console.warn('⏱️ Generation stall timeout reached — surfacing Retry button');
            this.clearProgressStallWatchdog();
            this.wheelStateMachine.setTimedOut();
            this.closeProgressModal();
            this.isLoading = false;
            this.error = 'Generation is taking too long. Please retry.';
          }
        }, 30_000);
      }
    }, stallThresholdMs);
  };

  private clearProgressStallWatchdog = () => {
    if (this.progressStallTimer) {
      clearTimeout(this.progressStallTimer);
      this.progressStallTimer = null;
    }
    this.generationStalled = false;
  };

  /**
   * Wait for wheel data to be populated before dismissing progress bar
   * Uses State Machine to check wheel state instead of infinite timeout loop
   */
  private waitForWheelPopulationAndDismiss = () => {
    // Use State Machine to check if wheel is populated
    if (this.wheelStateMachine.shouldShowPopulatedWheel) {
      // Wheel is already populated, start progressive dismissal
      this.progressivelyDismissProgressBar();
    } else {
      // Set up a one-time state machine listener for when wheel gets populated
      const unsubscribe = this.wheelStateMachine.subscribe((context) => {
        if (context.state === 'POPULATED') {
          // Wheel is now populated, dismiss progress bar
          this.progressivelyDismissProgressBar();
          unsubscribe(); // Clean up listener
        }
      });

      // Fallback timeout to prevent infinite waiting
      setTimeout(() => {
        console.warn('⚠️ Wheel population timeout reached, dismissing progress bar');
        this.progressivelyDismissProgressBar();
        unsubscribe(); // Clean up listener
      }, 10000); // 10 second timeout
    }
  };

  private progressDismissalStartTime = 0;

  /**
   * Progressively dismiss the progress bar with fade effect
   */
  private progressivelyDismissProgressBar = () => {
    const progressModal = this.shadowRoot?.querySelector('.progress-modal') as HTMLElement;

    if (progressModal) {
      // Add fade-out class for smooth transition
      progressModal.style.transition = 'opacity 1s ease-out';
      progressModal.style.opacity = '0';

      // Wait for fade animation to complete before hiding
      setTimeout(() => {
        this.showProgressBar = false;
        this.currentProgressTrackerId = '';

        // Clear any fallback timers
        if (this.progressFallbackTimer) {
          clearTimeout(this.progressFallbackTimer);
          this.progressFallbackTimer = null;
        }
        this.clearGenerationTimeout();

        // Force wheel refresh after progress completion
        this.requestUpdate();

        // Also trigger a wheel component update if it exists
        const wheelComponent = this.shadowRoot?.querySelector('game-wheel') as any;
        if (wheelComponent && typeof wheelComponent.requestUpdate === 'function') {
          wheelComponent.requestUpdate();
        }
      }, 1000); // Wait for fade animation
    } else {
      // Fallback if modal not found
      this.showProgressBar = false;
      this.currentProgressTrackerId = '';
      this.requestUpdate();
    }
  };

  /**
   * Handles progress update messages
   */
  private handleProgressUpdate = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const data = customEvent.detail;

    if (this.configService.isDebugMode()) {
      console.log('📊 Progress update:', data);
    }

    // Show progress bar if not already visible
    if (!this.showProgressBar && data.data) {
      this.showProgressBar = true;
      this.currentProgressTrackerId = data.data.tracker_id;
      this.progressTitle = data.data.workflow_type === 'wheel_generation' ? 'Generating Wheel...' : 'Processing...';

      // Set progress modal mode based on user type
      this.progressModalMode = this.currentUser?.isStaff ? 'debug' : 'user-friendly';
    }

    // Forward to progress bar component
    const progressBar = this.shadowRoot?.querySelector('real-time-progress-bar');
    if (progressBar) {
      progressBar.dispatchEvent(new CustomEvent('progress-update', { detail: data }));
    }

    // Reason: Activate stall watchdog on every progress update so we detect stalls
    // at any percentage. The watchdog warns after 45s without movement (30s at 90%+).
    const progressPercent = data.data?.progress_percent ?? 0;
    if (this.isLoading && progressPercent > 0) {
      this.startProgressStallWatchdog(progressPercent);
    }

    // Hide progress bar when completed and ensure wheel refresh
    if (data.data?.stage === 'completed' && data.data?.progress_percent >= 100) {
      this.clearProgressStallWatchdog();
      setTimeout(() => {
        this.showProgressBar = false;
        this.currentProgressTrackerId = '';

        // Force a complete re-render to ensure wheel data is displayed
        this.requestUpdate();

        // Also trigger a wheel component update if it exists
        const wheelComponent = this.shadowRoot?.querySelector('game-wheel') as any;
        if (wheelComponent && typeof wheelComponent.requestUpdate === 'function') {
          wheelComponent.requestUpdate();
        }
      }, 2000); // Keep visible for 2 seconds after completion
    }
  }

  /**
   * Handles performance metrics messages
   */
  private handlePerformanceMetrics(data: any): void {
    if (this.configService.isDebugMode()) {
      console.log('⚡ Performance metrics:', data);
    }

    // Forward to progress bar component
    const progressBar = this.shadowRoot?.querySelector('real-time-progress-bar');
    if (progressBar) {
      progressBar.dispatchEvent(new CustomEvent('performance-metrics', { detail: data }));
    }
  }

  /**
   * Handles workflow progress messages
   */
  private handleWorkflowProgress(data: any): void {
    if (this.configService.isDebugMode()) {
      console.log('🔄 Workflow progress:', data);
    }

    // Show progress bar if not already visible
    if (!this.showProgressBar && data.data) {
      this.showProgressBar = true;
      this.progressTitle = `${data.data.workflow_type?.replace('_', ' ').toUpperCase() || 'Processing'}...`;
    }

    // Forward to progress bar component
    const progressBar = this.shadowRoot?.querySelector('real-time-progress-bar');
    if (progressBar) {
      progressBar.dispatchEvent(new CustomEvent('workflow-progress', { detail: data }));
    }

    // Hide progress bar when workflow is complete
    if (data.data?.overall_progress >= 100) {
      setTimeout(() => {
        this.showProgressBar = false;
      }, 2000);
    }
  }

  /**
   * Load debug selections from localStorage
   */
  private loadDebugSelections(): void {
    if (this.configService.isDebugMode()) {
      try {
        this.debugUserId = localStorage.getItem('debug_selected_user_id') || '';
        this.debugLLMConfigId = localStorage.getItem('debug_selected_llm_config_id') || '';
      } catch (error) {
        console.warn('Failed to load debug selections:', error);
      }
    }
  }

  /**
   * Check for active enjoy overlay from previous session
   */
  private checkForActiveEnjoyOverlay(): void {
    const result = this.postSpinService.checkForActiveEnjoyOverlay();

    if (result.shouldShowEnjoy && result.activity) {
      console.log('🎯 Restoring enjoy overlay from previous session');
      this.postSpinActivity = result.activity;
      this.postSpinStartTime = this.postSpinService.getStoredSpinData()?.timestamp || Date.now();
      this.postSpinView = 'enjoy';

      // Set up timer to transition to feedback modal after remaining time
      const { remainingMs } = this.postSpinService.getRemainingTime(this.postSpinStartTime);
      setTimeout(() => {
        this.transitionToFeedbackModal();
      }, remainingMs);
    } else if (result.shouldShowFeedback && result.activity) {
      console.log('🎯 Showing post-activity feedback modal from previous session');
      this.showPostActivityFeedbackWithActivity(result.activity);
    }
  }

  /**
   * Show enjoy overlay with activity details
   */
  private showEnjoyOverlayWithActivity(activity: any): void {
    // Store in localStorage and set component state
    this.postSpinService.storeSpinData(activity);
    this.postSpinActivity = activity;
    this.postSpinStartTime = Date.now();
    this.postSpinView = 'enjoy';

    // Auto-transition to feedback modal after 10 minutes
    setTimeout(() => {
      this.transitionToFeedbackModal();
    }, 10 * 60 * 1000);
  }

  /**
   * Transition from enjoy overlay to feedback modal
   */
  private transitionToFeedbackModal(): void {
    if (this.postSpinView === 'enjoy' && this.postSpinActivity) {
      this.postSpinView = 'feedback';
    }
  }

  /**
   * Show post-activity feedback modal with activity details
   */
  private showPostActivityFeedbackWithActivity(activity: any): void {
    // Store in localStorage and set component state
    this.postSpinService.storeSpinData(activity);
    this.postSpinActivity = activity;
    this.postSpinView = 'feedback';
  }







  /**
   * Load current user information
   */
  private loadCurrentUser(): void {
    const previousUserId = this.currentUser?.id;

    if (this.configService.isDebugMode() && this.debugUserId) {
      // In debug mode, use debug user selection
      this.currentUser = {
        id: this.debugUserId,
        name: `Debug User ${this.debugUserId}`,
        isStaff: true
      };
    } else {
      // Use authenticated user from auth service
      const authUser = authService.getCurrentUser();
      if (authUser) {
        this.currentUser = {
          id: authUser.id,
          name: authUser.first_name || authUser.username || `User ${authUser.id}`,
          isStaff: authUser.is_staff || false
        };
      } else {
        // No authenticated user
        this.currentUser = null;
      }
    }

    // Invalidate activity cache if user changed
    if (previousUserId !== this.currentUser?.id) {
      this.invalidateActivityCache();
    }
  }

  /**
   * Get button text based on connection state
   */
  private getButtonText(): string {
    if (this.isLoading) {
      return 'Generating...';
    }

    switch (this.connectionState) {
      case 'disconnected':
        return 'Connecting...';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Generate';
      default:
        return 'Generate';
    }
  }

  /**
   * Get connection status text
   */
  private getConnectionStatusText(): string {
    switch (this.connectionState) {
      case 'disconnected':
        return 'Disconnected';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Connected';
      default:
        return 'Unknown';
    }
  }

  /**
   * Open user profile management
   */
  private openUserProfile(): void {
    // Show mobile-friendly profile modal instead of opening admin page
    this.showProfileModal = true;
    this.loadCurrentUserProfile();
  }

  /**
   * Load current user profile data
   */
  private async loadCurrentUserProfile(): Promise<void> {
    try {
      console.log('🔄 Loading user profile data from API...');

      const config = this.configService.getConfig();
      const baseUrl = config.websocket.url.replace('ws://', 'http://').replace('wss://', 'https://').replace('/ws/game/', '');

      // SECURITY FIX: Use authenticated user's ID, not hardcoded fallback
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.id) {
        console.error('❌ No authenticated user found - cannot load profile');
        throw new Error('User not authenticated');
      }

      // Use the comprehensive user profile API endpoint that includes all profile data
      const apiUrl = `${baseUrl}/api/user/profile/complete/`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.profile) {
          this.currentUserProfile = {
            id: data.profile.id,
            name: data.profile.name,
            description: data.profile.description,
            demographics: data.profile.demographics,
            environment: data.profile.environment,
            environments: data.profile.environments,
            preferences: data.profile.preferences,
            goals: data.profile.goals,
            skills: data.profile.skills,
            beliefs: data.profile.beliefs,
            traits: data.profile.traits,
            trust_level: data.profile.trust_level,
            resources: data.profile.resources,
            statistics: data.profile.statistics,
            is_real: data.profile.is_real
          };
          console.log('✅ User profile loaded successfully:', this.currentUserProfile);
        } else {
          throw new Error(data.error || 'Failed to load profile data');
        }
      } else {
        console.warn('⚠️ Profile API failed, using fallback data');
        // SECURITY FIX: Use authenticated user's data in fallback, not hardcoded PhiPhi
        const currentUser = authService.getCurrentUser();
        this.currentUserProfile = {
          id: currentUser?.id || 'unknown',
          name: currentUser?.first_name || currentUser?.username || 'Unknown User',
          description: 'Profile data not available',
          demographics: {
            age: 'Not set',
            location: 'Not set',
            occupation: 'Not set'
          },
          environment: {
            living_situation: 'Not set',
            available_resources: 'Not set',
            constraints: 'Not set'
          },
          goals: {
            short_term: 'Not set',
            long_term: 'Not set',
            motivation: 'Not set'
          }
        };
      }
    } catch (error) {
      console.error('❌ Failed to load user profile:', error);
      // SECURITY FIX: Use authenticated user's data in error fallback, not hardcoded values
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.id) {
        // If no authenticated user, throw error instead of using fallback data
        throw new Error('Cannot load profile: User not authenticated');
      }

      this.currentUserProfile = {
        id: (this.configService.isDebugMode() && this.debugUserId) ? this.debugUserId : currentUser.id,
        name: currentUser.first_name || currentUser.username || 'Unknown User',
        description: 'Profile data not available',
        demographics: {
          age: 'Not set',
          location: 'Not set',
          occupation: 'Not set'
        },
        environment: {
          living_situation: 'Not set',
          available_resources: 'Not set',
          constraints: 'Not set'
        },
        goals: {
          short_term: 'Not set',
          long_term: 'Not set',
          motivation: 'Not set'
        }
      };
    }
  }

  /**
   * Handles message sending from chat interface
   */
  private async handleMessageSend(event: CustomEvent): Promise<void> {
    const { message } = event.detail;

    // Add user message to chat
    this.messages = [...this.messages, message];

    if (this.wsConnected) {
      // SECURITY FIX: Get current user ID without hardcoded fallback
      let userId: string;
      if (this.currentUserProfile?.id) {
        userId = this.currentUserProfile.id;
      } else if (this.configService.isDebugMode() && this.debugUserId) {
        userId = this.debugUserId;
      } else {
        const currentUser = authService.getCurrentUser();
        if (!currentUser?.id) {
          console.error('❌ No authenticated user found - cannot send message');
          return;
        }
        userId = currentUser.id;
      }

      console.log('Sending message with debug selections:', { userId, llmConfigId: this.debugLLMConfigId });

      // CRITICAL FIX: Use message handler's sendChatMessage method to include conversation state
      try {
        await this.messageHandler.sendChatMessage(
          message.content,
          userId,
          undefined, // workflowType
          this.configService.isDebugMode() ? this.debugLLMConfigId : undefined // llmConfigId
        );
      } catch (error) {
        console.error('Failed to send message:', error);
        // Fallback to direct WebSocket send if message handler fails
        this.websocketManager.sendMessage('chat_message', {
          message: message.content,
          user_profile_id: userId,
          timestamp: message.timestamp.toISOString(),
          metadata: this.configService.isDebugMode() && this.debugLLMConfigId ? {
            llm_config_id: this.debugLLMConfigId
          } : {}
        });
      }
    } else {
      // Demo mode: simulate AI response
      this.simulateAIResponse(message.content);
    }
  }

  /**
   * Simulates AI response in demo mode
   */
  private simulateAIResponse(userMessage: string): void {
    setTimeout(() => {
      const responses = [
        "That's interesting! In the full version, I would analyze your message and provide personalized guidance. For now, try spinning the wheel to explore the sample activities!",
        "I understand! While this is demo mode, you can still experience the core wheel functionality. Each activity is designed to be quick and rewarding.",
        "Great question! In the complete version, I would create custom activities based on your interests, goals, and current situation. The wheel you see contains some popular starter activities.",
        "Thanks for sharing! The wheel spinning experience you're about to try is the same as in the full version - realistic physics and engaging interactions await!"
      ];

      const randomResponse = responses[Math.floor(Math.random() * responses.length)];

      const aiMessage = {
        id: `demo-ai-${Date.now()}`,
        type: 'ai' as const,
        content: randomResponse,
        timestamp: new Date(),
        metadata: {
          processingTime: 800 + Math.random() * 400 // Simulate realistic processing time
        }
      };

      this.messages = [...this.messages, aiMessage];
    }, 1000 + Math.random() * 1000); // Simulate realistic response delay
  }

  /**
   * Handles wheel spin completion
   */
  private handleWheelSpinComplete(event: CustomEvent): void {
    console.log('🎯 [APP-SHELL] handleWheelSpinComplete called!', event.detail);
    const { winningSegment, finalAngle, duration } = event.detail;

    if (winningSegment) {
      console.log('🎯 [APP-SHELL] Processing winning segment:', winningSegment);

      // ARCHITECTURAL FIX: Robust data merging for complete activity information
      const originalActivity = this.wheelData?.segments?.find(segment =>
        segment.id === winningSegment.activityId ||
        segment.id === winningSegment.id ||
        segment.activity_tailored_id === winningSegment.activityId ||
        segment.activity_tailored_id === winningSegment.id
      );

      console.log('🎯 [APP-SHELL] Found original activity:', originalActivity);

      // Create comprehensive winning activity data
      this.winningActivity = {
        // Base data from winning segment
        id: winningSegment.id || winningSegment.activityId,
        name: winningSegment.text || winningSegment.name,
        color: winningSegment.color,
        percentage: winningSegment.percentage,

        // Enhanced data from original activity (using any type for flexible property access)
        description: (originalActivity as any)?.description || (originalActivity as any)?.tailored_description || 'No description available',
        instructions: (originalActivity as any)?.instructions || (originalActivity as any)?.tailored_instructions || '',
        domain: originalActivity?.domain || 'general',
        base_challenge_rating: originalActivity?.base_challenge_rating || (originalActivity as any)?.challenge_rating || 50,
        duration_range: (originalActivity as any)?.duration_range || (originalActivity as any)?.estimated_duration || 'Flexible',

        // Activity type and metadata
        type: (originalActivity as any)?.type || (originalActivity?.activity_tailored_id ? 'tailored' : 'generic'),
        activity_tailored_id: originalActivity?.activity_tailored_id || winningSegment.activityId,

        // Wheel item ID for feedback linking
        wheel_item_id: winningSegment.id || winningSegment.activityId,

        // Spin metadata
        spinDuration: duration,
        finalAngle: finalAngle,

        // Additional fields that might be available
        requirements: (originalActivity as any)?.requirements || (originalActivity as any)?.social_requirements,
        materials: (originalActivity as any)?.materials,
        icon: (originalActivity as any)?.icon || ((originalActivity as any)?.type === 'tailored' ? '⭐' : '🎯')
      };

      console.log('🎯 [APP-SHELL] Complete winning activity data:', this.winningActivity);

      // Show winning modal with 2-second delay for better UX
      setTimeout(() => {
        this.showWinningModal = true;
        this.requestUpdate();
      }, 2000);

      // Add wheel result message
      const message: ChatMessage = {
        id: `msg-${Date.now()}`,
        type: 'wheel-result',
        content: `🎯 You spun: ${winningSegment.text}`,
        timestamp: new Date(),
        metadata: {
          wheelResult: {
            segmentId: winningSegment.id,
            segmentText: winningSegment.text,
            spinDuration: duration
          }
        }
      };
      this.messages = [...this.messages, message];

      // Send result to backend (if connected)
      if (this.wsConnected) {
        // Find the original activity data from wheelData to get proper description
        const originalActivity = this.wheelData?.segments?.find(segment =>
          segment.id === winningSegment.activityId
        );

        console.log('🎯 [APP-SHELL] Wheel spin completed - post-spin handled directly via API endpoints');
        // Note: Post-spin feedback is now handled directly via API endpoints,
        // no need to send messages to conversation dispatcher
      } else {
        // Demo mode: simulate follow-up message
        setTimeout(() => {
          const followUpMessage = {
            id: `demo-followup-${Date.now()}`,
            type: 'ai' as const,
            content: `Great choice! "${winningSegment.text}" is a wonderful activity. In the full version, I would provide personalized tips and track your progress. For now, enjoy exploring this activity!`,
            timestamp: new Date()
          };
          this.messages = [...this.messages, followUpMessage];
        }, 2000);
      }
    }
  }

  /**
   * Handle login success
   */
  private handleLoginSuccess(): void {
    this.isAuthenticated = true;
    this.initializeApp();
  }

  /**
   * Handle demo mode request
   */
  private handleDemoModeRequest(): void {
    // Switch to demo mode temporarily
    this.isAuthenticated = true;
    this.initializeDemoMode();
  }

  /**
   * Handle logout
   */
  private async handleLogout(): Promise<void> {
    try {
      // Immediately clear authentication state to prevent any brief login modal flash
      this.isAuthenticated = false;
      this.currentUser = null;

      // Clear all application state
      this.wsConnected = false;
      this.connectionState = 'disconnected';
      this.wheelStateMachine.clearWheel();
      this.messages = [];
      this.error = null;
      this.showDebugPanel = false;
      this.showActivityModal = false;
      this.showCreateActivityModal = false;
      this.showProfileModal = false;
      this.showWinningModal = false;

      // Disconnect WebSocket
      this.websocketManager.disconnect();

      // Force immediate re-render to show logged out state
      this.requestUpdate();

      // Now perform the actual logout
      await authService.logout();

      // Force a complete page reload to ensure clean state
      // This prevents any debug mode authentication bypass from interfering
      setTimeout(() => {
        window.location.reload();
      }, 50); // Reduced timeout to minimize flash

      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('❌ Logout failed:', error);
      // Force logout even if server request fails
      this.isAuthenticated = false;
      this.currentUser = null;

      // Force page reload on error too
      setTimeout(() => {
        window.location.reload();
      }, 50);
    }
  }

  /**
   * Clears error state
   */
  private handleClearError(): void {
    this.error = null;
  }

  private handleTimeAvailableChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const stepIndex = parseInt(target.value);
    this.timeAvailable = this.getTimeFromStepIndex(stepIndex);
    // TODO: Send update to backend
  };

  /**
   * Get discrete time steps: 10min ("quicky"), 20min (default), 40min, 1h, 2h, 4h ("all-in")
   */
  private getTimeSteps(): Array<{value: number, label: string, minutes: number}> {
    return [
      { value: 10, label: '10min', minutes: 10 },
      { value: 20, label: '20min', minutes: 20 },
      { value: 40, label: '40min', minutes: 40 },
      { value: 60, label: '1h', minutes: 60 },
      { value: 120, label: '2h', minutes: 120 },
      { value: 240, label: '4h', minutes: 240 }
    ];
  }

  /**
   * Convert time available value to minutes - now using direct minute values
   */
  private getTimeInMinutes(): number {
    // timeAvailable now directly represents minutes
    return this.timeAvailable;
  }

  /**
   * Get the current time step index for the slider
   */
  private getTimeStepIndex(): number {
    const steps = this.getTimeSteps();
    const currentMinutes = this.timeAvailable;

    // Find the index of the current time value
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].minutes === currentMinutes) {
        return i;
      }
    }

    // Default to index 1 (20min) if not found
    return 1;
  }

  /**
   * Convert slider index to time minutes
   */
  private getTimeFromStepIndex(index: number): number {
    const steps = this.getTimeSteps();
    if (index >= 0 && index < steps.length) {
      return steps[index].minutes;
    }
    // Default to 20min if index is out of range
    return 20;
  }

  /**
   * Format time available value to display string with special labels
   */
  private formatTimeAvailable(): string {
    const minutes = this.timeAvailable;

    // Special labels for specific values
    switch (minutes) {
      case 10:
        return '10min ("quicky")';
      case 20:
        return '20min (default)';
      case 240:
        return '4h ("all-in")';
      default:
        // Standard formatting for other values
        if (minutes < 60) {
          return `${minutes}min`;
        } else {
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          if (remainingMinutes === 0) {
            return `${hours}h`;
          } else {
            return `${hours}h ${remainingMinutes}min`;
          }
        }
    }
  }

  private handleEnergyLevelChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const rawValue = parseInt(target.value);

    // Snap to nearest discrete step
    const steps = this.getEnergySteps();
    let closestStep = steps[0];
    let minDistance = Math.abs(rawValue - steps[0].value);

    for (const step of steps) {
      const distance = Math.abs(rawValue - step.value);
      if (distance < minDistance) {
        minDistance = distance;
        closestStep = step;
      }
    }

    // Set to the closest discrete value
    this.energyLevel = closestStep.value;

    // Update the slider to show the snapped value
    target.value = this.energyLevel.toString();

    // TODO: Send update to backend
  };

  // Creativity level handler removed - creativity is now handled as fixed value in agents

  // 🆕 Enhanced energy slider handlers
  private handlePhysicalEnergyChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.physicalEnergy = parseInt(target.value);
  };

  private handleMentalEnergyChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.mentalEnergy = parseInt(target.value);
  };

  private handleSocialEnergyChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.socialEnergy = parseInt(target.value);
  };

  private handleEmotionalEnergyChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.emotionalEnergy = parseInt(target.value);
  };

  private handleEnergyDirectionChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.energyDirection = parseInt(target.value);
  };





  /**
   * Get discrete energy steps: -- (10%), - (40%), + (60%), ++ (90%)
   */
  private getEnergySteps(): Array<{value: number, label: string, percentage: number}> {
    return [
      { value: 10, label: '--', percentage: 10 },
      { value: 40, label: '-', percentage: 40 },
      { value: 60, label: '+', percentage: 60 },
      { value: 90, label: '++', percentage: 90 }
    ];
  }

  // Creativity steps method removed - creativity is now handled as fixed value in agents

  /**
   * Get energy level using discrete steps
   */
  private getEnergyLevelPercentage(): number {
    const steps = this.getEnergySteps();

    // Find the closest step
    let closestStep = steps[0];
    let minDistance = Math.abs(this.energyLevel - steps[0].value);

    for (const step of steps) {
      const distance = Math.abs(this.energyLevel - step.value);
      if (distance < minDistance) {
        minDistance = distance;
        closestStep = step;
      }
    }

    return closestStep.percentage;
  }

  /**
   * Format energy level using discrete steps
   */
  private formatEnergyLevel(): string {
    const steps = this.getEnergySteps();

    // Find the closest step
    let closestStep = steps[0];
    let minDistance = Math.abs(this.energyLevel - steps[0].value);

    for (const step of steps) {
      const distance = Math.abs(this.energyLevel - step.value);
      if (distance < minDistance) {
        minDistance = distance;
        closestStep = step;
      }
    }

    return closestStep.label;
  }

  // Format creativity level method removed - creativity is now handled as fixed value in agents

  // 🆕 Enhanced energy formatting methods
  private formatPhysicalEnergy(): string {
    if (this.physicalEnergy >= 80) return 'Very High';
    if (this.physicalEnergy >= 60) return 'High';
    if (this.physicalEnergy >= 40) return 'Moderate';
    if (this.physicalEnergy >= 20) return 'Low';
    return 'Very Low';
  }

  private formatMentalEnergy(): string {
    if (this.mentalEnergy >= 80) return 'Very High';
    if (this.mentalEnergy >= 60) return 'High';
    if (this.mentalEnergy >= 40) return 'Moderate';
    if (this.mentalEnergy >= 20) return 'Low';
    return 'Very Low';
  }

  private formatSocialEnergy(): string {
    if (this.socialEnergy >= 80) return 'Very High';
    if (this.socialEnergy >= 60) return 'High';
    if (this.socialEnergy >= 40) return 'Moderate';
    if (this.socialEnergy >= 20) return 'Low';
    return 'Very Low';
  }

  private formatEmotionalEnergy(): string {
    if (this.emotionalEnergy >= 80) return 'Very Assertive';
    if (this.emotionalEnergy >= 60) return 'Assertive';
    if (this.emotionalEnergy >= 40) return 'Balanced';
    if (this.emotionalEnergy >= 20) return 'Gentle';
    return 'Very Gentle';
  }

  private formatEnergyDirection(): string {
    if (this.energyDirection >= 80) return 'Very Outward';
    if (this.energyDirection >= 60) return 'Outward';
    if (this.energyDirection >= 40) return 'Balanced';
    if (this.energyDirection >= 20) return 'Inward';
    return 'Very Inward';
  }





  private getEnhancedEnergySteps(): Array<{value: number, label: string}> {
    return [
      { value: 10, label: 'Very Low' },
      { value: 30, label: 'Low' },
      { value: 50, label: 'Moderate' },
      { value: 70, label: 'High' },
      { value: 90, label: 'Very High' }
    ];
  }

  /**
   * Toggle hamburger menu
   */
  private toggleHamburgerMenu = () => {
    this.isHamburgerMenuOpen = !this.isHamburgerMenuOpen;
  };

  /**
   * Close hamburger menu
   */
  private closeHamburgerMenu = () => {
    this.isHamburgerMenuOpen = false;
  };

  /**
   * Open inventory modal
   */
  private openInventoryModal = async () => {
    this.showInventoryModal = true;
    this.closeHamburgerMenu();
    await this.loadInventoryData();
  };

  /**
   * Close inventory modal
   */
  private closeInventoryModal = () => {
    this.showInventoryModal = false;
    this.inventoryData = null;
  };

  /**
   * Open environment switcher modal
   */
  private openEnvironmentSwitcherModal = async () => {
    this.showEnvironmentSwitcherModal = true;
    this.closeHamburgerMenu();
    await this.loadEnvironmentData();
  };

  /**
   * Close environment switcher modal
   */
  private closeEnvironmentSwitcherModal = () => {
    this.showEnvironmentSwitcherModal = false;
    this.environmentData = null;
  };

  /**
   * Handle environment switch
   */
  private handleEnvironmentSwitch = async (event: CustomEvent) => {
    const { environmentId } = event.detail;
    console.log('🌍 Environment switching requested for ID:', environmentId);

    try {
      // Update the environment as current
      await this.updateEnvironmentCurrentStatus(environmentId, true);

      // Close the modal
      this.closeEnvironmentSwitcherModal();

      console.log('✅ Environment switched successfully');
    } catch (error) {
      console.error('❌ Failed to switch environment:', error);
    }
  };

  /**
   * Load environment data from user profile API
   */
  // Inventory-centric: map inventories to environment-like items for UI reuse
  // Each "environment" row actually represents an inventory. ID = inventory.id

  private async loadEnvironmentData(): Promise<void> {
    try {
      if (!this.currentUser?.id) {
        console.error('❌ No current user for inventory loading');
        return;
      }

      // Load inventories from backend and map to environment-like structure for modal reuse
      await this.loadInventoryData();

      const inventories = this.inventoryData?.inventories || [];
      const environments = inventories.map((inv: any) => ({
        id: inv.id, // Treat inventory ID as selection ID
        environment_name: inv.environment_name || inv.name || 'Unnamed',
        environment_description: inv.notes || '',
        is_current: !!inv.is_current_environment,
        generic_environment: undefined
      }));

      this.environmentData = {
        environments,
        current_environment_id: environments.find(env => env.is_current)?.id
      };
      console.log('✅ Inventory-mapped environment data loaded:', this.environmentData);
    } catch (error) {
      console.error('❌ Error loading inventory-mapped environment data:', error);
    }
  }

  /**
   * Load inventory data from user inventory API
   */
  private async loadInventoryData(): Promise<void> {
    try {
      if (!this.currentUser?.id) {
        console.error('❌ No current user for inventory loading');
        return;
      }

      const baseUrl = this.getBackendBaseUrl();
      const response = await fetch(`${baseUrl}/api/user/inventories/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const inventories: InventoryItem[] = data.inventories || [];
          // Reason: Resolve the current environment name from the API response first,
          // then fall back to finding the current inventory's environment_name, then
          // to the profile-based lookup. This prevents "Loading..." appearing as a
          // literal string when currentUserProfile has not yet been fetched.
          const currentInv = inventories.find((inv: InventoryItem) => inv.is_current_environment);
          const resolvedEnvName =
            data.current_environment_name ||
            currentInv?.environment_name ||
            this.resolveEnvironmentNameFromProfile();
          this.inventoryData = {
            inventories,
            unassigned_resources: data.unassigned_resources || [],
            current_environment_name: resolvedEnvName
          };
          console.log('✅ Inventory data loaded:', this.inventoryData);
        } else {
          console.error('❌ Failed to load inventory data:', data.error);
          // Set empty state on error
          this.inventoryData = {
            inventories: [],
            unassigned_resources: [],
            current_environment_name: this.resolveEnvironmentNameFromProfile()
          };
        }
      } else {
        console.error('❌ Failed to load inventory data:', response.status, await response.text());
        // Set empty state on error
        this.inventoryData = {
          inventories: [],
          unassigned_resources: [],
          current_environment_name: this.resolveEnvironmentNameFromProfile()
        };
      }
    } catch (error) {
      console.error('❌ Error loading inventory data:', error);
      // Set empty state on error
      this.inventoryData = {
        inventories: [],
        unassigned_resources: [],
        current_environment_name: this.resolveEnvironmentNameFromProfile()
      };
    }
  }

  /**
   * Set current inventory (replaces environment switching)
   */
  private async updateEnvironmentCurrentStatus(inventoryId: number, isCurrent: boolean): Promise<void> {
    try {
      const baseUrl = this.getBackendBaseUrl();
      const response = await fetch(`${baseUrl}/api/user/inventories/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inventory_id: inventoryId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log(`✅ Inventory ${inventoryId} set as current context.`);
          // Reload environment data (mapped from inventories) to reflect changes
          await this.loadEnvironmentData();
        } else {
          console.error('❌ Failed to set current inventory:', data.error);
        }
      } else {
        console.error('❌ Failed to set current inventory:', response.status, await response.text());
      }
    } catch (error) {
      console.error('❌ Error setting current inventory:', error);
    }
  }

  /**
   * Create new inventory for user
   */
  private async createInventory(name: string, environmentId?: number): Promise<void> {
    try {
      const baseUrl = this.getBackendBaseUrl();
      const requestData: any = { name };

      if (environmentId) {
        requestData.environment_id = environmentId;
      }

      const response = await fetch(`${baseUrl}/api/user/inventories/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('✅ Inventory created successfully:', data.inventory);
          // Reload inventory data to show the new inventory
          await this.loadInventoryData();
        } else {
          console.error('❌ Failed to create inventory:', data.error);
        }
      } else {
        console.error('❌ Failed to create inventory:', response.status, await response.text());
      }
    } catch (error) {
      console.error('❌ Error creating inventory:', error);
    }
  }

  /**
   * Handle inventory creation request from modal
   */
  private handleCreateInventory = async (event: CustomEvent) => {
    const { environmentName } = event.detail;
    console.log('🎒 Creating inventory for environment:', environmentName);

    // Get current environment ID
    const currentEnvironmentId = this.environmentData?.current_environment_id;

    // Create inventory name based on environment
    const inventoryName = `${environmentName} Inventory`;

    await this.createInventory(inventoryName, currentEnvironmentId);
  };




  /**
   * Move resource to inventory (mock implementation)
   */
  private moveResourceToInventory(item: any, inventoryId: number): void {
    if (!this.inventoryData) return;

    // Remove from current location
    this.inventoryData.unassigned_resources = this.inventoryData.unassigned_resources.filter((r: any) => r.id !== item.id);
    this.inventoryData.inventories.forEach((inv: any) => {
      inv.resources = inv.resources.filter((r: any) => r.id !== item.id);
    });

    // Add to target inventory
    const targetInventory = this.inventoryData.inventories.find((inv: any) => inv.id === inventoryId);
    if (targetInventory) {
      targetInventory.resources.push(item);
    }

    this.requestUpdate();
  };

  /**
   * Remove resource (mock implementation)
   */
  private removeResource(item: any): void {
    if (!this.inventoryData) return;

    // Remove from all locations
    this.inventoryData.unassigned_resources = this.inventoryData.unassigned_resources.filter((r: any) => r.id !== item.id);
    this.inventoryData.inventories.forEach((inv: any) => {
      inv.resources = inv.resources.filter((r: any) => r.id !== item.id);
    });

    this.requestUpdate();
  };

  /**
   * Get current environment name
   */
  private getCurrentEnvironmentName(): string {
    // Reason: Return "Loading..." while profile data is still being fetched to avoid
    // showing "Unknown Location" or "No environment set" as a false permanent state.
    if (!this.currentUserProfile) {
      return 'Loading...';
    }

    // Check current user profile environment first
    if (this.currentUserProfile?.environment?.name) {
      return this.currentUserProfile.environment.name;
    }

    // Fallback to environment data if available
    if (this.environmentData?.environments) {
      const currentEnv = this.environmentData.environments.find(env => env && env.is_current);

      // Handle different possible property names based on actual data structure
      if (currentEnv?.environment_name) {
        return currentEnv.environment_name;
      }

      // Check generic_environment.name as fallback
      if (currentEnv?.generic_environment?.name) {
        return currentEnv.generic_environment.name;
      }
    }

    return 'No environment set';
  }

  /**
   * Resolve environment name for static display contexts (e.g. modals that load on open).
   * Unlike getCurrentEnvironmentName(), this never returns "Loading..." — it returns
   * a stable fallback string when data is not yet available, preventing the literal
   * string "Loading..." from appearing as persistent modal content.
   */
  private resolveEnvironmentNameFromProfile(): string {
    // Check profile first (most authoritative)
    if (this.currentUserProfile?.environment?.name) {
      return this.currentUserProfile.environment.name;
    }

    // Check cached environment data
    if (this.environmentData?.environments) {
      const currentEnv = this.environmentData.environments.find(env => env && env.is_current);
      if (currentEnv?.environment_name) {
        return currentEnv.environment_name;
      }
      if (currentEnv?.generic_environment?.name) {
        return currentEnv.generic_environment.name;
      }
    }

    // Reason: Return "Current Location" instead of "Loading..." so that modals
    // opened before profile data arrives show a meaningful, non-confusing label.
    return 'Current Location';
  }

  /**
   * Handle profile button click from hamburger menu
   */
  private handleProfileFromMenu = () => {
    this.openUserProfile();
    this.closeHamburgerMenu();
  };

  /**
   * Handle logout from hamburger menu
   */
  private handleLogoutFromMenu = () => {
    this.handleLogout();
    this.closeHamburgerMenu();
  };



  /**
   * Handle generate wheel button click
   */
  private handleGenerateWheel = async () => {
    if (!this.wsConnected || this.isLoading) {
      return;
    }

    // Reason: Ensure the hamburger menu is closed before starting generation so that
    // the menu-open class does not linger and block UI interactions during progress display.
    this.closeHamburgerMenu();

    try {
      this.isLoading = true;
      this.error = null;

      // Reason: Transition state machine to LOADING so the ERROR overlay disappears
      // immediately when the user clicks Retry, providing instant visual feedback.
      this.wheelStateMachine.startLoading();

      // Reset stall state from any previous generation attempt
      this.clearProgressStallWatchdog();

      // Reason: Clear any stale workflow ID from a previous run so cancel messages
      // reference the correct backend workflow for this new generation.
      this.currentActiveWorkflowId = null;

      // Show progress bar immediately when generate button is clicked
      this.showProgressBar = true;
      this.progressTitle = 'Generating Wheel...';
      this.progressModalMode = this.currentUser?.isStaff ? 'debug' : 'user-friendly';

      // Generate a unique tracker ID for this session
      const trackerId = `wheel_gen_${Date.now()}`;
      this.currentProgressTrackerId = trackerId;

      // Initialize progress bar with 0% progress
      const progressBar = this.shadowRoot?.querySelector('real-time-progress-bar');
      if (progressBar) {
        progressBar.dispatchEvent(new CustomEvent('progress-update', {
          detail: {
            data: {
              tracker_id: trackerId,
              progress_percent: 0,
              stage: 'initiating',
              stage_id: 'init',
              stage_name: 'Initializing wheel generation...',
              message: 'Starting wheel generation process',
              workflow_type: 'wheel_generation'
            }
          }
        }));
      }

      // Force a re-render to ensure progress modal is displayed
      this.requestUpdate();

      // Start a fallback progress simulation in case backend doesn't send real-time updates
      this.startProgressFallback(trackerId);

      // Start generation timeout (120 seconds)
      this.startGenerationTimeout();

      // SECURITY FIX: Use authenticated user's ID, not hardcoded fallback
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.id && !this.debugUserId) {
        console.error('❌ No authenticated user found - cannot generate wheel');
        this.error = 'User not authenticated. Please log in to generate a wheel.';
        this.isLoading = false;
        return;
      }
      const userId = this.currentUserProfile?.id || ((this.configService.isDebugMode() && this.debugUserId) ? this.debugUserId : currentUser!.id);

      // timeAvailable now directly represents minutes
      const timeInMinutes = this.timeAvailable;

      // 🆕 Enhanced energy system support
      let message: string;
      let metadata: any;

      // Always use Enhanced 5-slider system (Updated 2025-07-24)
      message = `Generate a personalized activity wheel (Time: ${timeInMinutes}min, Physical: ${this.physicalEnergy}%, Mental: ${this.mentalEnergy}%, Social: ${this.socialEnergy}%, Emotional: ${this.emotionalEnergy}%, Direction: ${this.energyDirection}%)`;

      metadata = {
        forced_wheel_generation: true,
        time_available: timeInMinutes,
        physical_energy: this.physicalEnergy,
        mental_energy: this.mentalEnergy,
        social_energy: this.socialEnergy,
        emotional_energy: this.emotionalEnergy,
        energy_direction: this.energyDirection,
        energy_profile_type: 'enhanced'
      };

      // Use the existing sendChatMessage method but with wheel generation workflow and forced flag
      await this.messageHandler.sendChatMessage(
        message,
        userId,
        'wheel_generation', // workflowType
        this.configService.isDebugMode() ? this.debugLLMConfigId : undefined, // llmConfigId
        metadata
      );

      // TODO: Modify MessageHandler to support energy_level and time_available parameters
      // For now, the energy level and time available are included in the message text

    } catch (error) {
      console.error('❌ Failed to generate wheel:', error);
      this.error = 'Failed to generate wheel. Please try again.';
      this.isLoading = false;
    }
  };

  /**
   * Handle spin wheel button click - now shows contract modal first
   */
  private handleSpinWheel = () => {
    if (!this.wheelData || this.isLoading) {
      console.log('[APP] Cannot spin: wheelData missing or loading in progress');
      return;
    }

    console.log('[APP] Spin wheel button clicked - showing contract modal...');

    // Track contract display event
    this.trackEvent('contract_displayed', 'Wheel', 'current-wheel', {
      wheel_items_count: this.wheelData.segments?.length || 0,
      user_energy_level: this.energyLevel,
      time_available: this.timeAvailable
    });

    this.showContractModal = true;
  };

  /**
   * Handle contract acceptance - proceed with actual wheel spin
   */
  private handleContractAccepted = (event: CustomEvent) => {
    console.log('[APP] Contract accepted, proceeding with wheel spin...', event.detail);

    // Track contract signing event
    const signatureData = event.detail.signature;
    this.trackEvent('contract_signed', 'Wheel', 'current-wheel', {
      signature_data: signatureData,
      acceptance_timestamp: new Date().toISOString(),
      wheel_items_count: this.wheelData?.segments?.length || 0
    });

    this.showContractModal = false;

    // Store signature data for later use in winning modal
    if (signatureData) {
      console.log('📝 Contract signature captured for winning modal');
    }

    // Now proceed with the actual wheel spin
    this.performWheelSpin();
  };

  /**
   * Handle contract cancellation
   */
  private handleContractCancelled = () => {
    console.log('[APP] Contract cancelled by user');
    this.showContractModal = false;
  };

  /**
   * Perform the actual wheel spin (extracted from original handleSpinWheel)
   */
  private performWheelSpin() {
    console.log('[APP] Performing wheel spin...');

    // Find the wheel component (now there's only one)
    const wheelComponent = this.shadowRoot?.querySelector('game-wheel') as any;
    if (wheelComponent && typeof wheelComponent.spin === 'function') {
      console.log('[APP] Found interactive wheel component, checking state...');

      // Get wheel state for debugging
      const wheelState = wheelComponent.getWheelState();
      console.log('[APP] Wheel state:', wheelState);

      // Check if wheel is properly initialized
      if (!wheelState.hasPhysicsEngine || !wheelState.hasRenderer) {
        console.log('[APP] Wheel not fully initialized, waiting and retrying...');
        setTimeout(() => {
          console.log('[APP] Retrying spin after initialization delay...');
          this.performWheelSpin();
        }, 500);
        return;
      }

      // Check if wheel is already spinning
      if (wheelState.isSpinning) {
        console.log('[APP] Wheel is already spinning, ignoring spin request');
        return;
      }

      console.log('[APP] Triggering wheel spin...');

      // Track wheel spin initiation
      this.trackWheelSpin('current-wheel');

      wheelComponent.spin();
    } else {
      console.error('[APP] Interactive wheel component not found or spin method not available');
      console.log('[APP] Available wheel components:', this.shadowRoot?.querySelectorAll('game-wheel'));

      // Try to find any game-wheel component as fallback
      const anyWheelComponent = this.shadowRoot?.querySelector('game-wheel') as any;
      if (anyWheelComponent) {
        console.log('[APP] Found fallback wheel component, attempting spin...');
        anyWheelComponent.spin();
      }
    }
  }

  private openActivityDetailsModal = (activity: any) => {
    this.selectedActivity = activity;
    this.showActivityDetailsModal = true;
  };

  private closeActivityDetailsModal = () => {
    this.showActivityDetailsModal = false;
    this.selectedActivity = null;
  };

  private openActivityModal = (activityId: string) => {
    this.selectedActivityId = activityId;
    this.showActivityModal = true;
    // Force reload of activity catalog to ensure we have the latest data
    this.invalidateActivityCache();
    this.loadActivityCatalog();
  };

  private closeActivityModal = () => {
    this.showActivityModal = false;
    this.selectedActivityId = null;
    this.searchQuery = '';
  };

  private handleSearchChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.searchQuery = target.value;

    // Debounce search to avoid too many API calls
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 300);
  };

  private searchTimeout: ReturnType<typeof setTimeout> | undefined;

  private async performSearch() {
    if (!this.searchQuery.trim()) {
      // If search is empty, clear catalog to show empty state
      this.activityCatalog = [];
      this.requestUpdate();
      return;
    }

    try {
      console.log(`🔍 Searching activities for: "${this.searchQuery}"`);
      this.isLoading = true;

      // Build search URL with user context parameters for smart filtering
      const searchParams = new URLSearchParams({
        search: this.searchQuery,
        limit: '50',
        energy_level: this.energyLevel.toString(),
        time_available: this.timeAvailable.toString()
      });

      const response = await fetch(`${this.getBackendBaseUrl()}/api/activities/catalog/?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.activities) {
          // Apply colors from the color service to activities
          this.activityCatalog = await this.enhanceActivitiesWithColors(data.activities);
          console.log(`✅ Search found ${data.total_count} activities with smart filtering`);

          // Track search event
          this.trackActivitySearch(this.searchQuery, data.total_count || data.activities.length);
        } else {
          console.warn('⚠️ Invalid search response');
        }
      } else {
        console.warn('⚠️ Search request failed');
      }
    } catch (error) {
      console.error('❌ Error performing search:', error);
    } finally {
      this.isLoading = false;
      this.requestUpdate();
    }
  }

  /**
   * Enhance activities with colors from the domain color service
   */
  private async enhanceActivitiesWithColors(activities: any[]): Promise<any[]> {
    try {
      const enhancedActivities = activities.map((activity) => {
        // Get domain color directly from the domain color service
        const color = getDomainColor(activity.domain);

        return {
          ...activity,
          color: color
        };
      });

      return enhancedActivities;
    } catch (error) {
      console.warn('Failed to enhance activities with colors:', error);
      return activities; // Return original activities if color enhancement fails
    }
  }

  /**
   * Set search query and trigger search
   */
  private setSearchQuery = (query: string) => {
    this.searchQuery = query;
    this.performSearch();
  };

  /**
   * Enhance wheel data with consistent colors from the domain color service
   */
  private async enhanceWheelDataWithColors(wheelData: WheelData): Promise<WheelData> {
    try {
      // Prepare activities for the new color generation system
      const activities = (wheelData.segments || []).map((segment, index) => {

        let domain = (segment as any).domain;

        // Fallback: detect domain from activity name if domain is missing
        if (!domain || domain === 'general') {
          const activityName = (segment.text || segment.name || '').toLowerCase();
          if (activityName.includes('climb') || activityName.includes('walk') || activityName.includes('push') ||
              activityName.includes('stretch') || activityName.includes('balance') || activityName.includes('dance') ||
              activityName.includes('exercise') || activityName.includes('workout') || activityName.includes('physical')) {
            domain = 'physical';
          } else if (activityName.includes('read') || activityName.includes('study') || activityName.includes('learn')) {
            domain = 'intellectual';
          } else if (activityName.includes('paint') || activityName.includes('draw') || activityName.includes('create')) {
            domain = 'creative';
          } else if (activityName.includes('meditat') || activityName.includes('reflect') || activityName.includes('mindful')) {
            domain = 'reflective';
          } else {
            domain = 'general';
          }
        }

        const activityId = (segment as any).id || `segment_${index}`;

        return {
          id: activityId, // CRITICAL FIX: Use segment.id directly, not activity_tailored_id
          name: segment.text || segment.name || `Activity ${index + 1}`,
          domain: domain
        };
      });

      // CRITICAL FIX: Always enable dual color mode for proper inner/outer wheel distinction
      const useDualColorMode = true; // Always use dual color mode for inner/outer wheel

      // CRITICAL FIX: Use generateWheelColors properly for distinct outer wheel colors

      const { generateWheelColors } = await import('../services/domainColorService.js');

      // Generate colors with dual color mode for proper inner/outer wheel distinction
      const wheelColors = generateWheelColors(activities, {
        dualColorMode: true // Enable dual color mode for inner/outer wheel
      });

      const enhancedSegments = (wheelData.segments || []).map((segment, index) => {
        // Extract activity ID from segment ID (format: "item_245_1_86-seg-0" -> "item_245_1_86")
        const activityId = segment.id.includes('-seg-') ? segment.id.split('-seg-')[0] : segment.id;

        // Find the correct color info by matching extracted activity ID
        const colorInfo = wheelColors.find((color: any) => color.activityId === activityId);

        // Fallback if colorInfo not found (should not happen with proper ID extraction)
        if (!colorInfo) {
          console.error(`Color info not found for segment ${segment.id} (activity: ${activityId}), using fallback`);
          const fallbackDomain = (segment as any).domain || 'general';
          const fallbackColor = getDomainColor(fallbackDomain);
          return {
            ...segment,
            color: fallbackColor,
            domain: fallbackDomain,
            centerColor: fallbackColor,
            extremityColor: fallbackColor
          };
        }

        if (useDualColorMode && colorInfo) {
          // Dual color mode - include both center and extremity colors
          const fallbackDomain = (segment as any).domain || 'general';
          const enhancedSegment = {
            ...segment,
            color: colorInfo.extremityColor || getDomainColor(fallbackDomain), // Use extremity color for backward compatibility
            centerColor: colorInfo.centerColor || getDomainColor(fallbackDomain),
            extremityColor: colorInfo.extremityColor || getDomainColor(fallbackDomain),
            listColor: colorInfo.listColor || getDomainColor(fallbackDomain),
            backgroundColor: colorInfo.backgroundColor || getDomainColorWithOpacity(fallbackDomain, 0.1),
            // CRITICAL FIX: Preserve domain information for inner wheel
            domain: colorInfo.domain || fallbackDomain,

            // CRITICAL FIX: Preserve percentage information for proper wheel distribution
            percentage: colorInfo.percentage || segment.percentage,
            segmentSize: colorInfo.segmentSize || (segment as any).segmentSize
          };
          return enhancedSegment;
        } else {
          // Single color mode - CRITICAL: Always use the generated color, never fall back to domain color
          // If colorInfo is missing, generate a distinct color for this activity
          let finalColor = colorInfo?.color;
          let domain = colorInfo?.domain;
          if (!finalColor) {
            // Fallback: use domain color for missing color info
            domain = (segment as any).domain || 'general';
            finalColor = getDomainColor(domain);
          }

          const enhancedSegment = {
            ...segment,
            color: finalColor,
            // CRITICAL FIX: Preserve domain information for inner wheel
            domain: domain,
            // CRITICAL FIX: Preserve percentage information for proper wheel distribution
            percentage: colorInfo?.percentage || segment.percentage
          };
          return enhancedSegment;
        }
      });

      return {
        ...wheelData,
        segments: enhancedSegments
      };
    } catch (error) {
      console.error('Failed to enhance wheel data with colors:', error);
      return wheelData; // Return original wheel data if color enhancement fails
    }
  }

  private selectCatalogActivity = async (catalogActivity: any) => {
    if (!this.selectedActivityId || !this.wheelData) {
      console.error('No activity selected or no wheel data available');
      return;
    }

    console.log('Changing activity from', this.selectedActivityId, 'to:', catalogActivity);

    // If it's a generic activity, auto-tailor it first
    let finalActivity = catalogActivity;
    if (catalogActivity.type === 'generic') {
      try {
        // Show loading state
        this.isLoading = true;
        this.requestUpdate();

        console.log('🔄 Auto-tailoring generic activity:', catalogActivity.name);

        // Call the tailor API
        const config = this.configService.getConfig();
        const baseUrl = config.websocket.url.replace('ws://', 'http://').replace('wss://', 'https://').replace('/ws/game/', '');

        const response = await fetch(`${baseUrl}/api/activities/tailor/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for session-based auth
          body: JSON.stringify({
            generic_activity_id: catalogActivity.id
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.activity) {
            finalActivity = data.activity;
            console.log('✅ Activity auto-tailored successfully:', finalActivity.name);

            // Add to catalog cache for future use
            this.activityCatalog = [...this.activityCatalog, finalActivity];
            this.invalidateActivityCache(); // Refresh cache
          } else {
            console.warn('⚠️ Failed to tailor activity, using generic version');
          }
        } else {
          try {
            const err = await response.json().catch(() => ({} as any));
            if (response.status === 400 && (err?.error?.includes('No current environment') || err?.error?.includes('current environment'))) {
              this.showErrorMessage('No current environment set. Please select an environment to tailor this activity.');
              await this.openEnvironmentSwitcherModal();
            } else {
              console.warn('⚠️ Tailor API failed, using generic version', response.status, err?.error || '');
            }
          } catch (e) {
            console.warn('⚠️ Tailor API failed and error body parsing failed');
          }
        }
      } catch (error) {
        console.error('❌ Error auto-tailoring activity:', error);
        console.log('Using generic version as fallback');
      } finally {
        this.isLoading = false;
        this.requestUpdate();
      }
    }

    // Find the activity to replace in the wheel data
    const activityIndex = this.wheelData?.segments?.findIndex(segment =>
      segment.id === this.selectedActivityId ||
      segment.activity_tailored_id === this.selectedActivityId ||
      segment.wheel_item_id === this.selectedActivityId
    );

    if (activityIndex === -1 || activityIndex === undefined) {
      console.error('Activity not found in wheel data:', this.selectedActivityId);
      return;
    }

    // Create new wheel data with the replaced activity
    const updatedSegments = [...(this.wheelData?.segments || [])];
    const originalSegment = updatedSegments[activityIndex];

    // Replace the activity while keeping the same percentage and color
    updatedSegments[activityIndex] = {
      ...originalSegment,
      id: finalActivity.id,
      name: finalActivity.name,
      text: finalActivity.name,
      description: finalActivity.description,
      domain: finalActivity.domain,
      base_challenge_rating: finalActivity.base_challenge_rating,
      activity_tailored_id: finalActivity.id,
      wheel_item_id: finalActivity.id
    };

    // Update the wheel data via State Machine
    // Update the business wheel data properly
    const updatedWheelData: WheelData = {
      ...this.wheelData!,
      items: this.wheelData!.items?.map((item, index) =>
        index === activityIndex ? { ...item, ...updatedSegments[index] } : item
      ) || []
    };
    this.wheelData = updatedWheelData;
    this.wheelStateMachine.setWheelData(this.convertToStateMachineFormat(updatedWheelData));

    console.log('✅ Activity replaced successfully');

    this.closeActivityModal();

    // TODO: Optionally send the change to the backend to persist it
    // this.sendActivityChangeToBackend(this.selectedActivityId, finalActivity.id);
  };

  // New activity creation modal methods
  private openCreateActivityModal = () => {
    this.showCreateActivityModal = true;
    // Reset form
    this.newActivityForm = {
      name: '',
      description: '',
      domain: 'general',
      base_challenge_rating: 50
    };
  };

  private closeCreateActivityModal = () => {
    this.showCreateActivityModal = false;
  };

  // Custom activity form methods
  private toggleCustomActivityForm = () => {
    this.showCustomActivityForm = !this.showCustomActivityForm;
    if (this.showCustomActivityForm) {
      // Reset form when opening
      this.customActivityForm = {
        title: '',
        description: '',
        duration_from: 15,
        duration_to: 30,
        challengingness: 50,
        comfort_level: 50
      };
    }
  };

  private handleCustomActivityFormChange = (field: string, value: any) => {
    this.customActivityForm = {
      ...this.customActivityForm,
      [field]: value
    };
  };

  private createCustomActivity = async () => {
    try {
      const response = await fetch('/api/custom-activity/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCsrfToken()
        },
        body: JSON.stringify({
          title: this.customActivityForm.title,
          description: this.customActivityForm.description,
          duration_range: `${this.customActivityForm.duration_from}-${this.customActivityForm.duration_to} minutes`,
          challengingness: this.customActivityForm.challengingness,
          comfort_level: this.customActivityForm.comfort_level
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Custom activity created successfully:', result);

        // Now add the custom activity to the wheel
        const addResponse = await fetch('/api/wheel-items/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken()
          },
          body: JSON.stringify({
            activity_id: result.custom_activity.id,
            activity_type: 'custom'
          })
        });

        if (addResponse.ok) {
          const addResult = await addResponse.json();
          console.log('✅ Custom activity added to wheel:', addResult);

          // Update wheel data via State Machine and direct wheelData
          if (addResult.wheel_data) {
            // Update state machine
            this.wheelStateMachine.setWheelData(addResult.wheel_data);

            // Also update this.wheelData directly to ensure UI consistency
            this.wheelData = this.convertFromStateMachineFormat(addResult.wheel_data);
            console.log('🔄 Updated wheelData after custom activity creation:', this.wheelData);

            // Force UI update
            this.requestUpdate();
          }

          // Close the form and modal
          this.showCustomActivityForm = false;
          this.closeAddActivityModal();

          // Show success message
          this.showSuccessMessage('Custom activity created and added to your wheel!');
        } else {
          const addErrorData = await addResponse.json();
          console.error('Failed to add custom activity to wheel:', addErrorData.error);
          this.showErrorMessage('Failed to add custom activity to wheel: ' + addErrorData.error);
        }
      } else {
        const errorData = await response.json().catch(() => ({} as any));
        if (response.status === 400 && (errorData?.error?.includes('No current environment') || errorData?.error?.includes('current environment'))) {
          this.showErrorMessage('No current environment set. Please select an environment to create a custom activity.');
          await this.openEnvironmentSwitcherModal();
        } else {
          console.error('Failed to create custom activity:', errorData.error);
          this.showErrorMessage('Failed to create custom activity: ' + (errorData.error || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Error creating custom activity:', error);
      this.showErrorMessage('Error creating custom activity. Please try again.');
    }
  };

  private showSuccessMessage = (message: string) => {
    // Simple success message implementation - could be enhanced with a toast system
    console.log('✅ Success:', message);
    // TODO: Implement proper toast notification system
  };


  // Removed duplicate showErrorMessage method - using the enhanced version below

  private getCsrfToken = (): string => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    return csrfToken || '';
  };

  /**
   * Get debug user ID for staff impersonation
   */
  private getDebugUserId(): string | null {
    return this.configService.isDebugMode() && this.debugUserId ? this.debugUserId : null;
  };

  // HistoryEvent tracking methods
  private trackEvent = async (eventType: string, contentType: string, objectId: string, details: any = {}, secondaryContentType?: string, secondaryObjectId?: string) => {
    try {
      // PRODUCTION AUTHENTICATION FIX: Check authentication before tracking
      const config = this.configService.getConfig();
      if (config.security.requireAuthentication && !this.isAuthenticated) {
        console.warn('🔒 Skipping event tracking - user not authenticated:', eventType);
        return;
      }

      // Prepare headers with debug user ID for staff impersonation
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.getCsrfToken()
      };

      // Add debug user ID header if available (for staff impersonation)
      const debugUserId = this.getDebugUserId();
      if (debugUserId) {
        headers['X-Debug-User-ID'] = debugUserId;
      }

      const response = await fetch(`${this.getBackendBaseUrl()}/api/track-event/`, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for session-based auth
        body: JSON.stringify({
          event_type: eventType,
          content_type: contentType,
          object_id: objectId,
          secondary_content_type: secondaryContentType,
          secondary_object_id: secondaryObjectId,
          details: {
            ...details,
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            session_id: this.getSessionId()
          }
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('🔒 Event tracking failed - authentication required:', eventType);
          // If we get 401, user might need to re-authenticate
          if (config.security.requireAuthentication) {
            this.isAuthenticated = false;
            this.requestUpdate(); // Trigger re-render to show login form
          }
        } else {
          console.warn('Failed to track event:', eventType, 'Status:', response.status);
        }
      }
    } catch (error) {
      console.warn('Error tracking event:', eventType, error);
    }
  };

  private getSessionId = (): string => {
    let sessionId = sessionStorage.getItem('goali_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
      sessionStorage.setItem('goali_session_id', sessionId);
    }
    return sessionId;
  };

  // Specific event tracking methods
  private trackAppLaunch = async () => {
    if (this.currentUser) {
      await this.trackEvent('app_launched', 'UserProfile', this.currentUser.id, {
        device_info: {
          user_agent: navigator.userAgent,
          platform: (navigator as any).userAgentData?.platform || 'unknown',
          language: navigator.language,
          screen_resolution: `${screen.width}x${screen.height}`,
          viewport_size: `${window.innerWidth}x${window.innerHeight}`
        },
        referrer: document.referrer,
        url: window.location.href
      });
    }
  };

  private trackWheelSpin = async (wheelId: string) => {
    if (this.currentUser) {
      await this.trackEvent('wheel_spin_initiated', 'Wheel', wheelId, {
        user_energy_level: this.energyLevel,
        time_available: this.timeAvailable,
        wheel_items_count: this.wheelData?.segments?.length || 0
      });
    }
  };

  private trackWheelSpinCompleted = async (wheelId: string, winningItemId: string, spinDuration: number) => {
    if (this.currentUser) {
      await this.trackEvent('wheel_spin_completed', 'WheelItem', winningItemId, {
        spin_duration_ms: spinDuration,
        user_energy_level: this.energyLevel,
        time_available: this.timeAvailable
      }, 'Wheel', wheelId);
    }
  };

  private trackModalOpened = async (modalType: string, triggerAction: string, context: any = {}) => {
    if (this.currentUser) {
      await this.trackEvent('modal_opened', 'UserProfile', this.currentUser.id, {
        modal_type: modalType,
        trigger_action: triggerAction,
        context: context
      });
    }
  };

  private trackActivitySearch = async (searchQuery: string, resultsCount: number, selectedResult?: any) => {
    if (this.currentUser) {
      await this.trackEvent('activity_search_performed', 'UserProfile', this.currentUser.id, {
        search_query: searchQuery,
        results_count: resultsCount,
        filters_applied: {
          energy_level: this.energyLevel,
          time_available: this.timeAvailable
        },
        selected_result: selectedResult
      });
    }
  };

  // Duplicate methods removed - keeping only the first occurrence

  private closeProfileModal = () => {
    this.showProfileModal = false;
    // Reset editing state when closing modal
    this.editingProfileFields.clear();
  };

  private closeWinningModal = () => {
    // Store the winning activity before closing the modal
    const activityToEnjoy = this.winningActivity;

    this.showWinningModal = false;
    this.winningActivity = null;

    // Clean up stored signature after wheel completion
    ContractDisclaimerModal.clearStoredSignature();

    // Show enjoy overlay with the activity details
    if (activityToEnjoy) {
      this.showEnjoyOverlayWithActivity(activityToEnjoy);
    }
  };

  private handleDeclineActivity = () => {
    // Store the activity for the cancellation modal and show cancellation view
    this.postSpinActivity = this.winningActivity;
    this.postSpinView = 'cancellation';

    // Close winning modal
    this.showWinningModal = false;
  };

  // Post-spin event handlers
  private handlePostSpinSkipTimer = () => {
    if (!this.configService.isDebugMode()) {
      console.warn('Skip timer only available in DEBUG mode');
      return;
    }
    console.log('🐛 DEBUG: Skipping timer - transitioning to feedback modal');
    this.transitionToFeedbackModal();
  };

  private handlePostSpinResetApp = () => {
    this.resetAppFromPostSpin();
  };

  private handlePostSpinCloseFeedback = () => {
    this.postSpinService.clearStoredSpinData();
    this.resetAppFromPostSpin();
  };

  private handlePostSpinSubmitFeedback = async (event: CustomEvent) => {
    const { experienceRating, comment, activity } = event.detail;

    const success = await this.postSpinService.submitPostActivityFeedback(
      activity,
      experienceRating,
      comment,
      this.getBackendBaseUrl()
    );

    if (success) {
      this.resetAppFromPostSpin();
    } else {
      this.error = 'Failed to submit feedback. Please try again.';
    }
  };

  private handlePostSpinCloseCancellation = () => {
    // Go back to the winning modal
    this.postSpinView = null;
    this.postSpinActivity = null;
    this.showWinningModal = true;
  };

  private handlePostSpinCancelCancellation = () => {
    // User changed their mind - go back to the winning modal
    this.postSpinView = null;
    this.showWinningModal = true;
  };

  private handlePostSpinConfirmCancellation = async (event: CustomEvent) => {
    const { reason, activity } = event.detail;
    const currentUser = authService.getCurrentUser();

    const success = await this.postSpinService.submitCancellationFeedback(
      activity,
      reason,
      this.getBackendBaseUrl(),
      currentUser?.id
    );

    if (success) {
      // Clean up and reset app
      this.postSpinView = null;
      this.postSpinActivity = null;
      this.winningActivity = null;
      ContractDisclaimerModal.clearStoredSignature();
      this.resetAppFromPostSpin();
    } else {
      this.error = 'Failed to submit cancellation feedback. Please try again.';
    }
  };

  private resetAppFromPostSpin(): void {
    // Reset all app state
    this.wheelStateMachine.clearWheel();
    this.messages = [];
    this.error = null;
    this.isLoading = false;
    this.showActivityModal = false;
    this.showCreateActivityModal = false;
    this.showProfileModal = false;
    this.showWinningModal = false;
    this.showFeedbackModal = false;
    this.showAddActivityModal = false;
    this.showContractModal = false;
    this.postSpinView = null;
    this.postSpinActivity = null;
    this.postSpinStartTime = null;
    this.winningActivity = null;

    // Force re-render
    this.requestUpdate();

    console.log('🔄 App reset from post-spin view');
  }



  // Profile modal accordion methods
  private toggleProfileSection = (sectionId: string) => {
    const newExpanded = new Set(this.expandedProfileSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    this.expandedProfileSections = newExpanded;
  };

  private startEditingField = (fieldId: string) => {
    const newEditing = new Set(this.editingProfileFields);
    newEditing.add(fieldId);
    this.editingProfileFields = newEditing;
  };

  private stopEditingField = (fieldId: string) => {
    const newEditing = new Set(this.editingProfileFields);
    newEditing.delete(fieldId);
    this.editingProfileFields = newEditing;
  };

  // Wheel item management methods
  private handleRemoveActivity = (e: Event, item: any) => {
    e.stopPropagation(); // Prevent accordion toggle

    // Track activity removal event
    this.trackEvent('activity_removed_from_wheel', 'WheelItem', item.id || item.activityId, {
      activity_name: item.name,
      activity_domain: item.domain,
      wheel_state_before: this.wheelData?.segments?.length || 0,
      removal_reason: 'user_action'
    });

    // Validate that we have a proper wheel item ID
    const wheelItemId = item.id; // Should be the wheel item ID from backend

    if (!wheelItemId) {
      console.error('No wheel item ID found for removal');
      return;
    }

    if (wheelItemId.startsWith('llm_tailored_') || wheelItemId.startsWith('generic-')) {
      console.error('Received activity tailored ID instead of wheel item ID — data mapping issue. Received:', wheelItemId);
      return;
    }

    // Show feedback modal
    this.feedbackModalConfig = {
      title: "Don't like this one ?",
      message: "Please tell us more about the reason why you don't want this in your wheel",
      feedback_type: "wheel_item_refusal",
      content_type: "wheelitem",
      object_id: wheelItemId,
      primaryButtonLabel: "Remove Activity",
      secondaryButtonLabel: "Back"
    };
    this.showFeedbackModal = true;
  };

  private openAddActivityModal = () => {
    this.showAddActivityModal = true;
    // Clear search query and activity catalog - show empty state by default
    this.searchQuery = '';
    this.activityCatalog = []; // Start with empty catalog

    // Track modal opening
    this.trackModalOpened('add_activity', 'add_button_click');
  };

  private closeAddActivityModal = () => {
    this.showAddActivityModal = false;
    this.searchQuery = '';
  };

  private closeFeedbackModal = () => {
    this.showFeedbackModal = false;
  };

  private submitFeedback = async () => {
    try {
      const response = await fetch(`${this.getBackendBaseUrl()}/api/feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
        body: JSON.stringify({
          feedback_type: this.feedbackModalConfig.feedback_type,
          content_type: this.feedbackModalConfig.content_type,
          object_id: this.feedbackModalConfig.object_id,
          user_comment: (this.renderRoot.querySelector('#feedback-comment') as HTMLTextAreaElement)?.value || '',
          criticality: 1,
          context_data: {}
        })
      });

      if (response.ok) {
        // Remove the wheel item after feedback is submitted
        await this.removeWheelItem(this.feedbackModalConfig.object_id);
        this.closeFeedbackModal();
      } else {
        console.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  private removeWheelItem = async (wheelItemId: string) => {
    try {
      // CRITICAL FIX: Include current wheel ID to ensure backend operates on the same wheel
      const currentWheelId = this.wheelData?.wheelId;

      console.log('🗑️ ===== REMOVING WHEEL ITEM =====');
      console.log('🗑️ Removing item:', wheelItemId);
      console.log('🗑️ From wheel:', currentWheelId);
      console.log('🗑️ Current items count:', this.wheelData?.segments?.length || 0);
      if (!currentWheelId) {
        console.error('🚨 CRITICAL ERROR: No current wheel ID available for removal operation');
        this.handleWheelRemovalError('No current wheel ID available');
        return;
      }
      console.log('🔍 Current wheel ID from frontend:', currentWheelId);

      // Prepare headers with debug user ID for staff impersonation
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add debug user ID header if available (for staff impersonation)
      const debugUserId = this.getDebugUserId();
      if (debugUserId) {
        headers['X-Debug-User-ID'] = debugUserId;
        console.log('🔐 Adding debug user ID header:', debugUserId);
      }

      // CRITICAL FIX: Send current wheel ID in request body to ensure backend uses correct wheel
      const requestBody = {
        current_wheel_id: currentWheelId
      };

      const response = await fetch(`${this.getBackendBaseUrl()}/api/wheel-items/${wheelItemId}/`, {
        method: 'DELETE',
        headers,
        credentials: 'include', // Include cookies for session-based auth
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🗑️ Removal successful, processing response...');

        // ROBUST ERROR HANDLING: Check all possible response formats
        if (!data) {
          console.error('🚨 CRITICAL ERROR: API response is null or undefined');
          this.handleWheelRemovalError('API returned null response');
          return;
        }

        if (!data.success) {
          console.error('🚨 CRITICAL ERROR: API response indicates failure:', data);
          this.handleWheelRemovalError(data.error || 'API response indicates failure');
          return;
        }

        if (!data.wheel_data) {
          console.error('🚨 CRITICAL ERROR: No wheel_data in API response:', data);
          this.handleWheelRemovalError('No wheel_data in API response');
          return;
        }

        if (!data.wheel_data.segments || !Array.isArray(data.wheel_data.segments)) {
          console.error('🚨 CRITICAL ERROR: Invalid segments in wheel_data:', data.wheel_data);
          this.handleWheelRemovalError('Invalid segments in wheel_data');
          return;
        }

        console.log('✅ API response validation passed');

        // CRITICAL FIX: Validate that backend returned the expected wheel ID
        const returnedWheelId = data.wheel_data.id || data.wheel_data.wheelId || data.wheel_data.wheel_id || data.wheel_id;
        if (currentWheelId && returnedWheelId && returnedWheelId.toString() !== currentWheelId.toString()) {
          console.error('🚨 CRITICAL ERROR: Backend returned different wheel than expected!');
          console.error(`   Expected wheel ID: ${currentWheelId}`);
          console.error(`   Returned wheel ID: ${returnedWheelId}`);
          this.handleWheelRemovalError(`Backend returned wrong wheel (expected ${currentWheelId}, got ${returnedWheelId})`);
          return;
        }

        // CRITICAL FIX: Use the same data processing logic as WebSocket wheel data
        // to ensure consistent format and color handling
        const rawWheelData = {
          segments: data.wheel_data.segments.map((segment: any) => ({
            id: segment.id,
            text: segment.name,
            name: segment.name,
            description: segment.description,
            percentage: segment.percentage,
            color: segment.color,
            activity_tailored_id: segment.activity_tailored_id,
            domain: segment.domain || 'general',
            base_challenge_rating: segment.base_challenge_rating || 50,
            wheel_item_id: segment.id
          })),
          // CRITICAL FIX: Include wheelId and createdAt like WebSocket processing
          wheelId: returnedWheelId || `wheel-${Date.now()}`,
          createdAt: new Date().toISOString()
        };

        console.log('🗑️ Updated wheel:', returnedWheelId, 'with', rawWheelData.segments.length, 'items');

        // VALIDATION: Ensure we have valid wheel data
        if (rawWheelData.segments.length === 0) {
          console.warn('⚠️ WARNING: Wheel has no segments after removal - this might be expected if it was the last item');
        }

        // Check if backend already provided colors (enhanced logic to detect fallback colors)
        const backendHasColors = rawWheelData.segments.every((segment: { color?: string }) => {
          // Must have a color that's a valid hex code
          return segment.color && /^#[0-9A-Fa-f]{6}$/.test(segment.color);
        }) && rawWheelData.segments.length > 0 && (
          rawWheelData.segments.length === 1 ||
          // For multiple segments, ensure they're not all the same color AND not all the fallback grey color
          (new Set(rawWheelData.segments.map((s: any) => s.color)).size > 1 &&
           !rawWheelData.segments.every((s: any) => s.color === '#95A5A6'))
        );

        let finalWheelData;
        try {
          if (backendHasColors) {
            console.log('🎨 Backend provided colors after removal, using them directly');
            finalWheelData = rawWheelData;
          } else {
            console.log('🎨 Backend colors missing after removal, enhancing with frontend service');
            // Enhance wheel data with consistent colors (same as WebSocket handler)

            finalWheelData = await this.enhanceWheelDataWithColors(rawWheelData as any);

          }

          console.log('🗑️ Final wheel data prepared:', finalWheelData);

          // CRITICAL FIX: Validate final wheel data before updating state machine
          if (!finalWheelData || !finalWheelData.segments || !Array.isArray(finalWheelData.segments)) {
            console.error('🚨 CRITICAL ERROR: Invalid final wheel data:', finalWheelData);
            this.handleWheelRemovalError('Invalid final wheel data after processing');
            return;
          }

          // Update via State Machine with error handling
          try {
            this.wheelStateMachine.setWheelData(finalWheelData as any);
          } catch (stateError: any) {
            console.error('🚨 CRITICAL ERROR: Failed to update wheel state machine:', stateError);
            this.handleWheelRemovalError(`State machine update failed: ${stateError?.message || 'Unknown error'}`);
            return;
          }

        } catch (colorError: any) {
          console.error('🚨 CRITICAL ERROR: Failed to process wheel colors:', colorError);
          this.handleWheelRemovalError(`Color processing failed: ${colorError?.message || 'Unknown error'}`);
          return;
        }

        console.log('✅ Wheel item removed successfully');
      } else {
        // Handle different types of errors with specific user messages
        try {
          const errorData = JSON.parse(await response.text());

          if (errorData.error_type === 'data_inconsistency') {
            console.error('🚨 FRONTEND/BACKEND DATA INCONSISTENCY:', errorData.error);
            this.handleWheelRemovalError(`Data inconsistency detected. Please refresh the page to get the latest wheel data.`);

            // Log the inconsistency for debugging
            console.error('🔍 Inconsistency details:', {
              frontendWheelId: this.wheelData?.wheelId,
              backendError: errorData.error,
              suggestedAction: errorData.suggested_action
            });

          } else if (errorData.error_type === 'item_not_found') {
            console.error('🚨 WHEEL ITEM NOT FOUND:', errorData.error);
            this.handleWheelRemovalError(`The activity you're trying to remove no longer exists. Please refresh the page to get the latest wheel.`);

          } else {
            console.error('🚨 CRITICAL ERROR: API request failed:', response.status, errorData.error || errorData);
            this.handleWheelRemovalError(`API request failed: ${response.status} - ${errorData.error || 'Unknown error'}`);
          }
        } catch (parseError) {
          // Fallback for non-JSON error responses
          const errorText = await response.text();
          console.error('🚨 CRITICAL ERROR: API request failed:', response.status, errorText);
          this.handleWheelRemovalError(`API request failed: ${response.status} - ${errorText}`);
        }
      }
    } catch (error: any) {
      console.error('🚨 CRITICAL ERROR: Exception during wheel item removal:', error);
      this.handleWheelRemovalError(`Exception: ${error?.message || 'Unknown error'}`);
    }
  };

  /**
   * Handle wheel removal errors with proper user feedback and state recovery
   */
  private handleWheelRemovalError(errorMessage: string) {
    console.error('🚨 WHEEL REMOVAL ERROR:', errorMessage);

    // Show user-friendly error message
    this.showErrorMessage(`Failed to remove activity: ${errorMessage}`);

    // Log error for debugging
    this.trackEvent('wheel_item_removal_error', 'Error', 'wheel_removal', {
      error_message: errorMessage,
      wheel_state: this.wheelStateMachine.currentState,
      has_wheel_data: !!this.wheelData
    });

    // DO NOT clear the wheel - keep the current state
    // The user should still see their wheel even if removal failed
  }

  /**
   * Show error message to user
   */
  private showErrorMessage(message: string) {
    // Add error message to chat
    const errorMessage: ChatMessage = {
      id: `error-${Date.now()}`,
      type: 'system',
      content: `⚠️ ${message}`,
      timestamp: new Date()
    };
    this.messages = [...this.messages, errorMessage];

    // Also log to console for debugging
    console.error('🚨 USER ERROR MESSAGE:', message);
  }

  private addActivityToWheel = async (activity: any) => {
    try {
      // CRITICAL FIX: Include current wheel ID to ensure backend operates on the same wheel
      const currentWheelId = this.wheelData?.wheelId;
      if (!currentWheelId) {
        console.error('🚨 CRITICAL ERROR: No current wheel ID available for addition operation');
        this.showErrorMessage('No current wheel available for adding activity');
        return;
      }
      console.log('🔍 Current wheel ID from frontend for addition:', currentWheelId);

      // Show tailoring spinner for generic activities
      const isGenericActivity = activity.type === 'generic' || activity.id?.startsWith('generic-');
      if (isGenericActivity) {
        this.isTailoringActivity = true;
        this.tailoringActivityName = activity.name;
        console.log('🎨 Starting activity tailoring process for:', activity.name);
      }

      // Prepare headers with debug support
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add debug user ID header if available (for staff impersonation)
      const debugUserId = this.getDebugUserId();
      if (debugUserId) {
        headers['X-Debug-User-ID'] = debugUserId;
        console.log('🔐 Adding debug user ID header for wheel item addition:', debugUserId);
      }

      const response = await fetch(`${this.getBackendBaseUrl()}/api/wheel-items/`, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for session-based auth
        body: JSON.stringify({
          activity_id: activity.id,
          activity_type: activity.type,
          current_wheel_id: currentWheelId  // CRITICAL FIX: Send current wheel ID
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.wheel_data) {
          console.log('➕ Addition successful, processing response...');

          // CRITICAL FIX: Validate that backend returned the expected wheel ID
          const returnedWheelId = data.wheel_data.id || data.wheel_data.wheelId || data.wheel_data.wheel_id || data.wheel_id;
          if (currentWheelId && returnedWheelId && returnedWheelId.toString() !== currentWheelId.toString()) {
            console.error('🚨 CRITICAL ERROR: Backend returned different wheel than expected for addition!');
            console.error(`   Expected wheel ID: ${currentWheelId}`);
            console.error(`   Returned wheel ID: ${returnedWheelId}`);
            this.showErrorMessage(`Backend returned wrong wheel (expected ${currentWheelId}, got ${returnedWheelId})`);
            return;
          }

          // CRITICAL FIX: Use the same data processing logic as WebSocket wheel data
          // to ensure consistent format and color handling
          const rawWheelData = {
            segments: data.wheel_data.segments.map((segment: any) => ({
              id: segment.id,
              text: segment.name,
              name: segment.name,
              description: segment.description,
              percentage: segment.percentage,
              color: segment.color,
              activity_tailored_id: segment.activity_tailored_id,
              domain: segment.domain || 'general',
              base_challenge_rating: segment.base_challenge_rating || 50,
              wheel_item_id: segment.id
            })),
            // CRITICAL FIX: Include wheelId and createdAt like WebSocket processing
            wheelId: returnedWheelId || `wheel-${Date.now()}`,
            createdAt: new Date().toISOString()
          };

          console.log('➕ Raw wheel data after adding:', rawWheelData);

          // Check if backend already provided colors (enhanced logic to detect fallback colors)
          const backendHasColors = rawWheelData.segments.every((segment: { color?: string }) => {
            // Must have a color that's a valid hex code
            return segment.color && /^#[0-9A-Fa-f]{6}$/.test(segment.color);
          }) && rawWheelData.segments.length > 0 && (
            rawWheelData.segments.length === 1 ||
            // For multiple segments, ensure they're not all the same color AND not all the fallback grey color
            (new Set(rawWheelData.segments.map((s: any) => s.color)).size > 1 &&
             !rawWheelData.segments.every((s: any) => s.color === '#95A5A6'))
          );

          let finalWheelData;
          if (backendHasColors) {
            console.log('🎨 Backend provided colors after adding, using them directly');
            finalWheelData = rawWheelData;
          } else {
            console.log('🎨 Backend colors missing after adding, enhancing with frontend service');
            // Enhance wheel data with consistent colors (same as WebSocket handler)
            finalWheelData = await this.enhanceWheelDataWithColors(rawWheelData as any);
          }

          // Update via State Machine
          this.wheelStateMachine.setWheelData(finalWheelData as any);


          console.log('✅ Activity added successfully to wheel:', returnedWheelId);
          this.closeAddActivityModal();
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to add activity to wheel:', errorData.error);
        // Show error to user
        alert(errorData.error || 'Failed to add activity to wheel');
      }
    } catch (error) {
      console.error('Error adding activity to wheel:', error);
    } finally {
      // Hide tailoring spinner
      if (this.isTailoringActivity) {
        this.isTailoringActivity = false;
        this.tailoringActivityName = '';
        console.log('🎨 Activity tailoring process completed');
      }
    }
  };

  private saveProfileField = async (fieldId: string, value: string) => {
    // TODO: Implement API call to save profile field
    console.log(`Saving profile field ${fieldId}:`, value);

    // For now, just update local state
    if (this.currentUserProfile) {
      // Update the field in the current profile
      const fieldPath = fieldId.split('.');
      let target = this.currentUserProfile;
      for (let i = 0; i < fieldPath.length - 1; i++) {
        if (!target[fieldPath[i]]) target[fieldPath[i]] = {};
        target = target[fieldPath[i]];
      }
      target[fieldPath[fieldPath.length - 1]] = value;

      // Force re-render
      this.requestUpdate();
    }

    this.stopEditingField(fieldId);
  };

  private renderEditableField(fieldId: string, label: string, value: string) {
    const isEditing = this.editingProfileFields.has(fieldId);

    if (isEditing) {
      return html`
        <div class="profile-field">
          <label>${label}</label>
          <input
            type="text"
            class="profile-edit-input"
            .value=${value}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                this.saveProfileField(fieldId, (e.target as HTMLInputElement).value);
              } else if (e.key === 'Escape') {
                this.stopEditingField(fieldId);
              }
            }}
            @blur=${(e: Event) => {
              this.saveProfileField(fieldId, (e.target as HTMLInputElement).value);
            }}
            autofocus
          />
          <div class="profile-edit-actions">
            <button
              class="profile-edit-btn save"
              @click=${(e: Event) => {
                const input = (e.target as HTMLElement).parentElement?.previousElementSibling as HTMLInputElement;
                this.saveProfileField(fieldId, input.value);
              }}
            >
              Save
            </button>
            <button
              class="profile-edit-btn cancel"
              @click=${() => this.stopEditingField(fieldId)}
            >
              Cancel
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="profile-field">
        <label>${label}</label>
        <div
          class="profile-value editable"
          @click=${() => this.startEditingField(fieldId)}
          title="Click to edit"
        >
          ${value}
        </div>
      </div>
    `;
  }

  private handleNewActivityFormChange = (field: string, value: string | number) => {
    this.newActivityForm = {
      ...this.newActivityForm,
      [field]: value
    };
  };

  private createNewActivity = async () => {
    // Validate form
    if (!this.newActivityForm.name.trim()) {
      alert('Please enter an activity name');
      return;
    }

    if (!this.newActivityForm.description.trim()) {
      alert('Please enter an activity description');
      return;
    }

    try {
      console.log('Creating new activity:', this.newActivityForm);

      // Show loading state
      this.isLoading = true;
      this.requestUpdate();

      // Send to backend API to create new activity
      const config = this.configService.getConfig();
      const baseUrl = config.websocket.url.replace('ws://', 'http://').replace('wss://', 'https://').replace('/ws/game/', '');

      const response = await fetch(`${baseUrl}/api/activities/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
        body: JSON.stringify({
          name: this.newActivityForm.name,
          description: this.newActivityForm.description,
          domain: this.newActivityForm.domain,
          base_challenge_rating: this.newActivityForm.base_challenge_rating,
          duration_range: '15-30 minutes',
          instructions: this.newActivityForm.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.activity) {
          const newActivity = data.activity;

          // Add to catalog
          this.activityCatalog = [...this.activityCatalog, newActivity];

          // Invalidate cache since we added a new activity
          this.invalidateActivityCache();

          // Close create modal
          this.closeCreateActivityModal();

          // Show success message
          console.log('✅ New activity created successfully:', newActivity.name);

          // Auto-select the new activity if we're in activity selection mode
          if (this.selectedActivityId) {
            await this.selectCatalogActivity(newActivity);
          }
        } else {
          throw new Error(data.error || 'Failed to create activity');
        }
      } else {
        const errorData = await response.json().catch(() => ({} as any));
        if (response.status === 400 && (errorData?.error?.includes('No current environment') || errorData?.error?.includes('current environment'))) {
          this.showErrorMessage('No current environment set. Please select an environment to create a new activity.');
          await this.openEnvironmentSwitcherModal();
        } else {
          throw new Error(errorData.error || 'Failed to create activity');
        }
      }
    } catch (error) {
      console.error('Failed to create new activity:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.showErrorMessage(`Failed to create new activity: ${errorMessage}`);
    } finally {
      this.isLoading = false;
      this.requestUpdate();
    }
  };

  private async loadActivityCatalog() {
    const currentUserId = this.currentUser?.id || null;
    const now = Date.now();

    // Check cache validity
    if (this.activityCatalogCache.data &&
        this.activityCatalogCache.userId === currentUserId &&
        (now - this.activityCatalogCache.timestamp) < this.ACTIVITY_CACHE_TTL) {
      console.log('✅ Using cached activity catalog');
      this.activityCatalog = this.activityCatalogCache.data;
      return;
    }

    try {
      console.log('🔄 Loading fresh activity catalog from API...');
      const config = this.configService.getConfig();
      const baseUrl = config.websocket.url.replace('ws://', 'http://').replace('wss://', 'https://').replace('/ws/game/', '');

      const response = await fetch(`${baseUrl}/api/activities/catalog/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.activities) {
          this.activityCatalog = data.activities;

          // Update cache
          this.activityCatalogCache = {
            data: data.activities,
            timestamp: now,
            userId: currentUserId
          };

          console.log(`✅ Loaded ${data.total_count} activities (${data.tailored_count} tailored, ${data.generic_count} generic)`);
        } else {
          console.warn('⚠️ Invalid response from activity catalog API - keeping empty catalog');
          this.activityCatalog = [];
        }
      } else {
        console.warn('⚠️ Failed to load activity catalog from API - keeping empty catalog');
        this.activityCatalog = [];
      }
    } catch (error) {
      console.error('❌ Error loading activity catalog:', error);
      this.activityCatalog = [];
    }
  }

  /**
   * Invalidate activity catalog cache (e.g., when user changes or new activity is created)
   */
  private invalidateActivityCache() {
    this.activityCatalogCache = {
      data: null,
      timestamp: 0,
      userId: null
    };
    console.log('🗑️ Activity catalog cache invalidated');
  }

  private loadFallbackActivityCatalog() {
    // Fallback mock data with proper typing
    this.activityCatalog = [
      {
        id: 'cat-1',
        name: 'Morning Yoga',
        description: 'Start your day with gentle stretching and mindfulness',
        domain: 'physical',
        base_challenge_rating: 30,
        type: 'generic' as const,
        icon: '🎯'
      },
      {
        id: 'cat-2',
        name: 'Creative Writing',
        description: 'Express yourself through words and storytelling',
        domain: 'creative',
        base_challenge_rating: 50,
        type: 'generic' as const,
        icon: '🎯'
      },
      {
        id: 'cat-3',
        name: 'Learn a New Recipe',
        description: 'Explore culinary skills with a new dish',
        domain: 'practical',
        base_challenge_rating: 40,
        type: 'generic' as const,
        icon: '🎯'
      },
      {
        id: 'cat-4',
        name: 'Nature Photography',
        description: 'Capture the beauty of the outdoors',
        domain: 'creative',
        base_challenge_rating: 60,
        type: 'generic' as const,
        icon: '🎯'
      },
      {
        id: 'cat-5',
        name: 'Meditation Session',
        description: 'Find inner peace and clarity',
        domain: 'mental',
        base_challenge_rating: 25,
        type: 'generic' as const,
        icon: '🎯'
      }
    ];
  }

  private get filteredCatalog() {
    let catalog = this.activityCatalog;

    // If we have a search query, the API already filtered the results
    // So we don't need to filter again locally
    // Just sort to show tailored activities first
    return catalog.sort((a, b) => {
      // Tailored activities come first
      if (a.type === 'tailored' && b.type === 'generic') return -1;
      if (a.type === 'generic' && b.type === 'tailored') return 1;

      // Within same type, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  }

  private getWheelItems() {
    if (!this.wheelData?.segments) return [];

    return this.wheelData.segments.map(segment => {
      // CRITICAL FIX: Ensure we use the correct wheel item ID for removal operations
      // The segment.id should be the wheel item ID from the backend
      const wheelItemId = segment.id; // This comes from backend as wheel item ID (e.g., "item_0d7b6542")
      const activityTailoredId = segment.activity_tailored_id; // This is the activity tailored ID

      console.log(`🔍 Processing segment - Wheel Item ID: ${wheelItemId}, Activity Tailored ID: ${activityTailoredId}`);

      return {
        id: wheelItemId, // Use wheel item ID as the primary ID for removal operations
        name: segment.name || segment.text || 'Unknown Activity',
        description: segment.description || 'No description available',
        instructions: segment.instructions || '', // CRITICAL FIX: Preserve instructions field
        percentage: segment.percentage,
        color: segment.color,
        domain: segment.domain || 'general',
        base_challenge_rating: segment.base_challenge_rating || 50,
        activity_tailored_id: activityTailoredId,
        wheel_item_id: wheelItemId // Explicitly store wheel item ID for removal
      };
    });
  }

  private renderActivityList() {
    const wheelItems = this.getWheelItems();
    if (wheelItems.length === 0) return '';

    return html`
      <div class="activity-list">
        <div class="activity-list-header">
          <span>Activities (${wheelItems.length})</span>
          <button
            class="add-activity-btn"
            @click=${this.openAddActivityModal}
            title="Add activity to wheel"
          >
            +
          </button>
        </div>
        ${wheelItems.map(item => this.renderActivityItem(item))}
      </div>
    `;
  }

  private renderActivityItem(item: any) {
    // Use listColor for border and dot, but domain color for badge
    const listColor = item.listColor || item.extremityColor || item.color || getDomainColor(item.domain);
    const domainColor = getDomainColor(item.domain);
    const backgroundColorWithOpacity = getDomainColorWithOpacity(item.domain, 0.1);
    const domainTextColor = getContrastingTextColor(item.domain);

    // Create a brief preview of the description for compact view
    const descriptionPreview = item.description && item.description.length > 60
      ? item.description.substring(0, 60) + '...'
      : item.description || '';

    return html`
      <div class="activity-item"
           style="border-left: 4px solid ${listColor}; background: ${backgroundColorWithOpacity};">
        <div class="activity-item-header" @click=${() => this.openActivityDetailsModal(item)}>
          <div class="activity-item-title">
            <div class="activity-color-dot" style="background-color: ${listColor}; box-shadow: 0 0 8px ${getDomainColorWithOpacity(item.domain, 0.4)};"></div>
            <div style="flex: 1; min-width: 0;">
              <div class="activity-name">${item.name}</div>
              <div style="display: flex; align-items: center; gap: var(--spacing-2); margin-top: 2px;">
                <span class="domain-badge-small" style="background-color: ${domainColor}; color: ${domainTextColor};">${item.domain}</span>
                ${descriptionPreview ? html`
                  <div style="font-size: var(--font-size-xs); color: var(--color-text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1;">
                    ${descriptionPreview}
                  </div>
                ` : ''}
              </div>
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: var(--spacing-2);">
            <button
              class="remove-activity-btn"
              @click=${(e: Event) => this.handleRemoveActivity(e, item)}
              title="Remove from wheel"
            >
              ❌
            </button>
            <div class="activity-expand-icon">👁️</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderActivityModal() {
    if (!this.showActivityModal) return '';

    return html`
      <div class="modal-overlay" @click=${this.closeActivityModal}>
        <div class="modal activity-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">Change Activity</h3>
            <button class="modal-close" data-testid="change-activity-modal-close-btn" @click=${this.closeActivityModal}>×</button>
          </div>
          <div class="modal-body">
            <button class="create-activity-btn" @click=${this.openCreateActivityModal}>
              <span>➕</span>
              <span>Create New Activity</span>
            </button>
            <input
              type="text"
              class="search-input"
              placeholder="Search activities..."
              .value=${this.searchQuery}
              @input=${this.handleSearchChange}
            />
            <div class="activity-catalog">
              ${this.isLoading ? html`
                <div class="loading-state">
                  <div class="spinner"></div>
                  <p>Creating tailored activity...</p>
                </div>
              ` : this.filteredCatalog.length > 0 ?
                this.filteredCatalog.map(activity => html`
                  <div class="catalog-item ${activity.type}" @click=${() => this.selectCatalogActivity(activity)}>
                    <div class="catalog-item-info">
                      <div class="catalog-item-header">
                        <span class="activity-type-icon ${activity.type}">${activity.type === 'tailored' ? '⭐' : '🎯'}</span>
                        <div class="catalog-item-name">${activity.name}</div>
                        <span class="activity-type-badge ${activity.type}">${activity.type === 'tailored' ? 'Tailored' : 'Generic'}</span>
                      </div>
                      <div class="catalog-item-description">${activity.description}</div>
                      <div class="catalog-item-meta">
                        <span>🎯 ${activity.domain}</span>
                        <span>⚡ ${activity.base_challenge_rating}/100</span>
                      </div>
                    </div>
                  </div>
                `) :
                html`<div class="no-results">No activities found matching "${this.searchQuery}"</div>`
              }
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderCreateActivityModal() {
    if (!this.showCreateActivityModal) return '';

    return html`
      <div class="modal-overlay" @click=${this.closeCreateActivityModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">Create New Activity</h3>
            <button class="modal-close" data-testid="create-activity-modal-close-btn" @click=${this.closeCreateActivityModal}>×</button>
          </div>
          <div class="modal-body">
            <form @submit=${(e: Event) => { e.preventDefault(); this.createNewActivity(); }}>
              <div class="form-group">
                <label class="form-label" for="activity-name">Activity Name *</label>
                <input
                  type="text"
                  id="activity-name"
                  class="form-input"
                  placeholder="e.g., Morning Yoga, Creative Writing..."
                  .value=${this.newActivityForm.name}
                  @input=${(e: Event) => this.handleNewActivityFormChange('name', (e.target as HTMLInputElement).value)}
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label" for="activity-description">Description *</label>
                <textarea
                  id="activity-description"
                  class="form-textarea"
                  placeholder="Describe what this activity involves and why it's beneficial..."
                  .value=${this.newActivityForm.description}
                  @input=${(e: Event) => this.handleNewActivityFormChange('description', (e.target as HTMLTextAreaElement).value)}
                  required
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label" for="activity-domain">Domain</label>
                <select
                  id="activity-domain"
                  class="form-select"
                  .value=${this.newActivityForm.domain}
                  @change=${(e: Event) => this.handleNewActivityFormChange('domain', (e.target as HTMLSelectElement).value)}
                >
                  <option value="general">General</option>
                  <option value="physical">Physical</option>
                  <option value="mental">Mental</option>
                  <option value="creative">Creative</option>
                  <option value="social">Social</option>
                  <option value="practical">Practical</option>
                  <option value="learning">Learning</option>
                  <option value="relaxation">Relaxation</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label" for="activity-challenge">Challenge Level</label>
                <input
                  type="range"
                  id="activity-challenge"
                  class="form-range"
                  min="1"
                  max="100"
                  .value=${this.newActivityForm.base_challenge_rating.toString()}
                  @input=${(e: Event) => this.handleNewActivityFormChange('base_challenge_rating', parseInt((e.target as HTMLInputElement).value))}
                />
                <div class="range-value">${this.newActivityForm.base_challenge_rating}/100</div>
              </div>

              <div class="modal-actions">
                <button type="button" class="btn-secondary" @click=${this.closeCreateActivityModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn-primary"
                  ?disabled=${!this.newActivityForm.name.trim() || !this.newActivityForm.description.trim()}
                >
                  Create Activity
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    // Check if authentication is required
    const config = this.configService.getConfig();
    const requireAuth = config.security.requireAuthentication;

    // Show login form if authentication is required and user is not authenticated
    if (requireAuth && !this.isAuthenticated) {
      return html`
        <login-form
          @login-success=${this.handleLoginSuccess}
          .hideDemoMode=${true}
        ></login-form>
      `;
    }

    return html`
      <div class="app-container">
        <!-- Header -->
        <header class="header">
          <div class="header-content">
            <div class="logo">
              <div class="logo-icon">🎯</div>
              <span>Goali</span>
              ${this.configService.isDebugMode() ? html`
                <span style="font-size: 0.7em; color: #ffaa00; margin-left: 8px;">DEBUG</span>
              ` : ''}
            </div>
            <div class="status-bar">
              <!-- Connection Status -->
              <div class="connection-status">
                <div class="status-indicator ${this.connectionState === 'connected' ? 'connected' : this.connectionState === 'connecting' ? 'connecting' : 'disconnected'}"></div>
                <span>${this.getConnectionStatusText()}</span>
              </div>

              <!-- Hamburger Menu -->
              ${this.currentUser ? html`
                <div class="hamburger-menu-container">
                  <button
                    class="hamburger-button ${this.isHamburgerMenuOpen ? 'active' : ''}"
                    data-testid="hamburger-menu-btn"
                    @click=${this.toggleHamburgerMenu}
                    title="Menu"
                  >
                    ☰
                  </button>

                  <div class="hamburger-menu ${this.isHamburgerMenuOpen ? 'open' : ''}" @click=${(e: Event) => e.stopPropagation()}>
                    ${this.isHamburgerMenuOpen ? html`
                      <div class="hamburger-menu-backdrop" @click=${this.closeHamburgerMenu} style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: -1;"></div>
                    ` : ''}
                    <div class="hamburger-menu-header">
                      <div class="hamburger-menu-user">
                        <div class="hamburger-menu-user-avatar">
                          ${this.currentUser.name.charAt(0).toUpperCase()}
                        </div>
                        <div class="hamburger-menu-user-info">
                          <div class="hamburger-menu-user-name">${this.currentUser.name}</div>
                          <div class="hamburger-menu-user-status">
                            ${this.currentUser.isStaff ? html`<span class="staff-badge">Staff</span>` : ''}
                            <span>Online</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="hamburger-menu-body">
                      <div class="hamburger-menu-item" data-testid="menu-profile" @click=${this.handleProfileFromMenu}>
                        <div class="hamburger-menu-item-icon">👤</div>
                        <div class="hamburger-menu-item-content">
                          <div class="hamburger-menu-item-title" data-testid="menu-profile-title">Profile</div>
                          <div class="hamburger-menu-item-subtitle" data-testid="menu-profile-subtitle">Manage your profile settings</div>
                        </div>
                      </div>

                      <div class="hamburger-menu-item" data-testid="menu-environment" @click=${this.openEnvironmentSwitcherModal}>
                        <div class="hamburger-menu-item-icon">🌍</div>
                        <div class="hamburger-menu-item-content">
                          <div class="hamburger-menu-item-title" data-testid="menu-environment-title">Switch Inventory</div>
                          <div class="hamburger-menu-item-subtitle" data-testid="menu-environment-subtitle">Currently: ${this.getCurrentEnvironmentName()}</div>
                        </div>
                      </div>

                      <div class="hamburger-menu-item" data-testid="menu-inventory" @click=${this.openInventoryModal}>
                        <div class="hamburger-menu-item-icon">🎒</div>
                        <div class="hamburger-menu-item-content">
                          <div class="hamburger-menu-item-title" data-testid="menu-inventory-title">Inventory</div>
                          <div class="hamburger-menu-item-subtitle" data-testid="menu-inventory-subtitle">Manage your resources</div>
                        </div>
                      </div>

                      <div class="hamburger-menu-item" data-testid="menu-history" @click=${this.openActivityHistoryModal}>
                        <div class="hamburger-menu-item-icon">📜</div>
                        <div class="hamburger-menu-item-content">
                          <div class="hamburger-menu-item-title" data-testid="menu-history-title">Activity History</div>
                          <div class="hamburger-menu-item-subtitle" data-testid="menu-history-subtitle">View your completed activities</div>
                        </div>
                      </div>

                      ${this.configService.isDebugMode() ? html`
                        <div class="hamburger-menu-divider"></div>
                        <div class="hamburger-menu-item" data-testid="menu-debug" @click=${() => { this.showDebugPanel = !this.showDebugPanel; this.closeHamburgerMenu(); }}>
                          <div class="hamburger-menu-item-icon">🐛</div>
                          <div class="hamburger-menu-item-content">
                            <div class="hamburger-menu-item-title">Debug Panel</div>
                            <div class="hamburger-menu-item-subtitle">Developer tools</div>
                          </div>
                        </div>
                      ` : ''}

                      <div class="hamburger-menu-divider"></div>
                      <div class="hamburger-menu-item danger" data-testid="menu-logout" @click=${this.handleLogoutFromMenu}>
                        <div class="hamburger-menu-item-icon">🚪</div>
                        <div class="hamburger-menu-item-content">
                          <div class="hamburger-menu-item-title" data-testid="menu-logout-title">Logout</div>
                          <div class="hamburger-menu-item-subtitle" data-testid="menu-logout-subtitle">Sign out of your account</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ` : ''}
            </div>
          </div>
        </header>

        <!-- Main Content -->
        <main class="main-content ${this.isHamburgerMenuOpen ? 'menu-open' : ''}" data-testid="main-content">
          <!-- Error Banner -->
          ${this.error ? html`
            <div class="error-banner" data-testid="app-error-banner">
              <span>⚠️ ${this.error}</span>
              <button data-testid="app-error-dismiss-btn" @click=${this.handleClearError}>Dismiss</button>
            </div>
          ` : ''}

          <!-- Progress Modal Overlay -->
          ${this.showProgressBar ? html`
            <div class="progress-modal-overlay" @click=${(e: Event) => {
              // Close modal if clicking on overlay
              if (e.target === e.currentTarget) {
                this.handleCancelGeneration();
              }
            }}>
              <div class="progress-modal ${this.progressModalMode === 'user-friendly' ? 'user-friendly' : ''}" @click=${(e: Event) => e.stopPropagation()}>
                <div class="progress-modal-header">
                  <h3 class="progress-modal-title">${this.progressTitle}</h3>
                  <button class="progress-modal-close" data-testid="progress-modal-close-btn" @click=${this.handleCancelGeneration}>×</button>
                </div>
                <real-time-progress-bar
                  .trackerId=${this.currentProgressTrackerId}
                  .workflowType=${'wheel_generation'}
                  .title=${this.progressTitle}
                  .showMetrics=${this.progressModalMode === 'debug'}
                  .showTimeline=${this.progressModalMode === 'debug'}
                  .compact=${this.progressModalMode === 'user-friendly'}
                ></real-time-progress-bar>
                ${this.generationStalled ? html`
                  <div data-testid="generation-stall-warning" style="text-align: center; margin-top: 12px; color: var(--color-warning, #f59e0b); font-size: 0.85rem;">
                    Almost there — finalizing your personalized wheel. This can take a moment.
                  </div>
                ` : ''}
                <div style="text-align: center; margin-top: 16px;">
                  <button
                    data-testid="progress-modal-cancel-btn"
                    class="progress-modal-cancel"
                    @click=${this.handleCancelGeneration}
                  >Cancel</button>
                </div>
              </div>
            </div>
          ` : ''}

          <!-- Wheel Section -->
          <section class="wheel-section" data-testid="wheel-section">
            <div class="wheel-container" data-testid="wheel-container">
              <!-- Single wheel with two states: unpopulated (grey) or populated (colorful) -->
              ${this.wheelStateMachine.shouldShowPopulatedWheel ? html`
                <!-- Populated state: colorful interactive wheel -->
                <game-wheel
                  .wheelData=${this.wheelData}
                  .config=${this.configService.getConfig().wheel}
                  @wheel-spin-complete=${this.handleWheelSpinComplete}
                ></game-wheel>

                <!-- Centered SPIN button on top of wheel -->
                ${this.wheelStateMachine.shouldShowSpinButton ? html`
                  <button
                    data-testid="wheel-spin-btn"
                    class="wheel-center-button spin"
                    ?disabled=${this.isLoading || this.connectionState !== 'connected'}
                    @click=${this.handleSpinWheel}
                  >
                    ${this.isLoading ? '⏳' : 'SPIN'}
                  </button>
                ` : ''}
              ` : html`
                <!-- Unpopulated state: grey demo wheel -->
                <game-wheel
                  .wheelData=${this.getBackgroundWheelData()}
                  .config=${this.configService.getConfig().wheel}
                  hide-ui
                  disable-interaction
                  class="wheel-unpopulated"
                ></game-wheel>

                ${this.wheelStateMachine.currentState === WheelState.ERROR ? html`
                  <!-- Error state: prominent message + retry button overlaid on wheel -->
                  <div class="wheel-error-overlay" data-testid="wheel-error-state">
                    <div class="wheel-error-icon">⚠️</div>
                    <div class="wheel-error-message" data-testid="wheel-error-message">
                      ${this.wheelStateMachine.error || 'Generation failed. Please try again.'}
                    </div>
                    <button
                      data-testid="wheel-retry-btn"
                      class="wheel-center-button generate"
                      ?disabled=${this.connectionState !== 'connected' || this.isLoading}
                      @click=${this.handleGenerateWheel}
                    >
                      Retry
                    </button>
                  </div>
                ` : html`
                  <!-- Centered GENERATE button on top of unpopulated wheel -->
                  <button
                    data-testid="wheel-generate-btn"
                    class="wheel-center-button generate"
                    ?disabled=${this.connectionState !== 'connected' || this.isLoading}
                    @click=${this.handleGenerateWheel}
                  >
                    ${this.getButtonText()}
                  </button>
                `}
              `}
            </div>

            <!-- Wheel Controls -->
            <div class="wheel-controls">
              <!-- Button Bar - Hidden when wheel is populated to save space -->
              ${!this.wheelStateMachine.shouldShowPopulatedWheel ? html`
              <div class="button-bar">


                <!-- 🚀 Enhanced 5-Slider System (Updated 2025-07-24) -->
                <div class="energy-grid" data-testid="energy-grid">
                  <!-- Row 1: Time Available, Physical, Mental -->
                  <div class="energy-row">
                    <!-- Time Available -->
                    <div class="energy-slider-card">
                      <div class="energy-icon">⏰</div>
                      <label class="energy-label">Time Available</label>
                      <div class="slider-container">
                        <input
                          data-testid="energy-time-slider"
                          type="range"
                          min="0"
                          max="5"
                          step="1"
                          .value=${this.getTimeStepIndex().toString()}
                          @input=${this.handleTimeAvailableChange}
                          class="energy-slider"
                          aria-label="Time Available slider"
                          aria-describedby="energy-time-value"
                        />
                        <div class="energy-value">${this.formatTimeAvailable()}</div>
                      </div>
                    </div>

                    <!-- Physical Energy -->
                    <div class="energy-slider-card">
                      <div class="energy-icon">💪</div>
                      <label class="energy-label">Physical Energy</label>
                      <div class="slider-container">
                        <input
                          data-testid="energy-physical-slider"
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          .value=${this.physicalEnergy.toString()}
                          @input=${this.handlePhysicalEnergyChange}
                          class="energy-slider"
                          aria-label="Physical Energy slider"
                          aria-describedby="energy-physical-value"
                        />
                        <div class="energy-value">${this.formatPhysicalEnergy()}</div>
                      </div>
                    </div>

                    <!-- Mental Energy -->
                    <div class="energy-slider-card">
                      <div class="energy-icon">🧠</div>
                      <label class="energy-label">Mental Energy</label>
                      <div class="slider-container">
                        <input
                          data-testid="energy-mental-slider"
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          .value=${this.mentalEnergy.toString()}
                          @input=${this.handleMentalEnergyChange}
                          class="energy-slider"
                          aria-label="Mental Energy slider"
                          aria-describedby="energy-mental-value"
                        />
                        <div class="energy-value">${this.formatMentalEnergy()}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Row 2: Social, Emotional Energy, Energy Direction -->
                  <div class="energy-row">
                    <!-- Social Energy -->
                    <div class="energy-slider-card">
                      <div class="energy-icon">👥</div>
                      <label class="energy-label">Social Energy</label>
                      <div class="slider-container">
                        <input
                          data-testid="energy-social-slider"
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          .value=${this.socialEnergy.toString()}
                          @input=${this.handleSocialEnergyChange}
                          class="energy-slider"
                          aria-label="Social Energy slider"
                          aria-describedby="energy-social-value"
                        />
                        <div class="energy-value">${this.formatSocialEnergy()}</div>
                      </div>
                    </div>

                    <!-- Emotional Energy -->
                    <div class="energy-slider-card">
                      <div class="energy-icon">💫</div>
                      <label class="energy-label">Emotional State</label>
                      <div class="slider-container">
                        <input
                          data-testid="energy-emotional-slider"
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          .value=${this.emotionalEnergy.toString()}
                          @input=${this.handleEmotionalEnergyChange}
                          class="energy-slider"
                          aria-label="Emotional State slider"
                          aria-describedby="energy-emotional-value"
                        />
                        <div class="energy-value">${this.formatEmotionalEnergy()}</div>
                      </div>
                    </div>

                    <!-- Energy Direction -->
                    <div class="energy-slider-card">
                      <div class="energy-icon">🧭</div>
                      <label class="energy-label">Focus Direction</label>
                      <div class="slider-container">
                        <input
                          data-testid="energy-direction-slider"
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          .value=${this.energyDirection.toString()}
                          @input=${this.handleEnergyDirectionChange}
                          class="energy-slider"
                          aria-label="Focus Direction slider"
                          aria-describedby="energy-direction-value"
                        />
                        <div class="energy-value">${this.formatEnergyDirection()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ` : ''}

              <!-- Activity List -->
              ${this.wheelStateMachine.shouldShowPopulatedWheel ? this.renderActivityList() : ''}
            </div>
          </section>

          <!-- Chat Section (hidden for wheel focus) -->
          <!--
          <section class="chat-section">
            <chat-interface
              .messages=${this.messages}
              .connectionStatus=${this.wsConnected ? 'connected' : 'disconnected'}
              .isProcessing=${this.isLoading}
              @message-send=${this.handleMessageSend}
            ></chat-interface>
          </section>
          -->
        </main>

        <!-- Connection Error Banner -->
        ${!this.wsConnected && this.connectionState === 'disconnected' ? html`
          <div class="error-banner connection-error-banner" data-testid="connection-error-banner">
            <span>⚠️ Impossible to connect to backend</span>
          </div>
        ` : ''}

        <!-- Debug Panel -->
        ${this.configService.isDebugMode() ? html`
          <debug-panel
            .visible=${this.showDebugPanel}
            @user-changed=${this.handleDebugUserChange}
            @llm-config-changed=${this.handleDebugLLMConfigChange}
            @backend-url-changed=${this.handleDebugBackendUrlChange}
            @wheel-loaded=${this.handleDebugWheelLoaded}
          ></debug-panel>
        ` : ''}

        <!-- Contract Disclaimer Modal -->
        <contract-disclaimer-modal
          ?visible=${this.showContractModal}
          @contract-accepted=${this.handleContractAccepted}
          @contract-cancelled=${this.handleContractCancelled}
        ></contract-disclaimer-modal>

        <!-- Activity Change Modal -->
        ${this.renderActivityModal()}

        <!-- Create Activity Modal -->
        ${this.renderCreateActivityModal()}

        <!-- Profile Modal -->
        <user-profile-modal
          data-testid="profile-modal"
          ?open=${this.showProfileModal}
          .profileData=${this.currentUserProfile}
          .loading=${!this.currentUserProfile}
          @close-modal=${this.closeProfileModal}
          @open-full-editor=${this.openFullProfileEditor}
        ></user-profile-modal>

        <!-- Winning Modal -->
        ${this.renderWinningModal()}

        <!-- Feedback Modal -->
        ${this.renderFeedbackModal()}

        <!-- Add Activity Modal -->
        ${this.renderAddActivityModal()}

        <!-- Inventory Modal -->
        <inventory-modal
          data-testid="inventory-modal"
          .open=${this.showInventoryModal}
          .inventoryData=${this.inventoryData}
          @close-modal=${this.closeInventoryModal}
          @move-resource=${this.handleMoveResource}
          @remove-resource=${this.handleRemoveResource}
          @create-inventory=${this.handleCreateInventory}
        ></inventory-modal>

        <!-- Activity Details Modal -->
        <tailored-activity-details-modal
          .open=${this.showActivityDetailsModal}
          .activity=${this.selectedActivity}
          @close=${this.closeActivityDetailsModal}
        ></tailored-activity-details-modal>

        <!-- Environment Switcher Modal -->
        <environment-switcher-modal
          data-testid="environment-switcher-modal"
          .open=${this.showEnvironmentSwitcherModal}
          .environmentData=${this.environmentData}
          @close-modal=${this.closeEnvironmentSwitcherModal}
          @switch-environment=${this.handleEnvironmentSwitch}
        ></environment-switcher-modal>

        <!-- Activity History Modal -->
        <activity-history-modal
          data-testid="activity-history-modal"
          ?open=${this.showActivityHistoryModal}
          .historyData=${this.activityHistoryData}
          .loading=${this.isActivityHistoryLoading}
          @close-modal=${this.closeActivityHistoryModal}
        ></activity-history-modal>

        <!-- Post-Spin View Manager -->
        ${this.postSpinView && this.postSpinActivity ? html`
          <post-spin-view-manager
            .activity=${this.postSpinActivity}
            .view=${this.postSpinView}
            .startTime=${this.postSpinStartTime}
            @skip-timer=${this.handlePostSpinSkipTimer}
            @reset-app=${this.handlePostSpinResetApp}
            @close-feedback=${this.handlePostSpinCloseFeedback}
            @submit-feedback=${this.handlePostSpinSubmitFeedback}
            @close-cancellation=${this.handlePostSpinCloseCancellation}
            @cancel-cancellation=${this.handlePostSpinCancelCancellation}
            @confirm-cancellation=${this.handlePostSpinConfirmCancellation}
          ></post-spin-view-manager>
        ` : ''}

        <!-- Error Notification Components -->
        <error-banner
          .error=${this.currentError?.level === 'temporary' ? this.currentError : null}
          .isDebugMode=${this.currentMode === 'debug'}
          .dismissible=${true}
          .showRetry=${true}
        ></error-banner>

        <error-popup
          .error=${this.currentError?.level === 'non_critical' ? this.currentError : null}
          .visible=${this.showErrorPopup}
          .isDebugMode=${this.currentMode === 'debug'}
          .displayDuration=${8000}
        ></error-popup>

        <error-persistent
          .error=${this.currentError?.level === 'critical' ? this.currentError : null}
          .visible=${this.showErrorPersistent}
          .isDebugMode=${this.currentMode === 'debug'}
          .allowDismiss=${false}
        ></error-persistent>
      </div>
    `;
  }

  /**
   * Handle debug user change
   */
  private handleDebugUserChange(event: CustomEvent) {
    console.log('Debug: User changed to', event.detail.userId);
    this.debugUserId = event.detail.userId;
    this.loadCurrentUser(); // Reload user info when debug user changes
  }

  /**
   * Handle debug LLM config change
   */
  private handleDebugLLMConfigChange(event: CustomEvent) {
    console.log('Debug: LLM config changed to', event.detail.configId);
    this.debugLLMConfigId = event.detail.configId;
  }

  /**
   * Handle debug backend URL change
   */
  private handleDebugBackendUrlChange(event: CustomEvent) {
    console.log('Debug: Backend URL changed to', event.detail.url);
    // Reconnect with new URL
    this.websocketManager.disconnect();
    setTimeout(() => {
      this.initializeApp();
    }, 1000);
  }

  /**
   * Handle debug wheel loaded event
   */
  private async handleDebugWheelLoaded(event: CustomEvent) {
    console.log('🎡 DEBUG: Wheel loaded from debug panel:', event.detail.wheelData);

    try {
      const wheelData = event.detail.wheelData;

      // Process the wheel data the same way as WebSocket wheel_data messages
      await this.handleWheelGenerated({ wheel: wheelData });

      console.log('✅ DEBUG: Previous wheel loaded successfully');
    } catch (error) {
      console.error('❌ DEBUG: Failed to load previous wheel:', error);
      this.handleError({
        error: 'Failed to load previous wheel',
        details: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  /**
   * Handle opening full profile editor
   */
  private openFullProfileEditor = () => {
    // Open Django admin user profile management page for full editing
    const adminUrl = `${window.location.origin}/admin/user-profiles/`;
    window.open(adminUrl, '_blank');
    this.closeProfileModal();
  };

  /**
   * Open activity history modal
   */
  private openActivityHistoryModal = async () => {
    this.showActivityHistoryModal = true;
    this.isActivityHistoryLoading = true;
    this.closeHamburgerMenu();
    await this.loadActivityHistoryData();
  };

  /**
   * Close activity history modal
   */
  private closeActivityHistoryModal = () => {
    this.showActivityHistoryModal = false;
    this.activityHistoryData = [];
    this.isActivityHistoryLoading = false;
  };

  /**
   * Load activity history data from API
   */
  private async loadActivityHistoryData(): Promise<void> {
    try {
      if (!this.currentUser?.id) {
        console.error('❌ No current user for activity history loading');
        this.isActivityHistoryLoading = false;
        return;
      }

      const baseUrl = this.getBackendBaseUrl();
      const response = await fetch(`${baseUrl}/api/user/activity-history/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.history) {
          this.activityHistoryData = data.history;
          console.log('✅ Activity history data loaded:', this.activityHistoryData);
        } else {
          console.error('❌ Invalid activity history data structure:', data);
          this.activityHistoryData = [];
        }
      } else {
        console.error('❌ Failed to load activity history data:', response.status, await response.text());
        this.activityHistoryData = [];
      }
    } catch (error) {
      console.error('❌ Error loading activity history data:', error);
      this.activityHistoryData = [];
    } finally {
      // Reason: Always clear loading state regardless of success or failure to
      // prevent the modal from being stuck in a perpetual loading spinner.
      this.isActivityHistoryLoading = false;
    }
  }







  /**
   * Render winning activity modal
   */
  private renderWinningModal() {
    if (!this.showWinningModal || !this.winningActivity) return '';

    // Get stored signature from contract modal
    const storedSignature = ContractDisclaimerModal.getStoredSignature();

    // Get domain colors for the winning activity
    const domainColor = this.winningActivity.color || getDomainColor(this.winningActivity.domain);
    const backgroundColorWithOpacity = getDomainColorWithOpacity(this.winningActivity.domain, 0.15);
    const textColor = getContrastingTextColor(this.winningActivity.domain);

    return html`
      <div class="modal-overlay winning-modal-overlay" @click=${this.closeWinningModal}>
        <div class="modal winning-modal-content"
             style="background: linear-gradient(135deg, ${domainColor} 0%, ${getDomainColorWithOpacity(this.winningActivity.domain, 0.8)} 100%); border: 3px solid ${domainColor};"
             @click=${(e: Event) => e.stopPropagation()}>
          <div class="winning-header">
            <div class="winning-icon" style="color: ${textColor};">🎉</div>
            <h2 style="color: ${textColor};">Congratulations!</h2>
            <button data-testid="winning-close-btn" class="modal-close" style="color: ${textColor};" @click=${this.closeWinningModal}>×</button>
          </div>

          ${storedSignature ? html`
            <div class="contract-confirmation">
              <div class="contract-confirmation-header">
                <span class="contract-icon">⚖️</span>
                <span class="contract-message">You have approved this choice</span>
              </div>
              <div class="signature-display">
                <img
                  src="${storedSignature.imageData}"
                  alt="Your signature"
                  class="signature-image"
                />
                <div class="signature-timestamp">
                  Signed: ${new Date(storedSignature.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          ` : ''}

          <div class="modal-body">
            <div class="winning-activity">
              <!-- Activity Header -->
              <div class="activity-header" style="border-left: 6px solid ${domainColor}; background: ${backgroundColorWithOpacity}; padding: 16px; border-radius: 12px; margin-bottom: 16px;">
                <div class="activity-icon" style="font-size: 2em; color: ${domainColor};">${this.winningActivity.icon || '🎯'}</div>
                <div class="activity-title">
                  <div class="activity-name" style="color: ${textColor}; font-size: 1.5em; font-weight: bold; margin-bottom: 8px;">${this.winningActivity.name || this.winningActivity.text}</div>
                  <div class="activity-type-badge ${this.winningActivity.type || 'generic'}" style="background-color: ${domainColor}; color: ${textColor}; padding: 4px 12px; border-radius: 20px; font-size: 0.9em; font-weight: 600;">
                    ${this.winningActivity.type === 'tailored' ? '✨ Tailored' : '📋 Generic'}
                  </div>
                </div>
              </div>

              <!-- Activity Description -->
              <div class="activity-description">
                ${this.winningActivity.description || 'No description available'}
              </div>

              <!-- Activity Instructions (if available) -->
              ${this.winningActivity.instructions ? html`
                <div class="activity-instructions">
                  <h4>📝 Instructions:</h4>
                  <p>${this.winningActivity.instructions}</p>
                </div>
              ` : ''}

              <!-- Tailored Information (if available) -->
              ${this.winningActivity.tailored_description && this.winningActivity.tailored_description !== this.winningActivity.description ? html`
                <div class="tailored-info">
                  <h4>🎯 Personalized for You:</h4>
                  <p>${this.winningActivity.tailored_description}</p>
                </div>
              ` : ''}

              <!-- Activity Details Grid -->
              <div class="activity-details">
                <div class="detail-item">
                  <span class="detail-label">🏷️ Domain:</span>
                  <span class="detail-value">
                    <span class="domain-badge" style="background-color: ${domainColor}; color: ${textColor}; padding: 4px 12px; border-radius: 16px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
                      ${this.winningActivity.domain || 'General'}
                    </span>
                  </span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">⚡ Challenge:</span>
                  <span class="detail-value">${this.winningActivity.base_challenge_rating || 'N/A'}/100</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">⏱️ Duration:</span>
                  <span class="detail-value">${this.winningActivity.duration_range || 'Flexible'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">🎯 Type:</span>
                  <span class="detail-value">${this.winningActivity.type === 'tailored' ? '✨ Tailored' : '📋 Generic'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">📊 Weight:</span>
                  <span class="detail-value">${this.winningActivity.percentage ? this.winningActivity.percentage.toFixed(1) + '%' : 'N/A'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">🎲 Spin Time:</span>
                  <span class="detail-value">${this.winningActivity.spinDuration ? (this.winningActivity.spinDuration / 1000).toFixed(1) + 's' : 'N/A'}</span>
                </div>
              </div>

              <!-- Additional Information -->
              ${this.winningActivity.requirements || this.winningActivity.materials ? html`
                <div class="activity-requirements">
                  ${this.winningActivity.requirements ? html`
                    <div class="requirement-section">
                      <h4>📋 Requirements:</h4>
                      <p>${this.winningActivity.requirements}</p>
                    </div>
                  ` : ''}
                  ${this.winningActivity.materials ? html`
                    <div class="requirement-section">
                      <h4>🛠️ Materials:</h4>
                      <p>${this.winningActivity.materials}</p>
                    </div>
                  ` : ''}
                </div>
              ` : ''}

              <!-- Action Buttons -->
              <div class="winning-actions">
                <button data-testid="winning-accept-btn" class="primary-button" @click=${this.closeWinningModal}>
                  Let's Do This! 🚀
                </button>
                <button data-testid="winning-decline-btn" class="secondary-button decline-button" @click=${this.handleDeclineActivity}>
                  Don't want to do this
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }



  private renderFeedbackModal() {
    if (!this.showFeedbackModal) return '';

    return html`
      <div class="modal-overlay" @click=${this.closeFeedbackModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">${this.feedbackModalConfig.title}</h3>
            <button data-testid="feedback-modal-close-btn" class="modal-close" @click=${this.closeFeedbackModal}>×</button>
          </div>
          <div class="modal-body">
            <p>${this.feedbackModalConfig.message}</p>
            <div class="form-group">
              <label class="form-label" for="feedback-comment">Your feedback:</label>
              <textarea
                data-testid="feedback-comment-input"
                id="feedback-comment"
                class="form-textarea"
                placeholder="Tell us why you don't want this activity..."
                rows="4"
              ></textarea>
            </div>
            <div class="modal-actions">
              <button data-testid="feedback-modal-skip-btn" type="button" class="btn-secondary" @click=${this.closeFeedbackModal}>
                ${this.feedbackModalConfig.secondaryButtonLabel}
              </button>
              <button data-testid="feedback-modal-submit-btn" type="button" class="btn-primary" @click=${this.submitFeedback}>
                ${this.feedbackModalConfig.primaryButtonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderAddActivityModal() {
    if (!this.showAddActivityModal) return '';

    return html`
      <div class="modal-overlay" @click=${this.closeAddActivityModal}>
        <div class="modal activity-modal enhanced-search-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">Add Activity to Wheel</h3>
            <button class="modal-close" data-testid="add-activity-modal-close-btn" @click=${this.closeAddActivityModal}>×</button>
          </div>

          <!-- Activity Tailoring Spinner -->
          ${this.isTailoringActivity ? html`
            <div class="tailoring-spinner-overlay">
              <div class="tailoring-spinner-content">
                <div class="spinner-animation">
                  <div class="spinner-circle"></div>
                  <div class="spinner-circle"></div>
                  <div class="spinner-circle"></div>
                </div>
                <div class="tailoring-message">
                  <h4>🎨 Tailoring Activity</h4>
                  <p>Personalizing "${this.tailoringActivityName}" for your current context...</p>
                  <div class="tailoring-progress">
                    <div class="progress-bar">
                      <div class="progress-fill"></div>
                    </div>
                    <small>This may take up to 10 seconds</small>
                  </div>
                </div>
              </div>
            </div>
          ` : ''}

          <div class="modal-body">
            <!-- Enhanced Search Bar -->
            <div class="search-container">
              <div class="search-input-wrapper">
                <span class="search-icon">🔍</span>
                <input
                  type="text"
                  class="enhanced-search-input"
                  placeholder="Search activities by name, description, or domain..."
                  .value=${this.searchQuery}
                  @input=${this.handleSearchChange}
                  autofocus
                />
                ${this.searchQuery ? html`
                  <button class="clear-search" @click=${() => { this.searchQuery = ''; this.performSearch(); }}>×</button>
                ` : ''}
              </div>

              <!-- Search Context Info -->
              ${this.searchQuery ? html`
                <div class="search-context">
                  <span class="context-item">⚡ Energy: ${this.energyLevel}%</span>
                  <span class="context-item">⏱️ Time: ${this.timeAvailable}min</span>
                  <span class="context-item">🎯 Smart filtering enabled</span>
                </div>
              ` : ''}
            </div>

            <!-- Activity Catalog Results -->
            <div class="activity-catalog enhanced-catalog">
              ${this.isLoading ? html`
                <div class="loading-state">
                  <div class="spinner"></div>
                  <p>Searching activities...</p>
                </div>
              ` : this.searchQuery && this.filteredCatalog.length === 0 ? html`
                <div class="empty-search-state">
                  <div class="empty-icon">🔍</div>
                  <h4>No activities found</h4>
                  <p>Try adjusting your search terms or browse by typing something general like "exercise" or "creative"</p>
                </div>

                <!-- Custom Activity Creation Section - Show when no results found -->
                <div class="custom-activity-section">
                  <div class="custom-activity-prompt">
                    <h4>Not in our catalog? Create yours!</h4>
                    <p>Design a custom activity that fits your needs perfectly.</p>
                    <button
                      class="btn-primary create-custom-btn"
                      @click=${this.toggleCustomActivityForm}
                    >
                      ${this.showCustomActivityForm ? 'Cancel' : '✨ Create Custom Activity'}
                    </button>
                  </div>

                  ${this.showCustomActivityForm ? html`
                    <div class="custom-activity-form">
                      <div class="form-group">
                        <label class="form-label">Activity Title *</label>
                        <input
                          type="text"
                          class="form-input"
                          placeholder="e.g., Morning meditation in the garden"
                          .value=${this.customActivityForm.title}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('title', (e.target as HTMLInputElement).value)}
                        />
                      </div>

                      <div class="form-group">
                        <label class="form-label">Description *</label>
                        <textarea
                          class="form-textarea"
                          placeholder="Describe what this activity involves and why it's meaningful to you..."
                          .value=${this.customActivityForm.description}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('description', (e.target as HTMLTextAreaElement).value)}
                        ></textarea>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Duration Range</label>
                        <div class="duration-range">
                          <div class="range-input-group">
                            <label>From:</label>
                            <input
                              type="range"
                              min="5"
                              max="120"
                              .value=${this.customActivityForm.duration_from}
                              @input=${(e: Event) => this.handleCustomActivityFormChange('duration_from', parseInt((e.target as HTMLInputElement).value))}
                            />
                            <span>${this.customActivityForm.duration_from} min</span>
                          </div>
                          <div class="range-input-group">
                            <label>To:</label>
                            <input
                              type="range"
                              min="5"
                              max="120"
                              .value=${this.customActivityForm.duration_to}
                              @input=${(e: Event) => this.handleCustomActivityFormChange('duration_to', parseInt((e.target as HTMLInputElement).value))}
                            />
                            <span>${this.customActivityForm.duration_to} min</span>
                          </div>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Challengingness Level</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          class="form-range"
                          .value=${this.customActivityForm.challengingness}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('challengingness', parseInt((e.target as HTMLInputElement).value))}
                        />
                        <div class="range-value">${this.customActivityForm.challengingness}/100</div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Comfort Level</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          class="form-range"
                          .value=${this.customActivityForm.comfort_level}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('comfort_level', parseInt((e.target as HTMLInputElement).value))}
                        />
                        <div class="range-value">${this.customActivityForm.comfort_level}/100</div>
                      </div>

                      <div class="modal-actions">
                        <button
                          type="button"
                          class="btn-secondary"
                          @click=${this.toggleCustomActivityForm}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="btn-primary"
                          ?disabled=${!this.customActivityForm.title || !this.customActivityForm.description}
                          @click=${this.createCustomActivity}
                        >
                          Create & Add to Wheel
                        </button>
                      </div>
                    </div>
                  ` : ''}
                </div>
              ` : !this.searchQuery ? html`
                <div class="empty-initial-state">
                  <div class="empty-icon">✨</div>
                  <h4>Discover Activities</h4>
                  <p>Start typing to search through our activity catalog. We'll show you the most relevant activities based on your current energy level (${this.energyLevel}%) and available time (${this.timeAvailable} minutes).</p>
                  <div class="search-suggestions">
                    <p><strong>Try searching for:</strong></p>
                    <div class="suggestion-tags">
                      <span class="suggestion-tag" @click=${() => this.setSearchQuery('exercise')}>🏃‍♂️ Exercise</span>
                      <span class="suggestion-tag" @click=${() => this.setSearchQuery('creative')}>🎨 Creative</span>
                      <span class="suggestion-tag" @click=${() => this.setSearchQuery('learning')}>📚 Learning</span>
                      <span class="suggestion-tag" @click=${() => this.setSearchQuery('mindfulness')}>🧘‍♀️ Mindfulness</span>
                      <span class="suggestion-tag" @click=${() => this.setSearchQuery('social')}>👥 Social</span>
                    </div>
                  </div>
                </div>
              ` : html`
                <!-- Results Header -->
                <div class="results-header">
                  <span class="results-count">${this.filteredCatalog.length} activities found</span>
                  ${this.filteredCatalog.some(a => a.type === 'tailored') ? html`
                    <span class="tailored-indicator">⭐ Personalized results first</span>
                  ` : ''}
                </div>

                <!-- Activity Results -->
                ${this.filteredCatalog.map(activity => {
                  const activityColor = (activity as any).color || '#95A5A6';
                  const relevanceScore = (activity as any).relevance_score;

                  return html`
                    <div
                      class="catalog-item enhanced-item ${activity.type}"
                      style="border-left: 4px solid ${activityColor}"
                      @click=${() => this.addActivityToWheel(activity)}
                    >
                      <div class="catalog-item-info">
                        <div class="catalog-item-header">
                          <div class="activity-color-indicator" style="background-color: ${activityColor}"></div>
                          <span class="activity-type-icon ${activity.type}">${activity.type === 'tailored' ? '⭐' : '🎯'}</span>
                          <div class="catalog-item-name">${activity.name}</div>
                          <div class="activity-badges">
                            <span class="activity-type-badge ${activity.type}">${activity.type === 'tailored' ? 'Tailored' : 'Generic'}</span>
                            ${relevanceScore ? html`
                              <span class="relevance-badge" title="Relevance score based on your context">
                                ${Math.round(relevanceScore)}% match
                              </span>
                            ` : ''}
                          </div>
                        </div>
                        <div class="catalog-item-description">${activity.description}</div>
                        <div class="catalog-item-meta">
                          <span class="meta-item domain-meta" style="color: ${activityColor}">
                            🎯 ${activity.domain}
                          </span>
                          <span class="meta-item challenge-meta">⚡ ${activity.base_challenge_rating}/100</span>
                          ${activity.type === 'tailored' ? html`
                            <span class="meta-item tailored-meta">✨ Personalized for you</span>
                          ` : ''}
                        </div>
                      </div>
                    </div>
                  `;
                })}

                <!-- Custom Activity Creation Section - Show when there are search results or empty results -->
                <div class="custom-activity-section">
                  <div class="custom-activity-prompt">
                    <h4>Not in our catalog? Create yours!</h4>
                    <p>Design a custom activity that fits your needs perfectly.</p>
                    <button
                      class="btn-primary create-custom-btn"
                      @click=${this.toggleCustomActivityForm}
                    >
                      ${this.showCustomActivityForm ? 'Cancel' : '✨ Create Custom Activity'}
                    </button>
                  </div>

                  ${this.showCustomActivityForm ? html`
                    <div class="custom-activity-form">
                      <div class="form-group">
                        <label class="form-label">Activity Title *</label>
                        <input
                          type="text"
                          class="form-input"
                          placeholder="e.g., Morning meditation in the garden"
                          .value=${this.customActivityForm.title}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('title', (e.target as HTMLInputElement).value)}
                        />
                      </div>

                      <div class="form-group">
                        <label class="form-label">Description *</label>
                        <textarea
                          class="form-textarea"
                          placeholder="Describe what this activity involves and why it's meaningful to you..."
                          .value=${this.customActivityForm.description}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('description', (e.target as HTMLTextAreaElement).value)}
                        ></textarea>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Duration Range</label>
                        <div class="duration-range">
                          <div class="range-input-group">
                            <label>From:</label>
                            <input
                              type="range"
                              min="5"
                              max="120"
                              .value=${this.customActivityForm.duration_from}
                              @input=${(e: Event) => this.handleCustomActivityFormChange('duration_from', parseInt((e.target as HTMLInputElement).value))}
                            />
                            <span>${this.customActivityForm.duration_from} min</span>
                          </div>
                          <div class="range-input-group">
                            <label>To:</label>
                            <input
                              type="range"
                              min="5"
                              max="120"
                              .value=${this.customActivityForm.duration_to}
                              @input=${(e: Event) => this.handleCustomActivityFormChange('duration_to', parseInt((e.target as HTMLInputElement).value))}
                            />
                            <span>${this.customActivityForm.duration_to} min</span>
                          </div>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Challengingness Level</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          class="form-range"
                          .value=${this.customActivityForm.challengingness}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('challengingness', parseInt((e.target as HTMLInputElement).value))}
                        />
                        <div class="range-value">${this.customActivityForm.challengingness}/100</div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Comfort Level</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          class="form-range"
                          .value=${this.customActivityForm.comfort_level}
                          @input=${(e: Event) => this.handleCustomActivityFormChange('comfort_level', parseInt((e.target as HTMLInputElement).value))}
                        />
                        <div class="range-value">${this.customActivityForm.comfort_level}/100</div>
                      </div>

                      <div class="modal-actions">
                        <button
                          type="button"
                          class="btn-secondary"
                          @click=${this.toggleCustomActivityForm}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="btn-primary"
                          ?disabled=${!this.customActivityForm.title || !this.customActivityForm.description}
                          @click=${this.createCustomActivity}
                        >
                          Create & Add to Wheel
                        </button>
                      </div>
                    </div>
                  ` : ''}
                </div>
              `}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Handle move resource event from inventory modal
   */
  private handleMoveResource = (event: CustomEvent) => {
    const { item, inventoryId } = event.detail;
    this.moveResourceToInventory(item, inventoryId);
  };

  /**
   * Handle remove resource event from inventory modal
   */
  private handleRemoveResource = (event: CustomEvent) => {
    const { item } = event.detail;
    this.removeResource(item);
  };

  /**
   * Get background wheel data for the greyed-out wheel
   */
  private getBackgroundWheelData(): any {
    return {
      segments: [
        {
          id: 'bg-1',
          name: '🏃‍♂️ Exercise',
          text: '🏃‍♂️ Exercise',
          percentage: 20,
          color: '#FF6B6B',
          activity_tailored_id: 'bg-activity-1',
          description: 'Physical activity and movement'
        },
        {
          id: 'bg-2',
          name: '📚 Learning',
          text: '📚 Learning',
          percentage: 20,
          color: '#4ECDC4',
          activity_tailored_id: 'bg-activity-2',
          description: 'Study and skill development'
        },
        {
          id: 'bg-3',
          name: '🎨 Creative',
          text: '🎨 Creative',
          percentage: 20,
          color: '#45B7D1',
          activity_tailored_id: 'bg-activity-3',
          description: 'Artistic and creative pursuits'
        },
        {
          id: 'bg-4',
          name: '🧘‍♀️ Mindfulness',
          text: '🧘‍♀️ Mindfulness',
          percentage: 20,
          color: '#96CEB4',
          activity_tailored_id: 'bg-activity-4',
          description: 'Meditation and relaxation'
        },
        {
          id: 'bg-5',
          name: '👥 Social',
          text: '👥 Social',
          percentage: 20,
          color: '#FFEAA7',
          activity_tailored_id: 'bg-activity-5',
          description: 'Connect with friends and family'
        }
      ],
      wheelId: 'background-wheel',
      createdAt: new Date().toISOString()
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-shell': AppShell;
  }
}
